import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chessboard } from 'react-chessboard';
import { fetchBeginnerModules, fetchOpeningsModules, isSupabaseConfigured } from '../../lib/supabaseContent';
import { fetchLessonStepCounts, fetchUserLessonProgress } from '../../lib/academyOverview';
import {
  PUZZLE_ACTIVE_THEME_IDS,
  PUZZLE_COMING_SOON_THEME_IDS,
  fetchPuzzlesByTheme,
  fetchPuzzlePanelStats,
  fetchPuzzleStartsByThemeGroup,
  fetchUserPuzzleSkill,
  getPuzzleTierFromRating,
  mapThemeRowsToUiCategory
} from '../../lib/supabasePuzzles';
import { fetchMyEffectivePlan } from '../../lib/supabaseSubscription';
import { buildQuizSession, fetchQuizHubBank, isQuizHubConfigured } from '../../lib/supabaseQuizHub';
import ChessyPlayground from './ChessyPlayground';
import ChessyBrand from '../ChessyBrand';

function AcademyHome({
  language,
  user,
  profile,
  initialArea = 'academy',
  initialMindhubArticleSlug = '',
  onAreaChange,
  onMindhubArticleChange,
  onRequestLogin,
  onOpenAccount,
  onStartLesson,
  focusLessonSlug,
  focusNonce
}) {
  const beginnerModules = useMemo(
    () => [
      {
        id: 'm1',
        sortOrder: 1,
        available: true,
        title: language === 'ro' ? 'Modul 1: Aventura pe Tabla de Șah' : 'Module 1: Chessboard Adventure',
        summary:
          language === 'ro'
            ? 'Fundamentul și universul șahului: istorie, etichetă, geometria tablei și armata pieselor.'
            : 'Foundations of chess: history, etiquette, board geometry, and piece hierarchy.',
        // Keep fallback progress at 0 so it doesn't "jump" when Supabase content loads.
        progress: 0,
        accent: '#1e40af',
        avatarTip:
          language === 'ro'
            ? 'Chessy, partenera ta de învățat: Astăzi vedem tabla ca niște mici strategi.'
            : 'Chessy, your learning buddy: Today we see the board like young strategists.',
        lessons: [
          {
            id: 'b-m1-l1',
            section: language === 'ro' ? 'Povestea Șahului (Istorie & Etichetă)' : 'Chess Story (History & Etiquette)',
            type: 'story',
            title:
              language === 'ro'
                ? 'Povestea Șahului: De la Chaturanga la șahul modern'
                : 'Chess Story: From Chaturanga to modern chess',
            duration: language === 'ro' ? '4 min video + subtitrare' : '4 min subtitled video',
            available: true
          },
          {
            id: 'b-m1-l2',
            section: language === 'ro' ? 'Povestea Șahului (Istorie & Etichetă)' : 'Chess Story (History & Etiquette)',
            type: 'etiquette',
            title:
              language === 'ro'
                ? 'Codul de onoare: salut, respect și touch-move'
                : 'Code of honor: greeting, respect, and touch-move',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m1-l3',
            section: language === 'ro' ? 'Geometria Tablei' : 'Board Geometry',
            type: 'board',
            title: language === 'ro' ? 'Coordonatele tablei (A-H, 1-8)' : 'Board coordinates (A-H, 1-8)',
            duration: '~5 min',
            available: true
          },
          {
            id: 'b-m1-l4',
            section: language === 'ro' ? 'Geometria Tablei' : 'Board Geometry',
            type: 'board',
            title: language === 'ro' ? 'Linii, coloane și diagonale' : 'Ranks, files, and diagonals',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m1-l5',
            section: language === 'ro' ? 'Geometria Tablei' : 'Board Geometry',
            type: 'board',
            title: language === 'ro' ? 'Centrul tablei: miza principală' : 'The center: main strategic target',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m1-l6',
            section: language === 'ro' ? 'Armata și Ierarhia (Piesele)' : 'Army and Hierarchy (Pieces)',
            type: 'piece',
            title: language === 'ro' ? 'Pionul: primul soldat' : 'Pawn: the first soldier',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m1-l7',
            section: language === 'ro' ? 'Armata și Ierarhia (Piesele)' : 'Army and Hierarchy (Pieces)',
            type: 'piece',
            title: language === 'ro' ? 'Turnul: gardianul liniilor' : 'Rook: guardian of files and ranks',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m1-l8',
            section: language === 'ro' ? 'Armata și Ierarhia (Piesele)' : 'Army and Hierarchy (Pieces)',
            type: 'piece',
            title: language === 'ro' ? 'Nebunul: lunetistul pe culori' : 'Bishop: color-bound sniper',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m1-l9',
            section: language === 'ro' ? 'Armata și Ierarhia (Piesele)' : 'Army and Hierarchy (Pieces)',
            type: 'piece',
            title: language === 'ro' ? 'Calul: singurul care sare' : 'Knight: the only jumping piece',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m1-l10',
            section: language === 'ro' ? 'Armata și Ierarhia (Piesele)' : 'Army and Hierarchy (Pieces)',
            type: 'piece',
            title: language === 'ro' ? 'Dama: regina atacului' : 'Queen: ruler of attack',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m1-l11',
            section: language === 'ro' ? 'Armata și Ierarhia (Piesele)' : 'Army and Hierarchy (Pieces)',
            type: 'piece',
            title: language === 'ro' ? 'Regele: piesa care trebuie protejată' : 'King: the piece to protect',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m1-l12',
            section: language === 'ro' ? 'Armata și Ierarhia (Piesele)' : 'Army and Hierarchy (Pieces)',
            type: 'value',
            title:
              language === 'ro'
                ? 'Valoarea relativă a pieselor (1-3-5-9)'
                : 'Relative piece values (1-3-5-9)',
            duration: '~5 min',
            available: true
          },
          {
            id: 'b-m1-recap',
            section: language === 'ro' ? 'Final de Modul' : 'End of Module',
            type: 'recap',
            title: language === 'ro' ? 'Recapitulare Modul 1' : 'Module 1 recap',
            duration: '~3 min',
            available: false
          },
          {
            id: 'b-m1-exam-standard',
            section: language === 'ro' ? 'Final de Modul' : 'End of Module',
            type: 'quiz',
            title:
              language === 'ro'
                ? 'Examenul Final - Partea 1: Standard'
                : 'Final Exam - Part 1: Standard',
            duration: language === 'ro' ? '10 întrebări' : '10 questions',
            available: false
          },
          {
            id: 'b-m1-exam-captain',
            section: language === 'ro' ? 'Final de Modul' : 'End of Module',
            type: 'mindset',
            title:
              language === 'ro'
                ? 'Examenul de Căpitan - Partea 2: Mindset strategic'
                : 'Captain Exam - Part 2: Strategic mindset',
            duration: language === 'ro' ? '8 scenarii' : '8 scenarios',
            focus:
              language === 'ro'
                ? 'Identifică piesa care nu face nimic în poziție.'
                : 'Identify the piece that does nothing in the position.',
            available: false
          }
        ]
      },
      {
        id: 'm2',
        sortOrder: 2,
        available: true,
        title: language === 'ro' ? 'Modul 2: Super-Reguli și Puteri Speciale' : 'Module 2: Super Rules and Special Powers',
        summary:
          language === 'ro'
            ? 'Rocadă, en passant, promovare și situații speciale explicate pe înțelesul copiilor.'
            : 'Castling, en passant, promotion, and special situations explained for kids.',
        progress: 0,
        accent: '#0ea5e9',
        avatarTip:
          language === 'ro'
            ? 'Chessy, partenera ta de învățat: Ne pregătim de puteri speciale. Urmează modulul următor.'
            : 'Chessy, your learning buddy: Special powers are coming next.'
        ,
        lessons: [
          {
            id: 'b-m2-l1',
            section: language === 'ro' ? 'Reguli Speciale' : 'Special Rules',
            type: 'story',
            title: language === 'ro' ? 'Rocada: scutul Regelui' : 'Castling: the King’s shield',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m2-l2',
            section: language === 'ro' ? 'Reguli Speciale' : 'Special Rules',
            type: 'story',
            title: language === 'ro' ? 'En passant: captura surpriză' : 'En passant: the surprise capture',
            duration: '~4 min',
            available: true
          },
          {
            id: 'b-m2-l3',
            section: language === 'ro' ? 'Reguli Speciale' : 'Special Rules',
            type: 'story',
            title: language === 'ro' ? 'Promovarea: pionul devine erou' : 'Promotion: the pawn becomes a hero',
            duration: '~4 min',
            available: false
          }
        ]
      },
      {
        id: 'm3',
        sortOrder: 3,
        available: false,
        title: language === 'ro' ? 'Modul 3: Gândește ca un Mic Strateg' : 'Module 3: Think Like a Young Strategist',
        summary:
          language === 'ro'
            ? 'Primele planuri de gândire: atac, apărare, piese neprotejate și alegerea mutării corecte.'
            : 'First strategic plans: attack, defense, loose pieces, and choosing the right move.',
        progress: 0,
        accent: '#22c55e',
        avatarTip:
          language === 'ro'
            ? 'Chessy, partenera ta de învățat: Aici învățăm să alegem mutarea bună, nu doar să mutăm.'
            : 'Chessy, your learning buddy: Here we learn to choose the right move, not just move.'
        ,
        lessons: [
          {
            id: 'b-m3-l1',
            section: language === 'ro' ? 'Strategie de Bază' : 'Basic Strategy',
            type: 'story',
            title: language === 'ro' ? 'Atac și apărare: cine amenință pe cine?' : 'Attack and defense: who threatens whom?',
            duration: '~5 min',
            available: false
          },
          {
            id: 'b-m3-l2',
            section: language === 'ro' ? 'Strategie de Bază' : 'Basic Strategy',
            type: 'story',
            title: language === 'ro' ? 'Piese neprotejate: găsește ținta' : 'Loose pieces: find the target',
            duration: '~5 min',
            available: false
          },
          {
            id: 'b-m3-l3',
            section: language === 'ro' ? 'Strategie de Bază' : 'Basic Strategy',
            type: 'story',
            title: language === 'ro' ? 'Planul simplu: îmbunătățește cea mai slabă piesă' : 'Simple plan: improve your worst piece',
            duration: '~5 min',
            available: false
          }
        ]
      },
      {
        id: 'm4',
        sortOrder: 4,
        available: false,
        title: language === 'ro' ? 'Modul 4: Clubul de Mate Rapide' : 'Module 4: Quick Mate Club',
        summary:
          language === 'ro'
            ? 'Arsenalul de mate predefinite: pattern-uri simple și recunoaștere rapidă.'
            : 'Predefined mating patterns and quick recognition drills.',
        progress: 0,
        accent: '#f59e0b',
        avatarTip:
          language === 'ro'
            ? 'Chessy, partenera ta de învățat: Finalul perfect - vezi pattern-ul și dai mat.'
            : 'Chessy, your learning buddy: Final step - spot the pattern and deliver mate.'
        ,
        lessons: [
          {
            id: 'b-m4-l1',
            section: language === 'ro' ? 'Mate Rapide' : 'Quick Mates',
            type: 'story',
            title: language === 'ro' ? 'Mat cu două turnuri: închide Regele' : 'Two rooks mate: trap the King',
            duration: '~5 min',
            available: false
          },
          {
            id: 'b-m4-l2',
            section: language === 'ro' ? 'Mate Rapide' : 'Quick Mates',
            type: 'story',
            title: language === 'ro' ? 'Mat pe ultima linie: ușa închisă' : 'Back-rank mate: the closed door',
            duration: '~5 min',
            available: false
          },
          {
            id: 'b-m4-l3',
            section: language === 'ro' ? 'Mate Rapide' : 'Quick Mates',
            type: 'story',
            title: language === 'ro' ? 'Mat cu dama și rege: echipa perfectă' : 'Queen + King mate: the perfect team',
            duration: '~5 min',
            available: false
          }
        ]
      }
    ],
    [language]
  );

  const openingsFallbackModules = useMemo(
    () => [
      {
        id: 'openings-white',
        sortOrder: 1,
        available: true,
        title: language === 'ro' ? 'White Opening' : 'White Opening',
        summary:
          language === 'ro'
            ? 'Repertoriu cu piesele albe: centru, dezvoltare activă și planuri clare din primele mutări.'
            : 'White repertoire: center control, active development, and clear plans from move one.',
        progress: 0,
        accent: '#1e40af',
        avatarTip:
          language === 'ro'
            ? 'Cu albul, inițiativa începe din deschidere.'
            : 'With White, initiative starts in the opening.',
        lessons: [
          {
            id: 'b-o1-l1',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'Atacul Italian' : 'Italian Game',
            duration: '~18 min',
            difficulty: 'easy',
            sortOrder: 1,
            available: true
          },
          {
            id: 'b-o1-l2',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'Sistemul Londra' : 'London System',
            duration: '~16 min',
            difficulty: 'easy',
            sortOrder: 2,
            available: true
          },
          {
            id: 'b-o1-l3-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'Ruy Lopez: presiune pe e5 (în curând)' : 'Ruy Lopez: pressure on e5 (coming soon)',
            duration: '~18 min',
            difficulty: 'easy',
            sortOrder: 3,
            available: false
          },
          {
            id: 'b-o1-l4-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'Gambitul Damei: centru strategic (în curând)' : 'Queen\'s Gambit: strategic center (coming soon)',
            duration: '~18 min',
            difficulty: 'easy',
            sortOrder: 4,
            available: false
          },
          {
            id: 'b-o1-l5-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'Vienna Game: inițiativă rapidă (în curând)' : 'Vienna Game: quick initiative (coming soon)',
            duration: '~15 min',
            difficulty: 'easy',
            sortOrder: 5,
            available: false
          },
          {
            id: 'b-o1-l6-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'Scotch Game: deschidere directă (în curând)' : 'Scotch Game: direct opening (coming soon)',
            duration: '~15 min',
            difficulty: 'easy',
            sortOrder: 6,
            available: false
          },
          {
            id: 'b-o1-l7-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'English Opening: control elastic (în curând)' : 'English Opening: elastic control (coming soon)',
            duration: '~17 min',
            difficulty: 'easy',
            sortOrder: 7,
            available: false
          },
          {
            id: 'b-o1-l8-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'King\'s Gambit: atac romantic (în curând)' : 'King\'s Gambit: romantic attack (coming soon)',
            duration: '~16 min',
            difficulty: 'easy',
            sortOrder: 8,
            available: false
          },
          {
            id: 'b-o1-l9-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'Catalan: presiune pe diagonală (în curând)' : 'Catalan: diagonal pressure (coming soon)',
            duration: '~18 min',
            difficulty: 'easy',
            sortOrder: 9,
            available: false
          },
          {
            id: 'b-o1-l10-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Alb' : 'White openings',
            type: 'story',
            title: language === 'ro' ? 'Jobava London: atac practic (în curând)' : 'Jobava London: practical attack (coming soon)',
            duration: '~16 min',
            difficulty: 'easy',
            sortOrder: 10,
            available: false
          }
        ]
      },
      {
        id: 'openings-black',
        sortOrder: 2,
        available: true,
        title: language === 'ro' ? 'Black Opening' : 'Black Opening',
        summary:
          language === 'ro'
            ? 'Repertoriu cu piesele negre: răspunsuri solide, contra-joc și tranziții sănătoase.'
            : 'Black repertoire: solid replies, counterplay, and healthy transitions.',
        progress: 0,
        accent: '#1e40af',
        avatarTip:
          language === 'ro'
            ? 'Cu negrul, jocul bun începe cu un răspuns clar.'
            : 'With Black, strong chess starts with a clear response.',
        lessons: [
          {
            id: 'b-o2-l1',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'Caro-Kann Exchange' : 'Caro-Kann Exchange',
            duration: '~22 min',
            difficulty: 'easy',
            sortOrder: 1,
            available: true
          },
          {
            id: 'b-o2-l2-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'Siciliană: răspuns activ (în curând)' : 'Sicilian Defense: active reply (coming soon)',
            duration: '~18 min',
            difficulty: 'easy',
            sortOrder: 2,
            available: false
          },
          {
            id: 'b-o2-l3-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'Apărarea Franceză: lanțuri de pioni (în curând)' : 'French Defense: pawn chains (coming soon)',
            duration: '~17 min',
            difficulty: 'easy',
            sortOrder: 3,
            available: false
          },
          {
            id: 'b-o2-l4-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'Scandinavă: dezvoltare rapidă (în curând)' : 'Scandinavian: quick development (coming soon)',
            duration: '~15 min',
            difficulty: 'easy',
            sortOrder: 4,
            available: false
          },
          {
            id: 'b-o2-l5-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'Pirc Defense: flexibilitate totală (în curând)' : 'Pirc Defense: full flexibility (coming soon)',
            duration: '~16 min',
            difficulty: 'easy',
            sortOrder: 5,
            available: false
          },
          {
            id: 'b-o2-l6-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'Modern Defense: hipermodern practic (în curând)' : 'Modern Defense: practical hypermodern (coming soon)',
            duration: '~16 min',
            difficulty: 'easy',
            sortOrder: 6,
            available: false
          },
          {
            id: 'b-o2-l7-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'Slavă contra d4: structură sănătoasă (în curând)' : 'Slav vs d4: healthy structure (coming soon)',
            duration: '~17 min',
            difficulty: 'easy',
            sortOrder: 7,
            available: false
          },
          {
            id: 'b-o2-l8-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'Nimzo-Indian: control strategic (în curând)' : 'Nimzo-Indian: strategic control (coming soon)',
            duration: '~18 min',
            difficulty: 'easy',
            sortOrder: 8,
            available: false
          },
          {
            id: 'b-o2-l9-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'King\'s Indian: contraatac dinamic (în curând)' : 'King\'s Indian: dynamic counterattack (coming soon)',
            duration: '~18 min',
            difficulty: 'easy',
            sortOrder: 9,
            available: false
          },
          {
            id: 'b-o2-l10-coming-soon',
            section: language === 'ro' ? 'Deschideri cu Negru' : 'Black openings',
            type: 'story',
            title: language === 'ro' ? 'Alekhine: provocare creativă (în curând)' : 'Alekhine Defense: creative challenge (coming soon)',
            duration: '~16 min',
            difficulty: 'easy',
            sortOrder: 10,
            available: false
          }
        ]
      }
    ],
    [language]
  );

  const [supabaseModules, setSupabaseModules] = useState(null);
  const [supabaseOpeningsModules, setSupabaseOpeningsModules] = useState(null);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [supabaseError, setSupabaseError] = useState('');
  const [activeArea, setActiveArea] = useState(initialArea || 'academy');
  const [openModuleIds, setOpenModuleIds] = useState(['m1']);
  const [selectedModuleId, setSelectedModuleId] = useState('m1');
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [scrollToLessonActions, setScrollToLessonActions] = useState(false);
  const [flashLessonSlug, setFlashLessonSlug] = useState('');
  const [treeSearch, setTreeSearch] = useState('');
  const [academyModuleDetailOpen, setAcademyModuleDetailOpen] = useState(false);
  const [academyLessonFilter, setAcademyLessonFilter] = useState('all');
  const [academySectionPage, setAcademySectionPage] = useState({});
  const [academyOpenSections, setAcademyOpenSections] = useState({});
  const [puzzleSectionPage, setPuzzleSectionPage] = useState({});
  const [puzzleOpenSections, setPuzzleOpenSections] = useState({});
  const [selectedPuzzleCategoryId, setSelectedPuzzleCategoryId] = useState('');
  const [selectedPuzzleDifficultyId, setSelectedPuzzleDifficultyId] = useState('');
  const [puzzleSessionDiagramNo, setPuzzleSessionDiagramNo] = useState(1);
  const [puzzleModuleDetailOpen, setPuzzleModuleDetailOpen] = useState(false);
  const [puzzleGroupFilter, setPuzzleGroupFilter] = useState('all');
  const [puzzleStartsByGroup, setPuzzleStartsByGroup] = useState({});
  const [puzzleUserStats, setPuzzleUserStats] = useState(null);
  const [isLoadingPuzzleUserStats, setIsLoadingPuzzleUserStats] = useState(false);
  const [puzzleSkillProfile, setPuzzleSkillProfile] = useState({ puzzlePoints: 0, puzzleRating: 500 });
  const [dbPuzzleCategories, setDbPuzzleCategories] = useState([]);
  const [isLoadingPuzzles, setIsLoadingPuzzles] = useState(false);
  const [loadingPuzzleCategoryId, setLoadingPuzzleCategoryId] = useState('');
  const [loadedPuzzleCategoryMap, setLoadedPuzzleCategoryMap] = useState({});
  const [puzzleError, setPuzzleError] = useState('');
  const [guestPuzzleStartsByCategory, setGuestPuzzleStartsByCategory] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('cm_guest_puzzle_starts_by_category');
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });
  const [showGuestPuzzleLockModal, setShowGuestPuzzleLockModal] = useState(false);
  const [userPlan, setUserPlan] = useState('explorer');
  const [quizBankQuestions, setQuizBankQuestions] = useState([]);
  const [isLoadingQuizHub, setIsLoadingQuizHub] = useState(false);
  const [quizHubError, setQuizHubError] = useState('');
  const [selectedQuizTopic, setSelectedQuizTopic] = useState('all');
  const [selectedQuizDifficulty, setSelectedQuizDifficulty] = useState('easy');
  const [quizSessionMode, setQuizSessionMode] = useState('practice');
  const [quizSessionSize, setQuizSessionSize] = useState(10);
  const [quizSessionTimeLeftSec, setQuizSessionTimeLeftSec] = useState(0);
  const [quizSessionQuestions, setQuizSessionQuestions] = useState([]);
  const [quizSessionStarted, setQuizSessionStarted] = useState(false);
  const [quizSessionCompleted, setQuizSessionCompleted] = useState(false);
  const [quizSessionIndex, setQuizSessionIndex] = useState(0);
  const [quizSessionScore, setQuizSessionScore] = useState(0);
  const [quizSessionLives, setQuizSessionLives] = useState(3);
  const [quizSessionAnsweredCount, setQuizSessionAnsweredCount] = useState(0);
  const [quizSessionCorrectCount, setQuizSessionCorrectCount] = useState(0);
  const [quizSelectedOptionId, setQuizSelectedOptionId] = useState('');
  const [quizChecked, setQuizChecked] = useState(false);
  const [mindhubSelectedArticleId, setMindhubSelectedArticleId] = useState('');
  const [mindhubIsReading, setMindhubIsReading] = useState(false);
  const [mindhubReadingProgress, setMindhubReadingProgress] = useState(0);
  const [mindhubQuizAnswers, setMindhubQuizAnswers] = useState({});
  const [mindhubMomentsOpen, setMindhubMomentsOpen] = useState(false);
  const [gamelabSelectedArticleId, setGamelabSelectedArticleId] = useState('');
  const [gamelabPositionIndex, setGamelabPositionIndex] = useState(0);
  const [gamelabVisitedPositions, setGamelabVisitedPositions] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('cm_gamelab_progress_v1');
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed?.visitedPositions && typeof parsed.visitedPositions === 'object'
        ? parsed.visitedPositions
        : {};
    } catch {
      return {};
    }
  });
  const [gamelabQuizAnswers, setGamelabQuizAnswers] = useState(() => {
    if (typeof window === 'undefined') return {};
    try {
      const raw = window.localStorage.getItem('cm_gamelab_progress_v1');
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed?.quizAnswers && typeof parsed.quizAnswers === 'object'
        ? parsed.quizAnswers
        : {};
    } catch {
      return {};
    }
  });
  const [dailyQuestStarted, setDailyQuestStarted] = useState(false);
  const [dailyQuestCompleted, setDailyQuestCompleted] = useState(false);
  const [dailyQuestStepIndex, setDailyQuestStepIndex] = useState(-1);
  const [dailyQuestElapsedSec, setDailyQuestElapsedSec] = useState(0);
  const mindhubArticleViewRef = useRef(null);
  const mindhubProgressRef = useRef(0);

  const mapUiErrorMessage = useCallback((err, area = 'general') => {
    const raw = String(err?.message || err || '').toLowerCase();
    if (!raw) {
      if (area === 'puzzles') return language === 'ro' ? 'Puzzle-urile nu sunt disponibile momentan.' : 'Puzzles are temporarily unavailable.';
      if (area === 'quizzes') return language === 'ro' ? 'Quiz-urile nu sunt disponibile momentan.' : 'Quizzes are temporarily unavailable.';
      return language === 'ro' ? 'Conținutul nu este disponibil momentan.' : 'Content is temporarily unavailable.';
    }
    if (raw.includes('network') || raw.includes('failed to fetch') || raw.includes('timeout')) {
      return language === 'ro'
        ? 'Conexiunea este instabilă. Te rugăm să încerci din nou.'
        : 'Connection is unstable. Please try again.';
    }
    if (raw.includes('unauthorized') || raw.includes('invalid jwt') || raw.includes('forbidden')) {
      return language === 'ro'
        ? 'Sesiunea a expirat. Reautentifică-te pentru a continua.'
        : 'Your session has expired. Please sign in again to continue.';
    }
    if (area === 'puzzles') return language === 'ro' ? 'Nu am putut încărca puzzle-urile acum.' : 'We could not load puzzles right now.';
    if (area === 'quizzes') return language === 'ro' ? 'Nu am putut încărca quiz-urile acum.' : 'We could not load quizzes right now.';
    return language === 'ro' ? 'Nu am putut încărca conținutul acum.' : 'We could not load content right now.';
  }, [language]);

  const puzzleCategories = dbPuzzleCategories;
  const guestPuzzleFreeLimitPerCategory = 5;

  const mindhubArticles = useMemo(() => {
    const articleId = 'mindhub-article-001';
    return [
      {
        id: articleId,
        slug: 'psihologia-victoriei-in-sah',
        readMinutes: 8,
        title:
          language === 'ro'
            ? 'Psihologia Victoriei în Șah – Cum să Dezvolți un Mindset de Campion'
            : 'Psychology of Winning in Chess – How to Build a Champion Mindset',
        subtitle:
          language === 'ro'
            ? 'Cum transformi presiunea, emoțiile și greșelile în avantaj practic la tablă.'
            : 'How to turn pressure, emotions, and mistakes into practical advantage over the board.',
        heroImage: '/assets/mindhub/HeaderArticle.png',
        heroImageAlt:
          language === 'ro'
            ? 'Rege de șah stilizat, cu atmosferă de focus mental'
            : 'Stylized chess king in a focused mental atmosphere',
        spotifyEpisodeUrl: 'https://open.spotify.com/episode/6yHATA6htdI47ZyavZcglq?si=8JWL3kuwSB-YYAXTPAFMfQ',
        spotifyEmbedUrl: 'https://open.spotify.com/embed/episode/6yHATA6htdI47ZyavZcglq?utm_source=generator&theme=0',
        spotifyDurationLabel: language === 'ro' ? '≈38 min audio' : '≈38 min audio',
        podcastMoments:
          language === 'ro'
            ? [
                { time: '00:00', label: 'Introducere: de ce psihologia decide rezultatul' },
                { time: '08:20', label: 'Mindset de creștere: cum transformi greșelile' },
                { time: '17:45', label: 'Lupta cu tilt-ul în timpul partidei' },
                { time: '27:10', label: 'Rutină practică de reset înainte de mutare' }
              ]
            : [
                { time: '00:00', label: 'Intro: why psychology decides the result' },
                { time: '08:20', label: 'Growth mindset: turning mistakes into progress' },
                { time: '17:45', label: 'Fighting tilt during the game' },
                { time: '27:10', label: 'Practical reset routine before each move' }
              ],
        leadParagraphs:
          language === 'ro'
            ? [
                'Îți amintești acel moment? Stai în fața tablei, ceasul ticăie, ai făcut o mutare bună, dar brusc o greșeală mică îți schimbă totul. Inima îți bate mai tare, mâinile transpiră, și simți cum frustrarea urcă. Nu ești singur.',
                'Am vorbit cu sute de jucători, de la copii de 12 ani care tremură înainte de primul turneu, la adolescenți care se compară cu prietenii pe platforme online, și adulți care vin la șah după o zi grea la birou.',
                'Această bătălie interioară decide mai mult decât orice deschidere perfectă. Hai să explorăm cum transformi presiunea, emoțiile și greșelile în forță practică la tablă.'
              ]
            : [
                'Do you remember that moment? You are at the board, the clock is ticking, you made a good move, then one small mistake changes everything. Heart rate rises, tension appears, and frustration builds up.',
                'You are not alone. This inner battle decides more than any perfect opening. In this article, we turn pressure into clarity and emotion into better decisions.'
              ],
        soraPrompt:
          language === 'ro'
            ? 'Glass chess king with a calm ocean inside, cinematic light, mental resilience mood, ultra detailed.'
            : 'Glass chess king with a calm ocean inside, cinematic light, mental resilience mood, ultra detailed.',
        sections:
          language === 'ro'
            ? [
                {
                  title: 'De la „Nu sunt făcut pentru asta” la „Ce învăț din asta?”',
                  visual: '/assets/mindhub/ceinvatdinasta.png',
                  visualAlt: 'Transformare de la blocaj mental la creștere',
                  paragraphs: [
                    'Carol Dweck, în cercetările ei despre mindset, arată clar diferența: un mindset fix te blochează să crezi că abilitățile sunt gravate în piatră, iar o înfrângere înseamnă eșec total. Un mindset de creștere, dimpotrivă, vede fiecare partidă ca pe un profesor aspru, dar onest.',
                    'Nu promite minuni, dar studiile arată că jucătorii care adoptă această abordare își îmbunătățesc performanța cognitivă pe termen lung.',
                    'Gândește-te la Magnus Carlsen. După meciuri tensionate, el petrece ore analizând greșelile nu ca să se pedepsească, ci ca să le transforme în arme pentru următoarele partide.',
                    'Ce faci azi: ia un jurnal simplu. După fiecare joc, notează două lucruri: o mutare greșită și emoția resimțită atunci (furie, rușine, grabă). Revino a doua zi cu mintea calmă și extrage o regulă practică.'
                  ]
                },
                {
                  title: 'Când Emoțiile Vor să Preia Controlul – Lupta cu „Tilt-ul”',
                  visual: '/assets/mindhub/luptacutilt.png',
                  visualAlt: 'Control emoțional la tabla de șah',
                  paragraphs: [
                    '„Tilt” este acel vârtej de frustrare după o mutare proastă care duce la erori în lanț. Pe platformele de șah și în turnee, poveștile de la jucători care pierd mult rating într-o săptămână sunt mai comune decât par.',
                    'Psihologia sportului explică simplu: stresul activează sistemul de urgență și reduce claritatea deciziei. Semnalul apare clar: grabă, tensiune și nevoia de a repara imediat poziția.',
                    'Tehnică imediată: oprește-te 60 de secunde. Inspiră 4 secunde, ține 4, expiră 4. Respirația diafragmatică te readuce în prezent și scade impulsul de a muta „din nervi”.',
                    'Apoi întreabă-te: „Care este mutarea obiectivă acum?” și rulează SCA înainte de mutare.'
                  ]
                },
                {
                  title: 'Construiește Reziliența – Revenirea După Cădere',
                  visual: '/assets/mindhub/last.png',
                  visualAlt: 'Revenire și reziliență mentală în șah',
                  paragraphs: [
                    'Victoriile durabile nu vin din evitarea eșecurilor, ci din capacitatea de a te ridica. Reziliența nu înseamnă perfecțiune, ci revenire rapidă la un proces bun.',
                    'Jucătorii care revin după perioade grele au același tipar: expunere conștientă la adversari mai puternici și analiză fără judecată dură.',
                    'Pas concret: setează săptămânal un challenge de reziliență, trei jocuri contra rating mai mare. Sărbătorește efortul corect, nu doar scorul final.',
                    'Mindset-ul de campion nu se naște peste noapte, se cultivă în momentele grele, cu răbdare și onestitate. Mintea calmă face mutarea decisivă.'
                  ]
                }
              ]
            : [
                {
                  title: 'From “I am not made for this” to “What can I learn from this?”',
                  visual: '/assets/mindhub/ceinvatdinasta.png',
                  visualAlt: 'Mindset shift from blockage to growth',
                  paragraphs: [
                    'In chess, the difference between stagnation and growth is not only talent, but how you interpret failure.',
                    'When you say “I am not made for this,” progress stops. When you say “What can I learn from this?”, your mind starts searching for solutions.',
                    'A champion mindset means treating each game as feedback: what worked, what failed, and what to adjust next time.'
                  ]
                },
                {
                  title: 'When Emotions Try to Take Over – The Fight Against Tilt',
                  visual: '/assets/mindhub/luptacutilt.png',
                  visualAlt: 'Emotional control at the chessboard',
                  paragraphs: [
                    'Tilt appears when one mistake changes your emotional state and you begin to play impulsively.',
                    'First step: recognize the signal — rush, frustration, and the urge to force a quick fix.',
                    'Use a short routine: controlled breathing, 10-second reset, then run CCT (Checks, Captures, Threats) before your next move.'
                  ]
                },
                {
                  title: 'Build Resilience – Recovering After a Fall',
                  visual: '/assets/mindhub/last.png',
                  visualAlt: 'Mental resilience and comeback in chess',
                  paragraphs: [
                    'Strong players are not those who never make mistakes, but those who recover quickly to a strong process.',
                    'After each game, write 1 good decision and 1 poor decision. This simple habit builds long-term clarity and confidence.',
                    'Real resilience means consistency: you keep building even when short-term results are not ideal.'
                  ]
                }
              ],
        quizTitle: language === 'ro' ? 'Quiz de reflecție' : 'Reflection quiz',
        quizQuestions:
          language === 'ro'
            ? [
                {
                  id: 'q1',
                  question: 'Ce formulare îți activează mindset-ul de creștere în timpul antrenamentului?',
                  options: ['Ce pot învăța din această partidă?', 'Nu sunt făcut pentru asta', 'Mai bine evit partidele grele', 'Greșeala mă definește'],
                  correctIndex: 0,
                  explanation: 'Exact. Întrebarea corectă deschide analiza și progresul.'
                },
                {
                  id: 'q2',
                  question: 'Care este semnalul clasic că intri în tilt?',
                  options: ['Îți crește răbdarea', 'Joci mai clar decât de obicei', 'Apare grabă și vrei să repari imediat', 'Ai o rutină calmă de respirație'],
                  correctIndex: 2,
                  explanation: 'Corect. Graba emoțională este un semnal clasic de tilt.'
                },
                {
                  id: 'q3',
                  question: 'Ce rutină te stabilizează rapid după o greșeală?',
                  options: ['Muti imediat fără verificare', 'Închizi partida direct', 'Ignori poziția', 'Respirație + reset + verificare SCA'],
                  correctIndex: 3,
                  explanation: 'Foarte bine. Rutină scurtă, clară și repetabilă.'
                },
                {
                  id: 'q4',
                  question: 'Ce înseamnă reziliență în practică?',
                  options: ['Să nu mai pierzi niciodată', 'Să eviți partidele grele', 'Să revii la un proces bun după o greșeală', 'Să schimbi des stilul de joc'],
                  correctIndex: 2,
                  explanation: 'Corect. Reziliența este revenire disciplinată, nu perfecțiune.'
                }
              ]
            : [
                {
                  id: 'q1',
                  question: 'Which phrase supports real growth in chess?',
                  options: ['I am not made for this', 'What can I learn from this game?', 'I should avoid strong opponents', 'This mistake defines me'],
                  correctIndex: 1,
                  explanation: 'Exactly. The right question opens analysis and progress.'
                },
                {
                  id: 'q2',
                  question: 'What is a first sign of tilt?',
                  options: ['You calculate more clearly than usual', 'You rush and try to fix things immediately', 'You calmly write post-game notes', 'Your patience grows'],
                  correctIndex: 1,
                  explanation: 'Correct. Emotional urgency is a classic tilt signal.'
                },
                {
                  id: 'q3',
                  question: 'What routine helps you stabilize after a mistake?',
                  options: ['Play instantly without checking', 'Breath reset + CCT scan', 'Close the game immediately', 'Ignore the position'],
                  correctIndex: 1,
                  explanation: 'Great. Short, clear, repeatable routine.'
                },
                {
                  id: 'q4',
                  question: 'What does resilience mean in practice?',
                  options: ['Never lose again', 'Return to a good process after mistakes', 'Avoid difficult games', 'Constantly change style'],
                  correctIndex: 1,
                  explanation: 'Correct. Resilience is disciplined recovery, not perfection.'
                }
              ]
      }
    ];
  }, [language]);

  const gamelabArticles = useMemo(() => ([
    {
      id: 'gamelab-article-queens-gambit-001',
      slug: 'queens-gambit-every-position-chessmindset-cut',
      title: language === 'ro'
        ? "The Queen's Gambit · fiecare episod în poziții-cheie"
        : "The Queen's Gambit · every episode in key positions",
      subtitle: language === 'ro'
        ? 'Adaptare ChessMindset după articolul Chess.com: episod cu episod, planuri practice și decizii critice.'
        : 'ChessMindset adaptation of the Chess.com article: episode by episode, practical plans, and critical decisions.',
      readMinutes: 12,
      heroImage: 'https://images.chesscomfiles.com/uploads/v1/article/26444.f82e00e7.668x375o.41a933c53a0e@2x.png',
      sourceMeta: language === 'ro'
        ? 'Sursă editorială ChessMindset · Dosar Queen’s Gambit (actualizat 12 martie 2026)'
        : "ChessMindset editorial source · Queen's Gambit dossier (updated March 12, 2026)",
      positions: [
        {
          id: 'qg-pos-ep1',
          label: language === 'ro' ? 'Episodul 1 · Openings' : 'Episode 1 · Openings',
          fen: 'rnbqkbnr/ppp2ppp/4p3/3p4/2PP4/8/PP2PPPP/RNBQKBNR w KQkq - 0 3',
          insight: language === 'ro'
            ? 'În serial, primul cadru puternic este ideea de Queen’s Gambit Declined: centru solid și structură clară.'
            : "In the show, one of the first meaningful setups echoes Queen's Gambit Declined: solid center and clear structure."
        },
        {
          id: 'qg-pos-ep2',
          label: language === 'ro' ? 'Episodul 2 · Exchanges' : 'Episode 2 · Exchanges',
          fen: 'r1bqkb1r/pppp1Qpp/2n2n2/4p3/2B1P3/8/PPPP1PPP/RNB1K1NR b KQkq - 0 4',
          insight: language === 'ro'
            ? 'Momentul „șoc” pentru începători: Scholar’s Mate. Lecția practică este prevenția timpurie, nu „trucul”.'
            : "The beginner shock moment: Scholar's Mate. Practical lesson: early prevention, not cheap tricks."
        },
        {
          id: 'qg-pos-ep3',
          label: language === 'ro' ? 'Episodul 3 · Doubled Pawns' : 'Episode 3 · Doubled Pawns',
          fen: 'r2q1rk1/pp1nbppp/2p1pn2/3p4/3P4/2N1PN2/PPQ1BPPP/2KR3R w - - 0 11',
          insight: language === 'ro'
            ? 'Tema episodului: structură de pioni și compromisuri. Dublarea pionilor poate fi slăbiciune sau resursă dinamică.'
            : 'Episode theme: pawn structure trade-offs. Doubled pawns can be weakness or dynamic resource.'
        },
        {
          id: 'qg-pos-ep4',
          label: language === 'ro' ? 'Episodul 4 · Middle Game' : 'Episode 4 · Middle Game',
          fen: 'r4rk1/pp2bppp/2n1pn2/q7/3NP3/1BN1BP2/PPQ2P1P/2KR3R w - - 0 14',
          insight: language === 'ro'
            ? 'Aici contează timing-ul: dezvolți cu tempo sau simplifici prea devreme? Mijlocul de joc decide planul.'
            : 'Timing matters here: develop with tempo or simplify too early? The middlegame defines the plan.'
        },
        {
          id: 'qg-pos-ep5',
          label: language === 'ro' ? 'Episodul 5 · Fork' : 'Episode 5 · Fork',
          fen: 'r2q1rk1/pp2bppp/2n1pn2/2pp4/3P4/2P1PN2/PP1NBPPP/R2Q1RK1 w - - 0 10',
          insight: language === 'ro'
            ? 'Tema furcii: forțezi două ținte cu o piesă activă. În practică, „fork-ul” vine din poziție bună, nu din magie.'
            : 'Fork theme: attack two targets with one active piece. In practice, forks come from good positions, not magic.'
        },
        {
          id: 'qg-pos-ep6',
          label: language === 'ro' ? 'Episodul 6 · Adjournment' : 'Episode 6 · Adjournment',
          fen: 'r1b1kb1r/ppp2ppp/1qn1p3/3pN1B1/3P4/8/PPP2PPP/R2QKB1R w KQkq - 0 8',
          insight: language === 'ro'
            ? 'Adjournment-ul din epocă însemna analiză profundă între sesiuni. Azi traducem asta în „review calm” după partidă.'
            : "Classic adjournment meant deep between-session analysis. Today, translate it into a calm post-game review."
        },
        {
          id: 'qg-pos-ep7',
          label: language === 'ro' ? 'Episodul 7 · End Game' : 'Episode 7 · End Game',
          fen: '2r3k1/5ppp/p2p4/1p1P4/2p1P3/2P2Q2/PP3PPP/3R2K1 w - - 0 27',
          insight: language === 'ro'
            ? 'Finalul serialului pune accent pe convertirea avantajului: tehnică, calcul rece și control emoțional.'
            : 'The final episode highlights conversion technique: clean calculation and emotional control.'
        }
      ],
      summaryBullets: language === 'ro'
        ? [
            'Fiecare episod aduce o temă strategică clară: opening, structură, tactică, endgame.',
            'Nu memora mutări din serial; extrage principii reutilizabile în partidele tale.',
            'Secvența ideală de antrenament: vezi poziția → identifici tema → verifici planul.'
          ]
        : [
            'Each episode brings one clear strategic theme: opening, structure, tactics, endgame.',
            'Do not memorize show moves; extract reusable principles for your own games.',
            'Ideal training loop: view position → identify theme → validate plan.'
          ],
      quizQuestions: language === 'ro'
        ? [
            {
              id: 'gq1',
              question: 'Care este ideea centrală din această analiză GameLab?',
              options: [
                'Să memorizezi fiecare mutare exact ca în serial.',
                'Să extragi principiul practic din fiecare poziție-cheie.',
                'Să joci mereu gambit indiferent de poziție.',
                'Să eviți complet finalurile.'
              ],
              correctIndex: 1,
              explanation: 'Exact. Focusul e pe principii reutilizabile, nu pe memorare brută.'
            },
            {
              id: 'gq2',
              question: 'În Episodul 4 (Middle Game), ce decide calitatea planului?',
              options: [
                'Numărul de piese schimbate cât mai repede.',
                'Timing-ul dintre dezvoltare, tensiune și simplificare.',
                'Mutarea damei în fiecare tură.',
                'Forțarea atacului fără dezvoltare.'
              ],
              correctIndex: 1,
              explanation: 'Corect. În mijlocul de joc, timing-ul separă planul bun de planul superficial.'
            },
            {
              id: 'gq3',
              question: 'Cum tratezi furcile (forks) în practică?',
              options: [
                'Apar doar din noroc tactic.',
                'Le pregătești prin poziție activă și coordonare.',
                'Nu sunt relevante peste 1000 ELO.',
                'Sunt valabile doar cu calul.'
              ],
              correctIndex: 1,
              explanation: 'Perfect. Tactica eficientă vine din piese active și coordonate.'
            },
            {
              id: 'gq4',
              question: 'Ce mesaj duci din Episodul 7 (End Game)?',
              options: [
                'Dacă ai avantaj, câștigi automat.',
                'Avantajul se convertește doar prin tehnică și disciplină.',
                'Finalurile sunt mai puțin importante decât deschiderile.',
                'Trebuie să ataci regele cu orice preț.'
              ],
              correctIndex: 1,
              explanation: 'Corect. Finalul cere claritate tehnică, nu impuls.'
            }
          ]
        : [
            {
              id: 'gq1',
              question: 'What is the core idea of this GameLab analysis?',
              options: [
                'Memorize every move exactly as in the show.',
                'Extract practical principles from each key position.',
                'Always play gambits in any position.',
                'Avoid endgames completely.'
              ],
              correctIndex: 1,
              explanation: 'Exactly. The focus is reusable principles, not raw memorization.'
            },
            {
              id: 'gq2',
              question: 'In Episode 4 (Middle Game), what decides plan quality?',
              options: [
                'Trading as many pieces as possible immediately.',
                'Timing between development, tension, and simplification.',
                'Moving the queen every move.',
                'Forcing attack without development.'
              ],
              correctIndex: 1,
              explanation: 'Correct. In the middlegame, timing separates good plans from shallow ones.'
            },
            {
              id: 'gq3',
              question: 'How should forks be treated in practical play?',
              options: [
                'They come only from tactical luck.',
                'They are prepared through activity and coordination.',
                'They are irrelevant above 1000 ELO.',
                'They only exist with knights.'
              ],
              correctIndex: 1,
              explanation: 'Great. Efficient tactics come from active and coordinated pieces.'
            },
            {
              id: 'gq4',
              question: 'What is the key takeaway from Episode 7 (End Game)?',
              options: [
                'Any advantage converts automatically.',
                'Advantages convert through technique and discipline.',
                'Endgames matter less than openings.',
                'Always attack the king at all costs.'
              ],
              correctIndex: 1,
              explanation: 'Correct. Endgame conversion requires technical clarity, not impulse.'
            }
          ]
    }
  ]), [language]);

  const selectedMindhubArticle = useMemo(
    () => mindhubArticles.find((a) => a.id === mindhubSelectedArticleId) || null,
    [mindhubArticles, mindhubSelectedArticleId]
  );
  const selectedGamelabArticle = useMemo(
    () => gamelabArticles.find((a) => a.id === gamelabSelectedArticleId) || null,
    [gamelabArticles, gamelabSelectedArticleId]
  );
  const selectedGamelabPosition = selectedGamelabArticle?.positions?.[gamelabPositionIndex] || null;
  const selectedGamelabVisited = useMemo(() => {
    if (!selectedGamelabArticle) return new Set();
    const raw = gamelabVisitedPositions[selectedGamelabArticle.id];
    const list = Array.isArray(raw) ? raw : [];
    return new Set(list.filter((v) => Number.isInteger(v) && v >= 0));
  }, [selectedGamelabArticle, gamelabVisitedPositions]);
  const selectedGamelabQuizAnswers = useMemo(() => {
    if (!selectedGamelabArticle) return {};
    const answers = gamelabQuizAnswers[selectedGamelabArticle.id];
    return answers && typeof answers === 'object' ? answers : {};
  }, [selectedGamelabArticle, gamelabQuizAnswers]);
  const gamelabQuizSummary = useMemo(() => {
    if (!selectedGamelabArticle) {
      return { answered: 0, total: 0, correct: 0, pct: 0 };
    }
    const questions = selectedGamelabArticle.quizQuestions || [];
    const total = questions.length;
    const answered = questions.filter((q) => typeof selectedGamelabQuizAnswers[q.id] === 'number').length;
    const correct = questions.filter((q) => selectedGamelabQuizAnswers[q.id] === q.correctIndex).length;
    const pct = answered > 0 ? Math.round((correct / answered) * 100) : 0;
    return { answered, total, correct, pct };
  }, [selectedGamelabArticle, selectedGamelabQuizAnswers]);
  const gamelabArticleProgress = useMemo(() => {
    if (!selectedGamelabArticle) {
      return { visited: 0, totalPositions: 0, answered: 0, totalQuiz: 0, progressPct: 0 };
    }
    const totalPositions = Math.max(1, selectedGamelabArticle.positions?.length || 0);
    const visited = Math.min(totalPositions, selectedGamelabVisited.size || 0);
    const totalQuiz = Math.max(1, selectedGamelabArticle.quizQuestions?.length || 0);
    const answered = Math.min(totalQuiz, gamelabQuizSummary.answered);
    const progressPct = Math.min(
      100,
      Math.round((visited / totalPositions) * 70 + (answered / totalQuiz) * 30)
    );
    return { visited, totalPositions, answered, totalQuiz, progressPct };
  }, [selectedGamelabArticle, selectedGamelabVisited, gamelabQuizSummary.answered]);

  const isMindhubReadingMode =
    activeArea === 'mindhub' && mindhubIsReading && Boolean(selectedMindhubArticle);

  const selectedMindhubArticleSlug = selectedMindhubArticle?.slug || '';
  const mindhubQuizSummary = useMemo(() => {
    if (!selectedMindhubArticle) {
      return { answered: 0, total: 0, correct: 0, pct: 0 };
    }
    const total = selectedMindhubArticle.quizQuestions.length;
    const answered = selectedMindhubArticle.quizQuestions.filter(
      (q) => typeof mindhubQuizAnswers[q.id] === 'number'
    ).length;
    const correct = selectedMindhubArticle.quizQuestions.filter(
      (q) => mindhubQuizAnswers[q.id] === q.correctIndex
    ).length;
    const pct = answered > 0 ? Math.round((correct / answered) * 100) : 0;
    return { answered, total, correct, pct };
  }, [selectedMindhubArticle, mindhubQuizAnswers]);

  useEffect(() => {
    if (!initialArea) return;
    setActiveArea(initialArea);
  }, [initialArea]);

  useEffect(() => {
    if (activeArea !== 'academy' && activeArea !== 'openings') {
      setAcademyModuleDetailOpen(false);
      setAcademyLessonFilter('all');
      setAcademySectionPage({});
      setAcademyOpenSections({});
    }
    if (activeArea !== 'puzzles') {
      setPuzzleSectionPage({});
      setPuzzleOpenSections({});
      setPuzzleModuleDetailOpen(false);
      setPuzzleGroupFilter('all');
    }
  }, [activeArea]);

  useEffect(() => {
    if (typeof onAreaChange === 'function') {
      onAreaChange(activeArea);
    }
  }, [activeArea, onAreaChange]);

  useEffect(() => {
    if (activeArea !== 'mindhub') {
      setMindhubSelectedArticleId('');
      setMindhubIsReading(false);
      setMindhubReadingProgress(0);
      setMindhubQuizAnswers({});
      setMindhubMomentsOpen(false);
    }
  }, [activeArea]);

  useEffect(() => {
    if (activeArea !== 'gamelab') {
      setGamelabSelectedArticleId('');
      setGamelabPositionIndex(0);
    }
  }, [activeArea]);

  useEffect(() => {
    if (activeArea !== 'mindhub') return;
    if (mindhubSelectedArticleId) return;
    const firstArticleId = mindhubArticles[0]?.id || '';
    if (firstArticleId) setMindhubSelectedArticleId(firstArticleId);
  }, [activeArea, mindhubArticles, mindhubSelectedArticleId]);

  useEffect(() => {
    if (activeArea !== 'gamelab') return;
    if (gamelabSelectedArticleId) return;
    const firstArticleId = gamelabArticles[0]?.id || '';
    if (firstArticleId) setGamelabSelectedArticleId(firstArticleId);
  }, [activeArea, gamelabArticles, gamelabSelectedArticleId]);

  useEffect(() => {
    if (!selectedGamelabArticle) return;
    const safeIndex = Math.max(0, Math.floor(Number(gamelabPositionIndex) || 0));
    setGamelabVisitedPositions((prev) => {
      const current = Array.isArray(prev[selectedGamelabArticle.id]) ? prev[selectedGamelabArticle.id] : [];
      if (current.includes(safeIndex)) return prev;
      return {
        ...prev,
        [selectedGamelabArticle.id]: [...current, safeIndex].sort((a, b) => a - b)
      };
    });
  }, [selectedGamelabArticle, gamelabPositionIndex]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        'cm_gamelab_progress_v1',
        JSON.stringify({
          visitedPositions: gamelabVisitedPositions,
          quizAnswers: gamelabQuizAnswers
        })
      );
    } catch {
      // no-op
    }
  }, [gamelabVisitedPositions, gamelabQuizAnswers]);

  useEffect(() => {
    setMindhubMomentsOpen(false);
  }, [mindhubSelectedArticleId, mindhubIsReading]);

  useEffect(() => {
    if (activeArea !== 'mindhub') return;
    const slug = String(initialMindhubArticleSlug || '').trim().toLowerCase();
    if (!slug) {
      setMindhubIsReading(false);
      setMindhubMomentsOpen(false);
      return;
    }
    const target = mindhubArticles.find((a) => String(a.slug || '').toLowerCase() === slug);
    if (target) {
      setMindhubSelectedArticleId(target.id);
      setMindhubIsReading(true);
      setMindhubQuizAnswers({});
      setMindhubReadingProgress(0);
      setMindhubMomentsOpen(false);
    }
  }, [activeArea, initialMindhubArticleSlug, mindhubArticles]);

  useEffect(() => {
    if (typeof onMindhubArticleChange === 'function') {
      onMindhubArticleChange(activeArea === 'mindhub' && mindhubIsReading ? selectedMindhubArticleSlug : '');
    }
    if (typeof window === 'undefined') return;
    if (activeArea !== 'mindhub') return;
    const desiredHash = mindhubIsReading && selectedMindhubArticleSlug
      ? `#/mindhub/${encodeURIComponent(selectedMindhubArticleSlug)}`
      : '#/mindhub';
    if (window.location.hash !== desiredHash) {
      window.history.replaceState(null, '', desiredHash);
    }
  }, [activeArea, selectedMindhubArticleSlug, onMindhubArticleChange, mindhubIsReading]);

  useEffect(() => {
    if (activeArea !== 'mindhub' || !selectedMindhubArticle || !mindhubIsReading) return undefined;

    let rafId = null;

    const calculateProgress = () => {
      const articleEl = mindhubArticleViewRef.current;
      if (!articleEl) return;

      const rect = articleEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const visibleTopOffset = Math.max(0, 96 - rect.top);
      const totalScrollable = Math.max(1, rect.height - viewportHeight + 120);
      const rawPct = Math.max(0, Math.min(100, (visibleTopOffset / totalScrollable) * 100));

      // Stabilize tiny jitter from pixel-level scroll changes.
      const snapped = Math.round(rawPct / 2) * 2;
      const prev = mindhubProgressRef.current;
      const stabilized = Math.abs(snapped - prev) < 2 ? prev : snapped;
      const safePct = Math.max(0, Math.min(100, stabilized));

      mindhubProgressRef.current = safePct;
      setMindhubReadingProgress(safePct);
    };

    const onScrollOrResize = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        calculateProgress();
      });
    };

    onScrollOrResize();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [activeArea, selectedMindhubArticle, mindhubIsReading]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        'cm_guest_puzzle_starts_by_category',
        JSON.stringify(guestPuzzleStartsByCategory || {})
      );
    } catch {
      // ignore storage errors
    }
  }, [guestPuzzleStartsByCategory]);

  useEffect(() => {
    let alive = true;

    async function loadHistoricalPuzzleProgress() {
      if (activeArea !== 'puzzles') return;
      if (!user?.id) {
        if (alive) setPuzzleStartsByGroup({});
        return;
      }
      const startsMap = await fetchPuzzleStartsByThemeGroup({
        userId: user.id,
        activeThemes: PUZZLE_ACTIVE_THEME_IDS
      });
      if (!alive) return;
      setPuzzleStartsByGroup((prev) => ({
        ...(startsMap || {}),
        ...(prev || {})
      }));
    }

    loadHistoricalPuzzleProgress();
    return () => {
      alive = false;
    };
  }, [activeArea, user?.id]);

  useEffect(() => {
    let alive = true;
    async function loadUserPlan() {
      if (!user?.id) {
        setUserPlan('explorer');
        return;
      }
      const plan = await fetchMyEffectivePlan();
      if (!alive) return;
      setUserPlan(String(plan?.planCode || 'explorer'));
    }
    loadUserPlan();
    return () => {
      alive = false;
    };
  }, [user?.id]);

  useEffect(() => {
    let alive = true;

    async function hydrateModules(rawModules) {
      if (!rawModules || !rawModules.length) return [];
      const allLessons = rawModules.flatMap((m) => m.lessons || []);
      const lessonDbIds = allLessons.map((l) => l.dbId).filter(Boolean);

      let stepCounts = new Map();
      let progressMap = new Map();
      try {
        stepCounts = await fetchLessonStepCounts(lessonDbIds);
      } catch {
        stepCounts = new Map();
      }
      if (user?.id) {
        try {
          progressMap = await fetchUserLessonProgress(lessonDbIds);
        } catch {
          progressMap = new Map();
        }
      }

      return rawModules.map((m) => {
        const lessons = (m.lessons || []).map((l) => {
          const dbId = l.dbId;
          const totalSteps = dbId ? stepCounts.get(dbId) || 0 : 0;
          const progress = dbId ? progressMap.get(dbId) : null;
          const rawStepsCompleted = progress?.completed
            ? totalSteps
            : Math.max(0, Number(progress?.currentStep || 0) - 1);
          const stepsCompleted = totalSteps > 0
            ? Math.min(totalSteps, rawStepsCompleted)
            : rawStepsCompleted;
          const started = stepsCompleted > 0;
          const status = progress?.completed ? 'completed' : started ? 'in_progress' : 'new';
          const progressPct =
            totalSteps > 0 ? Math.min(100, Math.round((stepsCompleted / totalSteps) * 100)) : 0;

          return {
            ...l,
            status,
            stepsCompleted,
            stepsTotal: totalSteps,
            progressPct
          };
        });

        const totalLessons = lessons.length || 0;
        const totalLessonPct = lessons.reduce(
          (sum, l) => sum + Math.min(100, Math.max(0, Number(l.progressPct || 0))),
          0
        );
        const moduleProgress = totalLessons > 0 ? Math.ceil(totalLessonPct / totalLessons) : 0;

        return {
          ...m,
          lessons,
          progress: moduleProgress
        };
      });
    }

    async function loadModules() {
      if (!isSupabaseConfigured()) {
        // Keep last known good state; just mark as disconnected.
        setSupabaseError('');
        return;
      }

      setIsLoadingModules(true);
      setSupabaseError('');
      try {
        const [academyData, openingsData] = await Promise.all([
          fetchBeginnerModules(language),
          fetchOpeningsModules(language)
        ]);
        if (!alive) return;

        const nextAcademyModules = await hydrateModules(academyData || []);
        const nextOpeningsModules = await hydrateModules(openingsData || []);

        if (nextAcademyModules.length) {
          setSupabaseModules(nextAcademyModules);
          setOpenModuleIds([nextAcademyModules[0].id]);
          setSelectedModuleId((prev) => prev || nextAcademyModules[0].id);
        }
        if (nextOpeningsModules.length) {
          setSupabaseOpeningsModules(nextOpeningsModules);
        }
      } catch (error) {
        if (!alive) return;
        // Keep last known good state; don't flicker the UI back to local fallback.
        console.warn('[AcademyHome] Failed to load modules/progress from Supabase:', error);
        setSupabaseError(mapUiErrorMessage(error, 'academy'));
      } finally {
        if (alive) setIsLoadingModules(false);
      }
    }

    loadModules();
    return () => {
      alive = false;
    };
  }, [language, user?.id, mapUiErrorMessage]);

  const modulesData = useMemo(() => {
    if (!supabaseModules || !supabaseModules.length) return beginnerModules;

    // Merge: keep Supabase content where present, but always show teaser modules (2-4) as Coming soon.
    const bySort = new Map();
    for (let i = 0; i < beginnerModules.length; i += 1) {
      const m = beginnerModules[i];
      const k = Number(m.sortOrder || i + 1);
      bySort.set(k, m);
    }
    for (let i = 0; i < supabaseModules.length; i += 1) {
      const m = supabaseModules[i];
      const k = Number(m.sortOrder || i + 1);
      bySort.set(k, m);
    }
    return Array.from(bySort.entries())
      .sort((a, b) => a[0] - b[0])
      .map((x) => x[1]);
  }, [supabaseModules, beginnerModules]);

  const openingsModulesData = useMemo(() => {
    const sourceModules = (supabaseOpeningsModules && supabaseOpeningsModules.length)
      ? supabaseOpeningsModules
      : openingsFallbackModules;

    const allLessons = sourceModules.flatMap((m) => m.lessons || []);
    const whiteLessons = [];
    const blackLessons = [];

    for (let i = 0; i < allLessons.length; i += 1) {
      const lesson = allLessons[i];
      const lessonId = String(lesson?.id || '').toLowerCase();
      const lessonTitle = String(lesson?.title || '').toLowerCase();
      const isKnownWhiteOpening =
        lessonId.includes('b-o1-l') ||
        lessonId.includes('italian') ||
        lessonId.includes('london') ||
        lessonTitle.includes('italian') ||
        lessonTitle.includes('londra') ||
        lessonTitle.includes('london');
      if (isKnownWhiteOpening) whiteLessons.push(lesson);
      else blackLessons.push(lesson);
    }

    const fallbackWhite = openingsFallbackModules.find((m) => m.id === 'openings-white') || openingsFallbackModules[0];
    const fallbackBlack = openingsFallbackModules.find((m) => m.id === 'openings-black') || openingsFallbackModules[1];

    const mergeWithFallbackTeasers = (liveLessons, fallbackLessons) => {
      const merged = new Map();
      (liveLessons || []).forEach((lesson) => {
        merged.set(String(lesson.id || ''), lesson);
      });
      (fallbackLessons || []).forEach((lesson) => {
        const key = String(lesson.id || '');
        if (!merged.has(key)) merged.set(key, lesson);
      });
      return Array.from(merged.values()).sort(
        (a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)
      );
    };

    const normalizedWhite = {
      ...fallbackWhite,
      lessons: mergeWithFallbackTeasers(whiteLessons, fallbackWhite.lessons)
    };

    const normalizedBlack = {
      ...fallbackBlack,
      lessons: mergeWithFallbackTeasers(blackLessons, fallbackBlack.lessons)
    };

    return [normalizedWhite, normalizedBlack];
  }, [supabaseOpeningsModules, openingsFallbackModules]);

  const gatedModulesData = useMemo(() => {
    const isGuest = !user?.id;
    const normalizedPlan = String(userPlan || 'explorer').toLowerCase();

    const getRequiredPlanForLesson = (moduleSortOrder, lessonSortOrder) => {
      if (moduleSortOrder === 1) return 'explorer';
      if ([2, 3, 4].includes(moduleSortOrder) && lessonSortOrder <= 2) return 'explorer';
      if (moduleSortOrder === 2) return 'starter';
      return 'premium';
    };

    const isPlanAllowed = (requiredPlan) => {
      if (requiredPlan === 'explorer') return true;
      if (requiredPlan === 'starter') return normalizedPlan === 'starter' || normalizedPlan === 'premium';
      return normalizedPlan === 'premium';
    };

    return (modulesData || []).map((module) => {
      const moduleSortOrder = Number(module.sortOrder || 0);
      const moduleIsComingSoon = module.available === false;
      const guestLessons = (module.lessons || []).map((lesson, idx) => {
        const existsInDb = Boolean(lesson.dbId);
        const lessonSortOrder = Number(lesson.sortOrder || idx + 1);
        const lessonComingSoon = moduleIsComingSoon || !existsInDb;
        const requiredPlan = getRequiredPlanForLesson(moduleSortOrder, lessonSortOrder);
        const guestNeedsLogin = !lessonComingSoon && (idx >= 2 || [2, 3, 4].includes(moduleSortOrder));
        const planLocked = !lessonComingSoon && !isGuest && !isPlanAllowed(requiredPlan);
        const lessonAvailable =
          !lessonComingSoon &&
          (lesson.available !== false) &&
          (!isGuest || !guestNeedsLogin) &&
          !planLocked;
        return {
          ...lesson,
          // "Coming soon" should appear only if lesson is missing from DB (or module itself is not published yet).
          comingSoon: lessonComingSoon,
          available: lessonAvailable,
          guestLocked: isGuest ? guestNeedsLogin : false,
          planLocked,
          requiredPlan,
          status: isGuest ? 'new' : lesson.status,
          stepsCompleted: isGuest ? 0 : lesson.stepsCompleted,
          progressPct: isGuest ? 0 : lesson.progressPct
        };
      });

      // Academy V2 rule: module progress is derived from lesson states only.
      // completed = 100%, in_progress = 50%, new/locked = 0%.
      const progressLessons = guestLessons.filter((l) => !l.comingSoon);
      const completedCount = progressLessons.filter((l) => l.status === 'completed').length;
      const inProgressCount = progressLessons.filter((l) => l.status === 'in_progress').length;
      const moduleProgress =
        progressLessons.length > 0
          ? Math.round(((completedCount + inProgressCount * 0.5) / progressLessons.length) * 100)
          : 0;

      return {
        ...module,
        // Keep module availability from source; do not unlock teaser modules for guests.
        available: moduleIsComingSoon ? false : true,
        progress: moduleProgress,
        lessons: guestLessons
      };
    });
  }, [modulesData, user?.id, userPlan]);

  const openingsGatedModulesData = useMemo(() => {
    return (openingsModulesData || []).map((module) => {
      const moduleIsComingSoon = module.available === false;
      const lessons = (module.lessons || []).map((lesson) => {
        const existsInDb = Boolean(lesson.dbId);
        const lessonComingSoon = moduleIsComingSoon || !existsInDb || lesson.available === false;
        return {
          ...lesson,
          comingSoon: lessonComingSoon,
          available: !lessonComingSoon,
          guestLocked: false,
          planLocked: false,
          requiredPlan: 'explorer'
        };
      });

      const progressLessons = lessons.filter((l) => !l.comingSoon);
      const completedCount = progressLessons.filter((l) => l.status === 'completed').length;
      const inProgressCount = progressLessons.filter((l) => l.status === 'in_progress').length;
      const moduleProgress =
        progressLessons.length > 0
          ? Math.round(((completedCount + inProgressCount * 0.5) / progressLessons.length) * 100)
          : 0;

      return {
        ...module,
        available: moduleIsComingSoon ? false : true,
        progress: moduleProgress,
        lessons
      };
    });
  }, [openingsModulesData]);

  const isLearningArea = activeArea === 'academy' || activeArea === 'openings';
  const learningModulesData = activeArea === 'openings' ? openingsGatedModulesData : gatedModulesData;

  useEffect(() => {
    if (!focusLessonSlug) return;
    if (!gatedModulesData?.length) return;

    // Ensure the module containing the lesson is open.
    const containingModule = gatedModulesData.find((m) =>
      (m.lessons || []).some((l) => l.id === focusLessonSlug)
    );
    if (containingModule?.id) {
      setOpenModuleIds((prev) =>
        prev.includes(containingModule.id) ? prev : [...prev, containingModule.id]
      );
      setSelectedModuleId(containingModule.id);
      setAcademyModuleDetailOpen(true);
    }
    setSelectedLessonId(focusLessonSlug);

    // Scroll + flash after the UI has rendered the target row.
    window.setTimeout(() => {
      const el = document.getElementById(`lesson-${focusLessonSlug}`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setFlashLessonSlug(focusLessonSlug);
      window.setTimeout(() => setFlashLessonSlug(''), 1400);
    }, 220);
  }, [focusNonce, focusLessonSlug, gatedModulesData]);

  const difficultyLabel = (d) => {
    const v = String(d || '').toLowerCase();
    if (v === 'hard') return language === 'ro' ? 'Greu' : 'Hard';
    if (v === 'medium') return language === 'ro' ? 'Mediu' : 'Medium';
    return language === 'ro' ? 'Ușor' : 'Easy';
  };

  const filteredModulesData = useMemo(() => {
    if (!isLearningArea) return learningModulesData;
    const q = treeSearch.trim().toLowerCase();
    if (!q) return learningModulesData;

    return (learningModulesData || [])
      .map((module) => {
        const moduleText = `${module.title || ''} ${module.summary || ''}`.toLowerCase();
        const moduleMatch = moduleText.includes(q);
        const lessons = (module.lessons || []).filter((lesson) => {
          const lessonText = `${lesson.title || ''} ${lesson.section || ''} ${lesson.description || ''}`.toLowerCase();
          return moduleMatch || lessonText.includes(q);
        });
        if (!lessons.length && !moduleMatch) return null;
        return {
          ...module,
          lessons: moduleMatch ? module.lessons || [] : lessons
        };
      })
      .filter(Boolean);
  }, [learningModulesData, treeSearch, isLearningArea]);

  useEffect(() => {
    if (!isLearningArea) return;
    const q = treeSearch.trim();
    if (!q) return;
    const ids = filteredModulesData.map((m) => m.id);
    if (ids.length) {
      setOpenModuleIds(ids);
      setSelectedModuleId((prev) => (ids.includes(prev) ? prev : ids[0]));
      setSelectedLessonId((prev) => {
        if (!prev) return '';
        const hasSelected = filteredModulesData.some((m) => (m.lessons || []).some((l) => l.id === prev));
        return hasSelected ? prev : '';
      });
    }
  }, [treeSearch, filteredModulesData, isLearningArea]);

  useEffect(() => {
    if (activeArea !== 'puzzles' && activeArea !== 'dailyquest') return;
    if (!puzzleCategories.length) return;

    const firstCategory = puzzleCategories.find((c) => (c.puzzles || []).length > 0) || puzzleCategories[0];
    const firstPuzzle = firstCategory?.puzzles?.[0];
    const firstPuzzleRating = Number(firstPuzzle?.rating || 0);
    const firstDifficultyId =
      firstPuzzleRating < 1000 ? 'easy' :
      firstPuzzleRating < 1300 ? 'medium' :
      firstPuzzleRating < 1600 ? 'advanced' :
      firstPuzzleRating < 2000 ? 'hard' : 'expert';

    setSelectedPuzzleCategoryId((prev) => prev || firstCategory.id);
    setSelectedPuzzleDifficultyId((prev) => prev || firstDifficultyId);
  }, [activeArea, puzzleCategories]);

  useEffect(() => {
    if (activeArea !== 'puzzles') return;
    setPuzzleSessionDiagramNo(1);
  }, [activeArea, selectedPuzzleCategoryId, selectedPuzzleDifficultyId]);

  useEffect(() => {
    let alive = true;

    async function loadDbPuzzles() {
      if (activeArea !== 'puzzles' && activeArea !== 'dailyquest') return;
      if (!isSupabaseConfigured()) {
        setDbPuzzleCategories([]);
        setPuzzleError('');
        return;
      }

      setIsLoadingPuzzles(true);
      setPuzzleError('');
      try {
        let recommendedTier = 'easy';
        if (user?.id) {
          const skill = await fetchUserPuzzleSkill({ userId: user.id });
          if (alive) {
            setPuzzleSkillProfile(skill);
          }
          recommendedTier = getPuzzleTierFromRating(skill?.puzzleRating || 500);
        } else if (alive) {
          setPuzzleSkillProfile({ puzzlePoints: 0, puzzleRating: 500 });
        }

        if (!alive) return;
        const activeSkeleton = PUZZLE_ACTIVE_THEME_IDS.map((theme) => ({
          ...mapThemeRowsToUiCategory({ theme, rows: [], language }),
          comingSoon: false
        }));
        const comingSoonSkeleton = PUZZLE_COMING_SOON_THEME_IDS.map((theme) => ({
          ...mapThemeRowsToUiCategory({ theme, rows: [], language }),
          comingSoon: true
        }));
        const orderedCategories = [...activeSkeleton, ...comingSoonSkeleton];
        setDbPuzzleCategories(orderedCategories);
        setLoadedPuzzleCategoryMap({});
        setSelectedPuzzleDifficultyId((prev) => prev || recommendedTier);
        const firstActiveId = activeSkeleton[0]?.id || '';
        if (firstActiveId) {
          setSelectedPuzzleCategoryId((prev) => prev || firstActiveId);
        }
      } catch (e) {
        if (!alive) return;
        setDbPuzzleCategories([]);
        setPuzzleError(mapUiErrorMessage(e, 'puzzles'));
      } finally {
        if (alive) setIsLoadingPuzzles(false);
      }
    }

    loadDbPuzzles();
    return () => {
      alive = false;
    };
  }, [activeArea, language, user?.id, mapUiErrorMessage]);

  useEffect(() => {
    let alive = true;

    async function loadSelectedPuzzleCategory() {
      if (activeArea !== 'puzzles' && activeArea !== 'dailyquest') return;
      const selectedId = String(selectedPuzzleCategoryId || '').trim().toLowerCase();
      if (!selectedId) return;
      const category = (dbPuzzleCategories || []).find((c) => c.id === selectedId);
      if (!category || category.comingSoon) return;
      const alreadyLoaded = Boolean(loadedPuzzleCategoryMap[selectedId]);
      const alreadyHasEnoughForMain = (category?.puzzles || []).length >= 5;
      if (alreadyLoaded && alreadyHasEnoughForMain) return;

      setLoadingPuzzleCategoryId(selectedId);
      try {
        let rows = await fetchPuzzlesByTheme({
          theme: selectedId,
          limit: 5,
          userId: user?.id || ''
        });

        // If user-specific pool is too narrow, retry without user filter for category preview.
        if ((!rows || rows.length < 2) && user?.id) {
          rows = await fetchPuzzlesByTheme({
            theme: selectedId,
            limit: 5,
            userId: ''
          });
        }

        if (!alive) return;
        setDbPuzzleCategories((prev) =>
          (prev || []).map((c) =>
            c.id === selectedId
              ? { ...mapThemeRowsToUiCategory({ theme: selectedId, rows: rows || [], language }), comingSoon: false }
              : c
          )
        );
        setLoadedPuzzleCategoryMap((prev) => ({ ...prev, [selectedId]: true }));

        const firstRating = Number((rows && rows[0] && rows[0].rating) || 0);
        const nextDifficulty =
          firstRating < 1000 ? 'easy' :
          firstRating < 1300 ? 'medium' :
          firstRating < 1600 ? 'advanced' :
          firstRating < 2000 ? 'hard' : 'expert';
        if (rows && rows.length) {
          setSelectedPuzzleDifficultyId(nextDifficulty);
        }
      } catch {
        if (!alive) return;
        // Keep as not-loaded so user can retry by reselecting category.
        setLoadedPuzzleCategoryMap((prev) => ({ ...prev, [selectedId]: false }));
      } finally {
        if (alive) setLoadingPuzzleCategoryId('');
      }
    }

    loadSelectedPuzzleCategory();
    return () => {
      alive = false;
    };
  }, [activeArea, selectedPuzzleCategoryId, dbPuzzleCategories, loadedPuzzleCategoryMap, language, user?.id]);

  // Background sequential prefetch: load a tiny preview for remaining active categories.
  useEffect(() => {
    let alive = true;

    async function prefetchNextCategory() {
      if (activeArea !== 'puzzles' && activeArea !== 'dailyquest') return;
      if (loadingPuzzleCategoryId) return;
      const nextCategory = (dbPuzzleCategories || []).find(
        (c) => !c.comingSoon && c.id !== selectedPuzzleCategoryId && !loadedPuzzleCategoryMap[c.id]
      );
      if (!nextCategory?.id) return;

      const nextId = String(nextCategory.id).toLowerCase();
      setLoadingPuzzleCategoryId(nextId);
      try {
        let rows = await fetchPuzzlesByTheme({
          theme: nextId,
          limit: 2,
          userId: user?.id || ''
        });
        if ((!rows || rows.length < 1) && user?.id) {
          rows = await fetchPuzzlesByTheme({
            theme: nextId,
            limit: 2,
            userId: ''
          });
        }

        if (!alive) return;
        setDbPuzzleCategories((prev) =>
          (prev || []).map((c) =>
            c.id === nextId
              ? { ...mapThemeRowsToUiCategory({ theme: nextId, rows: rows || [], language }), comingSoon: false }
              : c
          )
        );
        setLoadedPuzzleCategoryMap((prev) => ({ ...prev, [nextId]: true }));
      } catch {
        if (!alive) return;
        setLoadedPuzzleCategoryMap((prev) => ({ ...prev, [nextId]: false }));
      } finally {
        if (alive) setLoadingPuzzleCategoryId('');
      }
    }

    prefetchNextCategory();
    return () => {
      alive = false;
    };
  }, [activeArea, dbPuzzleCategories, loadedPuzzleCategoryMap, selectedPuzzleCategoryId, loadingPuzzleCategoryId, language, user?.id]);

  useEffect(() => {
    let alive = true;

    async function loadPuzzleUserStats() {
      if (activeArea !== 'puzzles' && activeArea !== 'dailyquest') return;
      if (!user?.id) {
        if (alive) {
          setPuzzleUserStats(null);
          setIsLoadingPuzzleUserStats(false);
        }
        return;
      }

      setIsLoadingPuzzleUserStats(true);
      try {
        const stats = await fetchPuzzlePanelStats({ userId: user.id });
        if (!alive) return;
        setPuzzleUserStats(stats || null);
        if (stats) {
          setPuzzleSkillProfile((prev) => ({
            puzzlePoints: Number.isFinite(Number(stats.puzzlePoints)) ? Number(stats.puzzlePoints) : (prev?.puzzlePoints || 0),
            puzzleRating: Number.isFinite(Number(stats.puzzleRating)) ? Number(stats.puzzleRating) : (prev?.puzzleRating || 500)
          }));
        }
      } catch {
        if (!alive) return;
        setPuzzleUserStats(null);
      } finally {
        if (alive) setIsLoadingPuzzleUserStats(false);
      }
    }

    loadPuzzleUserStats();
    return () => {
      alive = false;
    };
  }, [activeArea, user?.id]);

  useEffect(() => {
    let alive = true;

    async function loadQuizHub() {
      if (activeArea !== 'quizzes' && activeArea !== 'dailyquest') return;
      if (!isQuizHubConfigured()) {
        if (!alive) return;
        setQuizBankQuestions([]);
        setQuizHubError('');
        return;
      }

      setIsLoadingQuizHub(true);
      setQuizHubError('');
      try {
        const payload = await fetchQuizHubBank(language);
        if (!alive) return;
        const questions = Array.isArray(payload?.questions) ? payload.questions : [];
        const counts = payload?.difficultyCounts || { easy: 0, medium: 0, hard: 0, expert: 0 };
        setQuizBankQuestions(questions);
        if (!counts[selectedQuizDifficulty]) {
          if (counts.easy > 0) setSelectedQuizDifficulty('easy');
          else if (counts.medium > 0) setSelectedQuizDifficulty('medium');
          else if (counts.hard > 0) setSelectedQuizDifficulty('hard');
          else if (counts.expert > 0) setSelectedQuizDifficulty('expert');
        }
      } catch (e) {
        if (!alive) return;
        setQuizBankQuestions([]);
        setQuizHubError(mapUiErrorMessage(e, 'quizzes'));
      } finally {
        if (alive) setIsLoadingQuizHub(false);
      }
    }

    loadQuizHub();
    return () => {
      alive = false;
    };
  }, [activeArea, language, selectedQuizDifficulty, mapUiErrorMessage]);

  useEffect(() => {
    // Reset quiz session when quiz filters change.
    setQuizSessionQuestions([]);
    setQuizSessionStarted(false);
    setQuizSessionCompleted(false);
    setQuizSessionIndex(0);
    setQuizSessionScore(0);
    setQuizSessionLives(3);
    setQuizSessionAnsweredCount(0);
    setQuizSessionCorrectCount(0);
    setQuizSelectedOptionId('');
    setQuizChecked(false);
  }, [selectedQuizDifficulty, selectedQuizTopic, language]);

  const visibleModules = useMemo(
    () => (isLearningArea ? filteredModulesData : learningModulesData),
    [isLearningArea, filteredModulesData, learningModulesData]
  );

  const selectedModule = useMemo(() => {
    if (!visibleModules?.length) return null;
    return visibleModules.find((m) => m.id === selectedModuleId) || visibleModules[0];
  }, [visibleModules, selectedModuleId]);

  const selectedLesson = useMemo(() => {
    const lessons = selectedModule?.lessons || [];
    if (!lessons.length) return null;
    const found = lessons.find((l) => l.id === selectedLessonId);
    return found || lessons[0];
  }, [selectedModule, selectedLessonId]);

  const parseDurationMinutes = (value) => {
    const match = String(value || '').match(/(\d+)/);
    return match ? Number(match[1]) : 0;
  };

  const getLessonUiStatus = (lesson) => {
    if (!lesson) return 'pending';
    if (lesson.comingSoon || lesson.planLocked || lesson.guestLocked || lesson.available === false) return 'locked';
    if (lesson.status === 'completed') return 'completed';
    if (lesson.status === 'in_progress') return 'in_progress';
    return 'pending';
  };

  const getLessonLockReason = (lesson) => {
    if (!lesson) return 'none';
    if (lesson.comingSoon) return 'coming';
    if (lesson.planLocked) return 'plan';
    if (lesson.guestLocked) return 'guest';
    if (lesson.available === false) return 'coming';
    return 'none';
  };

  const getLessonLockLabel = (lesson) => {
    const reason = getLessonLockReason(lesson);
    if (reason === 'plan') return language === 'ro' ? 'Upgrade necesar' : 'Upgrade required';
    if (reason === 'guest') return language === 'ro' ? 'Creează cont' : 'Create account';
    if (reason === 'coming') return language === 'ro' ? 'În curând' : 'Coming soon';
    return language === 'ro' ? 'Neînceput' : 'Not started';
  };

  const getModuleUiStatus = (module) => {
    if (!module) return 'pending';
    if (module.available === false) return 'locked';
    const progress = Number(module.progress || 0);
    if (progress >= 100) return 'completed';
    if (progress > 0) return 'in_progress';
    return 'pending';
  };

  const academyModuleCards = useMemo(() => {
    if (!isLearningArea) return [];
    return filteredModulesData || [];
  }, [isLearningArea, filteredModulesData]);

  const selectedAcademyModule = useMemo(() => {
    if (!isLearningArea) return null;
    if (!academyModuleCards.length) return null;
    return academyModuleCards.find((m) => m.id === selectedModuleId) || academyModuleCards[0];
  }, [isLearningArea, academyModuleCards, selectedModuleId]);

  const selectedAcademyLesson = useMemo(() => {
    const lessons = selectedAcademyModule?.lessons || [];
    if (!lessons.length) return null;
    const found = lessons.find((l) => l.id === selectedLessonId);
    return found || lessons[0];
  }, [selectedAcademyModule, selectedLessonId]);

  const academyLessonSections = useMemo(() => {
    const lessons = selectedAcademyModule?.lessons || [];
    if (!lessons.length) return [];
    const size = 10;
    const sections = [];
    for (let i = 0; i < lessons.length; i += size) {
      const items = lessons.slice(i, i + size);
      sections.push({
        key: `${selectedAcademyModule.id}-${i + 1}-${i + items.length}`,
        title: language === 'ro' ? `Secțiunea ${sections.length + 1}` : `Section ${sections.length + 1}`,
        range:
          language === 'ro'
            ? `Lecții ${i + 1}-${i + items.length}`
            : `Lessons ${i + 1}-${i + items.length}`,
        items
      });
    }
    return sections;
  }, [selectedAcademyModule, language]);

  const selectedAcademyModuleMinutes = useMemo(() => {
    const total = (selectedAcademyModule?.lessons || []).reduce((sum, l) => sum + parseDurationMinutes(l.duration), 0);
    return total > 0 ? total : 0;
  }, [selectedAcademyModule]);

  const selectedAcademyModuleProgress = useMemo(
    () => Math.min(100, Math.max(0, Number(selectedAcademyModule?.progress || 0))),
    [selectedAcademyModule?.progress]
  );

  const isLessonStartable = useCallback((lesson) => {
    if (!lesson) return false;
    if (lesson.comingSoon) return false;
    if (lesson.planLocked) return false;
    if (lesson.available === false) return false;
    if (lesson.guestLocked) return false;
    return true;
  }, []);

  const continuePathLesson = useMemo(() => {
    const lessons = selectedAcademyModule?.lessons || [];
    if (!lessons.length) return null;
    return (
      lessons.find((l) => l.status === 'in_progress' && isLessonStartable(l)) ||
      lessons.find((l) => l.status !== 'completed' && isLessonStartable(l)) ||
      lessons.find((l) => isLessonStartable(l)) ||
      null
    );
  }, [selectedAcademyModule, isLessonStartable]);

  const dailyQuestTheoryLesson = useMemo(() => {
    const allLessons = (gatedModulesData || []).flatMap((m) => m.lessons || []);
    if (!allLessons.length) return null;
    return (
      allLessons.find((l) => l.status === 'in_progress' && isLessonStartable(l)) ||
      allLessons.find((l) => l.status !== 'completed' && isLessonStartable(l)) ||
      allLessons.find((l) => isLessonStartable(l)) ||
      null
    );
  }, [gatedModulesData, isLessonStartable]);

  const todayTrainingLesson = useMemo(() => {
    const lessons = selectedAcademyModule?.lessons || [];
    if (!lessons.length) return continuePathLesson || dailyQuestTheoryLesson || null;

    const alternate = lessons.find(
      (l) =>
        l.id !== continuePathLesson?.id &&
        l.status !== 'completed' &&
        isLessonStartable(l)
    );
    return alternate || continuePathLesson || dailyQuestTheoryLesson || null;
  }, [selectedAcademyModule, continuePathLesson, dailyQuestTheoryLesson, isLessonStartable]);

  const nextMilestone = useMemo(() => {
    const lessons = selectedAcademyModule?.lessons || [];
    if (!lessons.length) return null;

    const recap = lessons.find((l) => String(l.id || '').includes('recap'));
    const examStd = lessons.find((l) => String(l.id || '').includes('exam-standard'));
    const examCaptain = lessons.find((l) => String(l.id || '').includes('exam-captain'));
    const coreLessons = lessons.filter(
      (l) =>
        !String(l.id || '').includes('recap') &&
        !String(l.id || '').includes('exam-standard') &&
        !String(l.id || '').includes('exam-captain')
    );
    const coreCompletedCount = coreLessons.filter((l) => l.status === 'completed').length;
    const allCoreCompleted = coreLessons.length > 0 && coreCompletedCount === coreLessons.length;
    const nextRequiredCore = coreLessons.find((l) => l.status !== 'completed' && isLessonStartable(l)) || null;

    if (recap && recap.status !== 'completed') {
      if (!allCoreCompleted) {
        return {
          label: language === 'ro' ? 'Următorul milestone' : 'Next milestone',
          text:
            language === 'ro'
              ? `Recapitularea se deblochează după ce finalizezi toate lecțiile de conținut (${coreCompletedCount}/${coreLessons.length}).`
              : `Recap unlocks after all core lessons are completed (${coreCompletedCount}/${coreLessons.length}).`,
          lesson: nextRequiredCore,
          ctaLabel: language === 'ro' ? 'Continuă modulul' : 'Continue module'
        };
      }
      return {
        label: language === 'ro' ? 'Următorul milestone' : 'Next milestone',
        text: language === 'ro' ? 'Finalizează recapitularea Modulului 1.' : 'Complete Module 1 recap.',
        lesson: recap,
        ctaLabel: language === 'ro' ? 'Mergi la recap' : 'Go to recap'
      };
    }
    if (examStd && examStd.status !== 'completed') {
      return {
        label: language === 'ro' ? 'Următorul milestone' : 'Next milestone',
        text: language === 'ro' ? 'Promovează examenul standard.' : 'Pass the standard exam.',
        lesson: examStd,
        ctaLabel: language === 'ro' ? 'Mergi la examen' : 'Go to exam'
      };
    }
    if (examCaptain && examCaptain.status !== 'completed') {
      return {
        label: language === 'ro' ? 'Următorul milestone' : 'Next milestone',
        text: language === 'ro' ? 'Încearcă examenul Captain.' : 'Try the Captain exam.',
        lesson: examCaptain,
        ctaLabel: language === 'ro' ? 'Mergi la examen' : 'Go to exam'
      };
    }

    const nextCore = lessons.find((l) => l.status !== 'completed' && isLessonStartable(l));
    if (nextCore) {
      return {
        label: language === 'ro' ? 'Următorul milestone' : 'Next milestone',
        text: language === 'ro' ? 'Continuă lecțiile de conținut.' : 'Continue core content lessons.',
        lesson: nextCore,
        ctaLabel: language === 'ro' ? 'Mergi la modul' : 'Go to module'
      };
    }

    return {
      label: language === 'ro' ? 'Următorul milestone' : 'Next milestone',
      text: language === 'ro' ? 'Modul finalizat. Ești gata de următorul.' : 'Module completed. You are ready for the next one.',
      lesson: null,
      ctaLabel: language === 'ro' ? 'Finalizat' : 'Completed'
    };
  }, [selectedAcademyModule, language, isLessonStartable]);

  const handleStartAcademyLesson = useCallback((lesson, options = {}) => {
    if (!lesson) return;
    if (lesson.comingSoon) return;
    if (lesson.planLocked) {
      if (user?.id && typeof onOpenAccount === 'function') {
        onOpenAccount('billing');
      } else if (typeof onRequestLogin === 'function') {
        onRequestLogin();
      }
      return;
    }
    if (lesson.available === false || lesson.guestLocked) {
      if (!user?.id && onRequestLogin) onRequestLogin();
      return;
    }
    onStartLesson(lesson.id, {
      sourceArea: activeArea,
      trackProgress: options?.trackProgress !== false
    });
  }, [user?.id, onOpenAccount, onRequestLogin, onStartLesson, activeArea]);

  const selectedQuizDifficultyLabel = useMemo(() => {
    if (selectedQuizDifficulty === 'expert') return language === 'ro' ? 'Expert' : 'Expert';
    if (selectedQuizDifficulty === 'hard') return language === 'ro' ? 'Avansat' : 'Advanced';
    if (selectedQuizDifficulty === 'medium') return language === 'ro' ? 'Intermediar' : 'Intermediate';
    return language === 'ro' ? 'Începător' : 'Beginner';
  }, [selectedQuizDifficulty, language]);

  const quizDifficultyOptions = useMemo(
    () => [
      { id: 'easy', label: language === 'ro' ? 'Începător' : 'Beginner' },
      { id: 'medium', label: language === 'ro' ? 'Intermediar' : 'Intermediate' },
      { id: 'hard', label: language === 'ro' ? 'Avansat' : 'Advanced' },
      { id: 'expert', label: language === 'ro' ? 'Expert' : 'Expert' }
    ],
    [language]
  );

  const quizModeOptions = useMemo(
    () => [
      { id: 'practice', label: language === 'ro' ? 'Antrenament' : 'Practice' },
      { id: 'challenge', label: language === 'ro' ? 'Provocare' : 'Challenge' }
    ],
    [language]
  );

  const quizQuestionCountOptions = useMemo(() => [10, 20, 30], []);

  const quizTopics = useMemo(() => {
    const map = new Map();
    for (let i = 0; i < quizBankQuestions.length; i += 1) {
      const q = quizBankQuestions[i];
      const slug = String(q?.topicSlug || '').trim();
      if (!slug) continue;
      if (!map.has(slug)) {
        map.set(slug, String(q?.lessonTitle || slug));
      }
    }

    if (!map.has('history')) {
      map.set('history', language === 'ro' ? 'History' : 'History');
    }

    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => {
        if (a.id === 'history') return -1;
        if (b.id === 'history') return 1;
        return a.label.localeCompare(b.label, language === 'ro' ? 'ro' : 'en');
      });
  }, [quizBankQuestions, language]);

  const quizQuestionsByTopic = useMemo(() => {
    if (selectedQuizTopic === 'all') return quizBankQuestions;
    return quizBankQuestions.filter((q) => String(q?.topicSlug || '') === selectedQuizTopic);
  }, [quizBankQuestions, selectedQuizTopic]);

  useEffect(() => {
    if (selectedQuizTopic === 'all') return;
    const exists = quizTopics.some((t) => t.id === selectedQuizTopic);
    if (!exists) setSelectedQuizTopic('all');
  }, [selectedQuizTopic, quizTopics]);

  const quizDifficultyCountsByTopic = useMemo(() => {
    const counts = { easy: 0, medium: 0, hard: 0, expert: 0 };
    for (let i = 0; i < quizQuestionsByTopic.length; i += 1) {
      const difficulty = String(quizQuestionsByTopic[i]?.difficulty || 'easy');
      if (difficulty in counts) counts[difficulty] += 1;
    }
    return counts;
  }, [quizQuestionsByTopic]);

  const selectedQuizTopicLabel = useMemo(() => {
    if (selectedQuizTopic === 'all') return language === 'ro' ? 'Toate subiectele' : 'All topics';
    const found = quizTopics.find((t) => t.id === selectedQuizTopic);
    return found?.label || (language === 'ro' ? 'Subiect' : 'Topic');
  }, [selectedQuizTopic, quizTopics, language]);

  const currentQuizQuestion = useMemo(() => {
    if (!quizSessionQuestions.length) return null;
    return quizSessionQuestions[Math.min(quizSessionIndex, quizSessionQuestions.length - 1)] || null;
  }, [quizSessionQuestions, quizSessionIndex]);

  const currentQuizCorrectOption = useMemo(() => {
    if (!currentQuizQuestion) return null;
    const correctId = String(currentQuizQuestion.correctOptionId || '');
    const options = Array.isArray(currentQuizQuestion.options) ? currentQuizQuestion.options : [];
    return options.find((opt) => String(opt?.id || '') === correctId) || null;
  }, [currentQuizQuestion]);

  const selectedQuizAvailableCount = useMemo(
    () => Number(quizDifficultyCountsByTopic?.[selectedQuizDifficulty] || 0),
    [quizDifficultyCountsByTopic, selectedQuizDifficulty]
  );

  const plannedQuizQuestionCount = useMemo(
    () => Math.max(0, Math.min(selectedQuizAvailableCount, Number(quizSessionSize || 10))),
    [selectedQuizAvailableCount, quizSessionSize]
  );

  const quizSessionTotalTimeSec = useMemo(() => {
    if (Number(quizSessionSize) === 30) return 180;
    if (Number(quizSessionSize) === 20) return 120;
    return 60;
  }, [quizSessionSize]);

  const quizTimerLabel = useMemo(() => {
    const safe = Math.max(0, Number(quizSessionTimeLeftSec || 0));
    const mm = String(Math.floor(safe / 60)).padStart(2, '0');
    const ss = String(safe % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [quizSessionTimeLeftSec]);

  const quizTimerToneClass = useMemo(() => {
    if (quizSessionTimeLeftSec <= 15) return 'danger';
    if (quizSessionTimeLeftSec <= 30) return 'warning';
    return 'normal';
  }, [quizSessionTimeLeftSec]);

  const quizSessionLivesPreset = useMemo(
    () => (quizSessionMode === 'challenge' ? 1 : 3),
    [quizSessionMode]
  );

  const quizSessionMaxScore = useMemo(
    () => Math.max(0, plannedQuizQuestionCount * 10),
    [plannedQuizQuestionCount]
  );

  useEffect(() => {
    if (activeArea !== 'quizzes') return;
    if (selectedQuizAvailableCount > 0) return;
    const fallback = ['easy', 'medium', 'advanced', 'hard', 'expert'].find(
      (lvl) => Number(quizDifficultyCountsByTopic?.[lvl] || 0) > 0
    );
    if (fallback && fallback !== selectedQuizDifficulty) {
      setSelectedQuizDifficulty(fallback);
    }
  }, [activeArea, selectedQuizAvailableCount, quizDifficultyCountsByTopic, selectedQuizDifficulty]);

  const selectedPuzzleCategory = useMemo(() => {
    if (!puzzleCategories.length) return null;
    return (
      puzzleCategories.find((c) => c.id === selectedPuzzleCategoryId) ||
      puzzleCategories[0] ||
      null
    );
  }, [puzzleCategories, selectedPuzzleCategoryId]);

  const selectedPuzzleGroups = useMemo(() => {
    const list = selectedPuzzleCategory?.puzzles || [];
    const buckets = {
      easy: [],
      medium: [],
      advanced: [],
      hard: [],
      expert: []
    };

    for (let i = 0; i < list.length; i += 1) {
      const p = list[i];
      const rating = Number(p?.rating || 0);
      if (rating < 1000) buckets.easy.push(p);
      else if (rating < 1300) buckets.medium.push(p);
      else if (rating < 1600) buckets.advanced.push(p);
      else if (rating < 2000) buckets.hard.push(p);
      else buckets.expert.push(p);
    }

    const mk = (id, ro, en, minRating, maxRating, puzzles) => ({
      id,
      title: language === 'ro' ? ro : en,
      minRating,
      maxRating,
      puzzles: puzzles || [],
      count: (puzzles || []).length
    });

    return [
      mk('easy', 'Începător', 'Beginner', 0, 999, buckets.easy),
      mk('medium', 'Intermediar', 'Intermediate', 1000, 1299, buckets.medium),
      mk('advanced', 'Avansat', 'Advanced', 1300, 1599, buckets.advanced),
      mk('hard', 'Greu', 'Hard', 1600, 1999, buckets.hard),
      mk('expert', 'Expert', 'Expert', 2000, null, buckets.expert)
    ].filter((g) => g.count > 0);
  }, [selectedPuzzleCategory, language]);

  const selectedPuzzleGroup = useMemo(() => {
    if (!selectedPuzzleGroups.length) return null;
    return (
      selectedPuzzleGroups.find((g) => g.id === selectedPuzzleDifficultyId) ||
      selectedPuzzleGroups[0]
    );
  }, [selectedPuzzleGroups, selectedPuzzleDifficultyId]);

  const selectedPuzzleGroupsAll = useMemo(() => {
    const list = selectedPuzzleCategory?.puzzles || [];
    const buckets = {
      easy: [],
      medium: [],
      advanced: [],
      hard: [],
      expert: []
    };

    for (let i = 0; i < list.length; i += 1) {
      const p = list[i];
      const rating = Number(p?.rating || 0);
      if (rating < 1000) buckets.easy.push(p);
      else if (rating < 1300) buckets.medium.push(p);
      else if (rating < 1600) buckets.advanced.push(p);
      else if (rating < 2000) buckets.hard.push(p);
      else buckets.expert.push(p);
    }

    const mk = (id, ro, en, minRating, maxRating, puzzles) => ({
      id,
      title: language === 'ro' ? ro : en,
      minRating,
      maxRating,
      puzzles: puzzles || [],
      count: (puzzles || []).length
    });

    return [
      mk('easy', 'Începător', 'Beginner', 0, 999, buckets.easy),
      mk('medium', 'Intermediar', 'Intermediate', 1000, 1299, buckets.medium),
      mk('advanced', 'Avansat', 'Advanced', 1300, 1599, buckets.advanced),
      mk('hard', 'Greu', 'Hard', 1600, 1999, buckets.hard),
      mk('expert', 'Expert', 'Expert', 2000, null, buckets.expert)
    ];
  }, [selectedPuzzleCategory, language]);

  const getPuzzleGroupStatus = (categoryId, group) => {
    if (!group || group.count <= 0) return 'locked';
    const starts = Math.max(0, Number(puzzleStartsByGroup?.[`${categoryId}:${group.id}`] || 0));
    if (starts >= 5) return 'completed';
    if (starts > 0) return 'in_progress';
    if (selectedPuzzleCategory?.id === categoryId && selectedPuzzleDifficultyId === group.id) return 'in_progress';
    return 'pending';
  };

  const puzzleModuleCards = useMemo(() => {
    return (puzzleCategories || []).map((category) => {
      const groups = ['easy', 'medium', 'advanced', 'hard', 'expert'];
      const groupProgress = groups.map((gid) => {
        const starts = Math.min(5, Math.max(0, Number(puzzleStartsByGroup?.[`${category.id}:${gid}`] || 0)));
        return Math.round((starts / 5) * 100);
      });
      const progress = groupProgress.length
        ? Math.round(groupProgress.reduce((sum, val) => sum + val, 0) / groupProgress.length)
        : 0;
      const status = category.comingSoon
        ? 'locked'
        : progress >= 100
          ? 'completed'
          : progress > 0
            ? 'in_progress'
            : 'pending';
      const puzzleCount = Number((category.puzzles || []).length || 0);
      return {
        ...category,
        progress,
        status,
        lessonCountLabel:
          language === 'ro'
            ? `${puzzleCount} puzzle-uri`
            : `${puzzleCount} puzzles`,
        durationLabel: `~${Math.max(2, Math.min(40, puzzleCount * 2))} min`
      };
    });
  }, [puzzleCategories, puzzleStartsByGroup, language]);

  const selectedPuzzleModuleCard = useMemo(() => {
    if (!puzzleModuleCards.length) return null;
    return puzzleModuleCards.find((c) => c.id === selectedPuzzleCategory?.id) || puzzleModuleCards[0];
  }, [puzzleModuleCards, selectedPuzzleCategory?.id]);

  const puzzleGroupSections = useMemo(() => {
    const groups = selectedPuzzleGroupsAll || [];
    if (!groups.length || !selectedPuzzleCategory?.id) return [];
    const size = 4;
    const sections = [];
    for (let i = 0; i < groups.length; i += size) {
      const items = groups.slice(i, i + size);
      sections.push({
        key: `puzzle-${selectedPuzzleCategory.id}-${i + 1}-${i + items.length}`,
        title: language === 'ro' ? `Secțiunea ${sections.length + 1}` : `Section ${sections.length + 1}`,
        range:
          language === 'ro'
            ? `Grupe ${i + 1}-${i + items.length}`
            : `Groups ${i + 1}-${i + items.length}`,
        items
      });
    }
    return sections;
  }, [selectedPuzzleGroupsAll, selectedPuzzleCategory?.id, language]);

  const selectedPuzzleCategoryGuestStarts = Math.max(
    0,
    Number(guestPuzzleStartsByCategory?.[selectedPuzzleCategory?.id || ''] || 0)
  );
  const selectedPuzzleCategoryGuestRemaining = Math.max(
    0,
    guestPuzzleFreeLimitPerCategory - selectedPuzzleCategoryGuestStarts
  );

  const selectedPuzzleFromGroup = useMemo(() => {
    const list = selectedPuzzleGroup?.puzzles || [];
    if (!list.length) return null;
    const minR = Number(selectedPuzzleGroup?.minRating);
    const maxR = selectedPuzzleGroup?.maxRating == null ? null : Number(selectedPuzzleGroup?.maxRating);
    const inRange = list.find((p) => {
      const r = Number(p?.rating || 0);
      if (Number.isFinite(minR) && r < minR) return false;
      if (maxR != null && Number.isFinite(maxR) && r > maxR) return false;
      return true;
    });
    return inRange || list[0];
  }, [selectedPuzzleGroup]);
  const puzzlePopularityText = useMemo(() => {
    const p = Number(selectedPuzzleFromGroup?.popularity);
    if (!Number.isFinite(p)) return '';
    const normalized = Math.max(0, Math.min(100, p));
    const topPct = Math.max(1, Math.round(100 - normalized));
    return language === 'ro' ? `Top ${topPct}% apreciate` : `Top ${topPct}% liked`;
  }, [selectedPuzzleFromGroup?.popularity, language]);

  const dailyQuestPlan = useMemo(() => {
    const totalQuestions = 20;
    const maxMinutes = 10;
    const availablePuzzleCount = (puzzleCategories || []).reduce(
      (sum, category) => sum + Number((category?.puzzles || []).length || 0),
      0
    );
    const availableQuizCount = Array.isArray(quizBankQuestions) ? quizBankQuestions.length : 0;
    const recommendedDifficulty =
      Number(profile?.computedLevel || 1) >= 12
        ? (language === 'ro' ? 'Avansat' : 'Advanced')
        : Number(profile?.computedLevel || 1) >= 6
          ? (language === 'ro' ? 'Intermediar' : 'Intermediate')
          : (language === 'ro' ? 'Începător' : 'Beginner');
    const recommendedDifficultyId =
      Number(profile?.computedLevel || 1) >= 12
        ? 'hard'
        : Number(profile?.computedLevel || 1) >= 6
          ? 'medium'
          : 'easy';

    const puzzleTarget = Math.min(8, Math.max(0, availablePuzzleCount));
    const quizTarget = Math.min(8, Math.max(0, availableQuizCount));
    const theoryTarget = Math.max(0, totalQuestions - puzzleTarget - quizTarget);
    const hasAnyQuestContent = puzzleTarget + quizTarget + theoryTarget > 0;

    return {
      totalQuestions,
      maxMinutes,
      recommendedDifficulty,
      recommendedDifficultyId,
      puzzleTarget,
      quizTarget,
      theoryTarget,
      hasAnyQuestContent
    };
  }, [puzzleCategories, quizBankQuestions, profile?.computedLevel, language]);

  const puzzleDifficultyUi = useMemo(() => {
    const rating = Number(selectedPuzzleFromGroup?.rating || 0);
    if (rating >= 2000) {
      return {
        badgeClass: 'puzzle-badge-expert',
        icon: '♛',
        ratingClass: 'puzzle-rating-expert',
        label: language === 'ro' ? 'Expert' : 'Expert'
      };
    }
    if (rating >= 1600) {
      return {
        badgeClass: 'puzzle-badge-hard',
        icon: '♜',
        ratingClass: 'puzzle-rating-hard',
        label: language === 'ro' ? 'Greu' : 'Hard'
      };
    }
    if (rating >= 1300) {
      return {
        badgeClass: 'puzzle-badge-hard',
        icon: '♜',
        ratingClass: 'puzzle-rating-hard',
        label: language === 'ro' ? 'Avansat' : 'Advanced'
      };
    }
    if (rating >= 1000) {
      return {
        badgeClass: 'puzzle-badge-medium',
        icon: '♞',
        ratingClass: 'puzzle-rating-medium',
        label: language === 'ro' ? 'Intermediar' : 'Intermediate'
      };
    }
    return {
      badgeClass: 'puzzle-badge-easy',
      icon: '♟',
      ratingClass: 'puzzle-rating-easy',
      label: language === 'ro' ? 'Începător' : 'Beginner'
    };
  }, [selectedPuzzleFromGroup?.rating, language]);

  const puzzleProfileUi = useMemo(() => {
    const tier = getPuzzleTierFromRating(Number(puzzleSkillProfile?.puzzleRating || 500));
    if (tier === 'expert') {
      return {
        icon: '♛',
        badgeClass: 'puzzle-badge-expert',
        label: language === 'ro' ? 'Profil: Expert' : 'Profile: Expert'
      };
    }
    if (tier === 'hard') {
      return {
        icon: '♜',
        badgeClass: 'puzzle-badge-hard',
        label: language === 'ro' ? 'Profil: Greu' : 'Profile: Hard'
      };
    }
    if (tier === 'advanced') {
      return {
        icon: '♜',
        badgeClass: 'puzzle-badge-hard',
        label: language === 'ro' ? 'Profil: Avansat' : 'Profile: Advanced'
      };
    }
    if (tier === 'medium') {
      return {
        icon: '♞',
        badgeClass: 'puzzle-badge-medium',
        label: language === 'ro' ? 'Profil: Intermediar' : 'Profile: Intermediate'
      };
    }
    return {
      icon: '♟',
      badgeClass: 'puzzle-badge-easy',
      label: language === 'ro' ? 'Profil: Începător' : 'Profile: Beginner'
    };
  }, [puzzleSkillProfile?.puzzleRating, language]);

  useEffect(() => {
    if (!isLearningArea) return;
    if (!selectedLesson?.id) return;
    if (!scrollToLessonActions) return;
    if (typeof window === 'undefined') return;
    if (window.innerWidth > 768) return;

    const t = window.setTimeout(() => {
      const actionsEl = document.getElementById(`lesson-actions-${selectedLesson.id}`);
      const cardEl = document.getElementById(`lesson-${selectedLesson.id}`);
      const target = actionsEl || cardEl;
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      setScrollToLessonActions(false);
    }, 140);

    return () => window.clearTimeout(t);
  }, [isLearningArea, selectedLesson?.id, scrollToLessonActions]);

  const lessonMapState = (module, lesson) => {
    if (!module || !lesson) return 'available';
    if (module.available === false || lesson.comingSoon) return 'coming';
    if (lesson.status === 'completed') return 'done';
    if (lesson.status === 'in_progress') return 'progress';
    return 'available';
  };

  const goToBilling = useCallback(() => {
    if (user?.id && typeof onOpenAccount === 'function') {
      onOpenAccount('billing');
      return;
    }
    if (typeof onRequestLogin === 'function') onRequestLogin();
  }, [user?.id, onOpenAccount, onRequestLogin]);

  const areaMenu = [
    { id: 'academy', label: language === 'ro' ? 'Academy' : 'Academy', icon: '♟' },
    { id: 'dailyquest', label: 'Daily Quest', icon: '⚡' },
    { id: 'puzzles', label: language === 'ro' ? 'Puzzle-uri' : 'Puzzles', icon: '🧩' },
    { id: 'openings', label: language === 'ro' ? 'Deschideri' : 'Openings', icon: '📘' },
    { id: 'quizzes', label: 'Quiz Hub', icon: '📝' },
    { id: 'mindhub', label: language === 'ro' ? 'Mind Hub' : 'Mind Hub', icon: '🧠' },
    { id: 'gamelab', label: language === 'ro' ? 'GameLab' : 'GameLab', icon: '🎬' },
    { id: 'playground', label: language === 'ro' ? 'Chessy AI' : 'Chessy AI', icon: '♛' },
    { id: 'community', label: language === 'ro' ? 'Community' : 'Community', icon: '◍' }
  ];

  const areaContent = useMemo(() => {
    if (activeArea === 'dailyquest') {
      return {
        title: language === 'ro' ? 'Daily Quest' : 'Daily Quest',
        subtitle:
          language === 'ro'
            ? 'Misiunea zilnică: 10 minute de focus, 20 de provocări mixte.'
            : 'Daily mission: 10 minutes of focus, 20 mixed challenges.',
        bio:
          language === 'ro'
            ? 'Combinăm exerciții, puzzle-uri, quiz-uri și întrebări de teorie pe nivelul tău, apoi închidem cu o partidă rapidă contra Chessy AI.'
            : 'We combine exercises, puzzles, quizzes, and theory prompts at your level, then close with a quick game against Chessy AI.'
      };
    }
    if (activeArea === 'puzzles') {
      return {
        title: language === 'ro' ? 'Puzzle-uri Tactice' : 'Tactical Puzzles',
        subtitle:
          language === 'ro'
            ? 'Intră în rolul de strateg: vezi pattern-ul, calculează clar și finalizează ca un campion.'
            : 'Step into the strategist role: spot patterns, calculate clearly, and finish like a champion.',
        bio:
          language === 'ro'
            ? 'Alege-ți misiunea, analizează poziția și joacă mutarea decisivă. Fiecare exercițiu îți antrenează intuiția, disciplina și viteza de decizie.'
            : 'Choose your mission, analyze the position, and play the decisive move. Each exercise trains your intuition, discipline, and decision speed.'
      };
    }
    if (activeArea === 'quizzes') {
      return {
        title: language === 'ro' ? 'Quiz-uri de Șah' : 'Chess Quizzes',
        subtitle:
          language === 'ro'
            ? 'Teste rapide pe subiecte: reguli, tactică și decizie sub presiune.'
            : 'Fast topic-based tests: rules, tactics, and decision-making under pressure.',
        bio:
          language === 'ro'
            ? 'Alege un subiect, pornește sesiunea și răspunde la întrebări independente de lecții. Primești scor, acuratețe și progres instant.'
            : 'Choose a topic, start a session, and answer questions that are independent from lessons. Get instant score, accuracy, and progress.'
      };
    }
    if (activeArea === 'openings') {
      return {
        title: language === 'ro' ? 'Openings Lab' : 'Openings Lab',
        subtitle:
          language === 'ro'
            ? 'Repertoriu și planuri de joc: învață ideile de bază, nu doar mutările.'
            : 'Repertoire and game plans: learn core ideas, not just moves.',
        bio:
          language === 'ro'
            ? 'În zona de deschideri vom construi trasee clare pe linii populare, cu planuri de mijloc de joc, capcane frecvente și exerciții scurte de consolidare.'
            : 'In the openings area we will build clear paths through popular lines, with middlegame plans, common traps, and short reinforcement drills.'
      };
    }
    if (activeArea === 'mindhub') {
      return {
        title: language === 'ro' ? 'Mind Hub' : 'Mind Hub',
        subtitle:
          language === 'ro'
            ? 'Centru de mindset pentru șah: focus, reziliență, gestionarea emoțiilor și psihologia jocului.'
            : 'A mindset center for chess: focus, resilience, emotional control, and game psychology.',
        bio:
          language === 'ro'
            ? 'Aici Chessy îți arată cum să rămâi calm, să gândești clar sub presiune și să revii după greșeli.'
          : 'Here, Chessy helps you stay calm, think clearly under pressure, and recover after mistakes.'
      };
    }
    if (activeArea === 'gamelab') {
      return {
        title: language === 'ro' ? 'GameLab' : 'GameLab',
        subtitle:
          language === 'ro'
            ? 'Partide celebre, analizate pe poziții-cheie cu tablă interactivă.'
            : 'Famous games analyzed through key positions with an interactive board.',
        bio:
          language === 'ro'
            ? 'În GameLab transformăm partidele mari în lecții clare: ce plan alegi, ce e critic, ce e capcană.'
            : 'In GameLab, we turn great games into clear lessons: which plan to choose, what is critical, and what is a trap.'
      };
    }
    if (activeArea === 'playground') {
      return {
        title: language === 'ro' ? 'Chessy AI Playground' : 'Chessy AI Playground',
        subtitle:
          language === 'ro'
            ? 'Joacă practic contra botului nostru, calibrat pe nivelul tău actual.'
            : 'Play practical games against our bot, calibrated to your current level.',
        bio:
          language === 'ro'
            ? 'Playground-ul transformă puzzle-urile în antrenament real: decizie, ritm și consolidare pe poziții complete.'
            : 'The playground turns puzzle work into real practice: decision speed, rhythm, and full-position consolidation.'
      };
    }
    if (activeArea === 'community') {
      return {
        title: language === 'ro' ? 'Community' : 'Community',
        subtitle:
          language === 'ro'
            ? 'Spațiul de comunitate: discuții, întrebări, provocări și turnee pentru învățare împreună.'
            : 'Your community space: discussions, questions, challenges, and tournaments for peer learning.',
        bio:
          language === 'ro'
            ? 'În comunitate înveți mai repede: feedback de la alți jucători, sesiuni Q&A și mini-competiții.'
            : 'In community you improve faster: feedback from peers, Q&A sessions, and mini competitions.'
      };
    }
    return {
      title:
        language === 'ro'
          ? 'Academia ChessMindset: Arhitectul Gândirii Tale'
          : 'ChessMindset Academy: The Architect of Your Thinking',
      subtitle:
        language === 'ro'
          ? 'Nu doar mutăm piese. Construim strategii care câștigă pe tablă și în viață.'
          : 'We do not just move pieces. We build strategies that win on the board and in life.',
      bio:
        language === 'ro'
          ? 'Dincolo de tabla de joc, șahul este antrenamentul suprem pentru minte. Academia noastră îți oferă un traseu ghidat, unde fiecare lecție interactivă și misiune de mindset te transformă dintr-un simplu jucător într-un strateg veritabil. Cu feedback instantaneu, teste de intuiție și progres salvat în timp real, treci de la bazele jocului la performanță printr-o experiență modernă, adaptată ritmului tău. Ești gata să faci prima mutare spre o minte de campion?'
          : 'Beyond the board, chess is the ultimate training for the mind. Our Academy gives you a guided path where every interactive lesson and mindset mission transforms you from a simple player into a true strategist. With instant feedback, intuition tests, and real-time saved progress, you move from fundamentals to performance through a modern experience adapted to your pace. Are you ready to make your first move toward a champion\'s mind?'
    };
  }, [activeArea, language]);

  const teaserModules = useMemo(() => {
    if (activeArea === 'puzzles') {
      return [
        {
          id: 'pz-1',
          title: language === 'ro' ? 'Puzzle-uri cu pioni' : 'Pawn Puzzles',
          summary:
            language === 'ro'
              ? 'Structuri de pioni, pioni trecuți, promovare și curse de pioni.'
              : 'Pawn structures, passed pawns, promotion, and pawn races.'
        },
        {
          id: 'pz-2',
          title: language === 'ro' ? 'Puzzle-uri cu turnuri' : 'Rook Puzzles',
          summary:
            language === 'ro'
              ? 'Activarea turei, coloane deschise, mat pe ultima linie.'
              : 'Rook activity, open files, and back-rank motifs.'
        },
        {
          id: 'pz-3',
          title: language === 'ro' ? 'Rege blocat' : 'Trapped King',
          summary:
            language === 'ro'
              ? 'Îngrădirea regelui, pătrate de evadare și finalizare tactică.'
              : 'King restriction, escape squares, and tactical conversion.'
        },
        {
          id: 'pz-4',
          title: language === 'ro' ? 'Mat în 1' : 'Mate in 1',
          summary:
            language === 'ro'
              ? 'Pattern-uri de mat instant pentru viteză și încredere.'
              : 'Instant mate patterns for speed and confidence.'
        },
        {
          id: 'pz-5',
          title: language === 'ro' ? 'Mat în 2' : 'Mate in 2',
          summary:
            language === 'ro'
              ? 'Forțare în două mutări, coordonare de piese.'
              : 'Two-move forcing lines and piece coordination.'
        },
        {
          id: 'pz-6',
          title: language === 'ro' ? 'Furculițe' : 'Forks',
          summary:
            language === 'ro'
              ? 'Atac dublu cu cal, pion sau damă pe ținte multiple.'
              : 'Double attacks with knight, pawn, or queen.'
        },
        {
          id: 'pz-7',
          title: language === 'ro' ? 'Țintuiri' : 'Pins',
          summary:
            language === 'ro'
              ? 'Imobilizarea pieselor și câștig de material.'
              : 'Piece immobilization and material gain.'
        },
        {
          id: 'pz-8',
          title: language === 'ro' ? 'Skewers' : 'Skewers',
          summary:
            language === 'ro'
              ? 'Atac pe linie: piesă valoroasă forțată să se mute.'
              : 'Line attack forcing a high-value piece away.'
        },
        {
          id: 'pz-9',
          title: language === 'ro' ? 'Atac descoperit' : 'Discovered Attacks',
          summary:
            language === 'ro'
              ? 'Ridici o piesă și descoperi forța din spate.'
              : 'Move one piece and reveal a hidden attack.'
        },
        {
          id: 'pz-10',
          title: language === 'ro' ? 'Dublu șah' : 'Double Check',
          summary:
            language === 'ro'
              ? 'Motiv tactic forțant, cu opțiuni limitate de apărare.'
              : 'A forcing tactical motif with limited defenses.'
        },
        {
          id: 'pz-11',
          title: language === 'ro' ? 'Piese neprotejate' : 'Hanging Pieces',
          summary:
            language === 'ro'
              ? 'Identifică piesa fără apărare și exploatează imediat.'
              : 'Spot undefended pieces and punish quickly.'
        },
        {
          id: 'pz-12',
          title: language === 'ro' ? 'Finaluri de pioni și ture' : 'Pawn & Rook Endgames',
          summary:
            language === 'ro'
              ? 'Tehnică de final: opoziție, activitate de rege, turn activ.'
              : 'Endgame technique: opposition, king activity, active rook.'
        }
      ];
    }
    if (activeArea === 'mindhub') {
      return [
        {
          id: 'mh-1',
          title: language === 'ro' ? 'Mindset de competiție' : 'Competitive Mindset',
          summary:
            language === 'ro'
              ? 'Rutine mentale înainte de partidă, respirație și intrare în flow.'
              : 'Pre-game mental routines, breathing, and flow-state entry.'
        },
        {
          id: 'mh-2',
          title: language === 'ro' ? 'Anti-tilt și recuperare' : 'Anti-Tilt Recovery',
          summary:
            language === 'ro'
              ? 'Cum revii rapid după o greșeală și continui cu claritate.'
              : 'How to recover quickly after a mistake and continue with clarity.'
        },
        {
          id: 'mh-3',
          title: language === 'ro' ? 'Focus pe termen lung' : 'Long-Term Focus',
          summary:
            language === 'ro'
              ? 'Construirea disciplinei și a progresului constant, zi de zi.'
              : 'Building discipline and steady progress, day by day.'
        }
      ];
    }
    if (activeArea === 'gamelab') {
      return [
        {
          id: 'gl-1',
          title: language === 'ro' ? "Queen's Gambit · Blueprint" : "Queen's Gambit · Blueprint",
          summary:
            language === 'ro'
              ? 'Pozițiile care explică de ce centrul și timing-ul schimburilor contează.'
              : 'Positions showing why center control and exchange timing matter.'
        },
        {
          id: 'gl-2',
          title: language === 'ro' ? 'Meciuri clasice explicate' : 'Classic games explained',
          summary:
            language === 'ro'
              ? 'Vom adăuga analize de Campionat Mondial și partide model, în format interactiv.'
              : 'World Championship model games will be added in an interactive format.'
        }
      ];
    }
    if (activeArea === 'quizzes') {
      return [
        {
          id: 'qz-1',
          title: language === 'ro' ? 'Reguli de bază' : 'Core Rules',
          summary:
            language === 'ro'
              ? 'Întrebări despre reguli esențiale: rocadă, en passant, promovare.'
              : 'Questions on essential rules: castling, en passant, promotion.'
        },
        {
          id: 'qz-2',
          title: language === 'ro' ? 'Tactică' : 'Tactics',
          summary:
            language === 'ro'
              ? 'Pattern-uri, calcul scurt și identificarea mutării decisive.'
              : 'Patterns, short calculation, and spotting the decisive move.'
        },
        {
          id: 'qz-3',
          title: language === 'ro' ? 'Finaluri' : 'Endgames',
          summary:
            language === 'ro'
              ? 'Decizii în finaluri simple: pion, turn, activitatea regelui.'
              : 'Simple endgame decisions: pawn, rook, and king activity.'
        },
        {
          id: 'qz-4',
          title: language === 'ro' ? 'Mindset competitiv' : 'Competitive Mindset',
          summary:
            language === 'ro'
              ? 'Situații de presiune, alegerea planului și control emoțional.'
              : 'Pressure situations, plan selection, and emotional control.'
        }
      ];
    }
    if (activeArea === 'openings') {
      return [
        {
          id: 'op-1',
          title: language === 'ro' ? 'Italian Game (Giuoco Piano)' : 'Italian Game (Giuoco Piano)',
          summary:
            language === 'ro'
              ? 'Deschidere clasică pentru începători: dezvoltare naturală, control central și planuri simple de atac.'
              : 'A beginner-friendly classic: natural development, center control, and clear attacking plans.'
        },
        {
          id: 'op-2',
          title: language === 'ro' ? "Queen's Gambit" : "Queen's Gambit",
          summary:
            language === 'ro'
              ? 'Model strategic pentru structură de pioni și inițiativă pozițională în centru.'
              : 'A strategic model for pawn structure and positional initiative in the center.'
        }
      ];
    }
    if (activeArea === 'community') {
      return [
        {
          id: 'cm-1',
          title: language === 'ro' ? 'Forum de întrebări' : 'Q&A Forum',
          summary:
            language === 'ro'
              ? 'Întreabă, răspunde și învață din poziții reale.'
              : 'Ask, answer, and learn from real board positions.'
        },
        {
          id: 'cm-2',
          title: language === 'ro' ? 'Sesiuni live Q&A' : 'Live Q&A Sessions',
          summary:
            language === 'ro'
              ? 'Întâlniri periodice cu explicații practice și feedback.'
              : 'Regular sessions with practical explanations and feedback.'
        },
        {
          id: 'cm-3',
          title: language === 'ro' ? 'Turnee interne' : 'Community Tournaments',
          summary:
            language === 'ro'
              ? 'Mini-turnee tematice pentru ritm, strategie și experiență.'
              : 'Themed mini tournaments for pace, strategy, and experience.'
        }
      ];
    }
    if (activeArea === 'playground') {
      return [
        {
          id: 'pg-1',
          title: language === 'ro' ? 'Nivel adaptiv' : 'Adaptive level',
          summary:
            language === 'ro'
              ? 'Începi de la nivelul tău și urci gradual în complexitate.'
              : 'Start from your level and scale complexity progressively.'
        },
        {
          id: 'pg-2',
          title: language === 'ro' ? 'Antrenament practic' : 'Practical training',
          summary:
            language === 'ro'
              ? 'Mutări complete pe tablă, nu doar soluții punctuale de puzzle.'
              : 'Full-board play, not only isolated puzzle solutions.'
        },
        {
          id: 'pg-3',
          title: language === 'ro' ? 'Profil Chessy AI' : 'Chessy AI profile',
          summary:
            language === 'ro'
              ? 'Bot cu stil propriu, orientat pe învățare și progres.'
              : 'A personality-driven bot focused on learning and progression.'
        }
      ];
    }
    return [];
  }, [activeArea, language]);

  const quizProgressPct = quizSessionQuestions.length
    ? Math.round((quizSessionAnsweredCount / quizSessionQuestions.length) * 100)
    : 0;

  const quizAccuracyPct = quizSessionAnsweredCount
    ? Math.round((quizSessionCorrectCount / quizSessionAnsweredCount) * 100)
    : 0;

  const quizRankLabel = useMemo(() => {
    if (quizSessionScore >= 90) return language === 'ro' ? 'Maestru' : 'Master';
    if (quizSessionScore >= 70) return language === 'ro' ? 'Strateg' : 'Strategist';
    if (quizSessionScore >= 40) return language === 'ro' ? 'Ucenic' : 'Apprentice';
    return language === 'ro' ? 'Începător' : 'Beginner';
  }, [quizSessionScore, language]);

  const startQuizSession = () => {
    const session = buildQuizSession(quizQuestionsByTopic, selectedQuizDifficulty, quizSessionSize);
    if (!session.length) return;
    setQuizSessionQuestions(session);
    setQuizSessionStarted(true);
    setQuizSessionCompleted(false);
    setQuizSessionIndex(0);
    setQuizSessionScore(0);
    setQuizSessionLives(quizSessionLivesPreset);
    setQuizSessionTimeLeftSec(quizSessionTotalTimeSec);
    setQuizSessionAnsweredCount(0);
    setQuizSessionCorrectCount(0);
    setQuizSelectedOptionId('');
    setQuizChecked(false);
  };

  const handleQuizPick = (optionId) => {
    if (!currentQuizQuestion || quizChecked || quizSessionCompleted || quizSessionTimeLeftSec <= 0) return;
    const picked = String(optionId || '');
    const correctId = String(currentQuizQuestion.correctOptionId || '');
    const isCorrect = picked === correctId;
    setQuizSelectedOptionId(picked);
    setQuizChecked(true);
    setQuizSessionAnsweredCount((prev) => prev + 1);
    if (isCorrect) {
      setQuizSessionCorrectCount((prev) => prev + 1);
      setQuizSessionScore((prev) => prev + 10);
    } else {
      setQuizSessionLives((prev) => Math.max(0, prev - 1));
    }
  };

  const goNextQuizQuestion = () => {
    if (!quizChecked) return;
    const outOfLives = quizSessionLives <= 0;
    const isLast = quizSessionIndex >= quizSessionQuestions.length - 1;
    if (outOfLives || isLast) {
      setQuizSessionCompleted(true);
      return;
    }
    setQuizSessionIndex((prev) => prev + 1);
    setQuizSelectedOptionId('');
    setQuizChecked(false);
  };

  useEffect(() => {
    if (!quizSessionStarted || quizSessionCompleted) return undefined;
    const timerId = window.setInterval(() => {
      setQuizSessionTimeLeftSec((prev) => Math.max(0, Number(prev || 0) - 1));
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [quizSessionStarted, quizSessionCompleted]);

  useEffect(() => {
    if (!quizSessionStarted || quizSessionCompleted) return;
    if (quizSessionTimeLeftSec > 0) return;
    setQuizSessionCompleted(true);
  }, [quizSessionTimeLeftSec, quizSessionStarted, quizSessionCompleted]);

  useEffect(() => {
    if (!dailyQuestStarted || dailyQuestCompleted) return undefined;
    const timerId = window.setInterval(() => {
      setDailyQuestElapsedSec((prev) => Math.max(0, Number(prev || 0) + 1));
    }, 1000);
    return () => window.clearInterval(timerId);
  }, [dailyQuestStarted, dailyQuestCompleted]);

  const dailyQuestTimerLabel = useMemo(() => {
    const safe = Math.max(0, Number(dailyQuestElapsedSec || 0));
    const mm = String(Math.floor(safe / 60)).padStart(2, '0');
    const ss = String(safe % 60).padStart(2, '0');
    return `${mm}:${ss}`;
  }, [dailyQuestElapsedSec]);

  const dailyQuestSteps = useMemo(
    () => [
      {
        id: 'theory',
        title: language === 'ro' ? 'Încălzire teorie' : 'Theory warm-up',
        ready: Boolean(dailyQuestTheoryLesson)
      },
      {
        id: 'puzzle',
        title: language === 'ro' ? 'Puzzle sprint' : 'Puzzle sprint',
        ready: Boolean(selectedPuzzleFromGroup)
      },
      {
        id: 'quiz',
        title: language === 'ro' ? 'Quiz focus' : 'Quiz focus',
        ready: Array.isArray(quizBankQuestions) && quizBankQuestions.length > 0
      },
      {
        id: 'final',
        title: language === 'ro' ? 'Final boss: Chessy AI' : 'Final boss: Chessy AI',
        ready: true
      }
    ],
    [language, dailyQuestTheoryLesson, selectedPuzzleFromGroup, quizBankQuestions]
  );

  const runDailyQuestStep = useCallback((index) => {
    const step = dailyQuestSteps[index];
    if (!step) return;

    if (step.id === 'theory') {
      if (dailyQuestTheoryLesson) {
        handleStartAcademyLesson(dailyQuestTheoryLesson, { trackProgress: false });
      } else {
        setActiveArea('academy');
      }
      return;
    }

    if (step.id === 'puzzle') {
      if (selectedPuzzleFromGroup) {
        onStartLesson(selectedPuzzleFromGroup.id, {
          sourceArea: 'puzzles',
          trackProgress: false,
          puzzleDiagramNo: 1,
          puzzleTheme: selectedPuzzleCategory?.id || '',
          puzzleDifficultyId: selectedPuzzleGroup?.id || '',
          minRating: selectedPuzzleGroup?.minRating ?? null,
          maxRating: selectedPuzzleGroup?.maxRating ?? null
        });
      } else {
        setActiveArea('puzzles');
      }
      return;
    }

    if (step.id === 'quiz') {
      const questDifficulty = String(dailyQuestPlan?.recommendedDifficultyId || 'easy');
      const session = buildQuizSession(quizBankQuestions, questDifficulty, 20);
      setActiveArea('quizzes');
      setSelectedQuizTopic('all');
      setSelectedQuizDifficulty(questDifficulty);
      setQuizSessionMode('challenge');
      setQuizSessionSize(20);
      if (session.length) {
        setQuizSessionQuestions(session);
        setQuizSessionStarted(true);
        setQuizSessionCompleted(false);
        setQuizSessionIndex(0);
        setQuizSessionScore(0);
        setQuizSessionLives(1);
        setQuizSessionTimeLeftSec(120);
        setQuizSessionAnsweredCount(0);
        setQuizSessionCorrectCount(0);
        setQuizSelectedOptionId('');
        setQuizChecked(false);
      }
      return;
    }

    if (step.id === 'final') {
      setActiveArea('playground');
      setDailyQuestStarted(false);
      setDailyQuestCompleted(true);
    }
  }, [
    dailyQuestSteps,
    dailyQuestTheoryLesson,
    handleStartAcademyLesson,
    selectedPuzzleFromGroup,
    onStartLesson,
    selectedPuzzleCategory?.id,
    selectedPuzzleGroup?.id,
    selectedPuzzleGroup?.minRating,
    selectedPuzzleGroup?.maxRating,
    dailyQuestPlan?.recommendedDifficultyId,
    quizBankQuestions
  ]);

  const handleStartDailyQuest = useCallback(() => {
    setDailyQuestElapsedSec(0);
    setDailyQuestCompleted(false);
    setDailyQuestStarted(true);
    setDailyQuestStepIndex(0);
    runDailyQuestStep(0);
  }, [runDailyQuestStep]);

  const handleContinueDailyQuest = useCallback(() => {
    if (!dailyQuestStarted && !dailyQuestCompleted) {
      handleStartDailyQuest();
      return;
    }
    if (dailyQuestCompleted) {
      handleStartDailyQuest();
      return;
    }
    const nextIndex = Math.min(dailyQuestSteps.length - 1, Number(dailyQuestStepIndex || 0) + 1);
    setDailyQuestStepIndex(nextIndex);
    runDailyQuestStep(nextIndex);
  }, [dailyQuestStarted, dailyQuestCompleted, handleStartDailyQuest, dailyQuestSteps.length, dailyQuestStepIndex, runDailyQuestStep]);

  const handleOpenMindhubArticle = useCallback((articleId) => {
    setMindhubSelectedArticleId(articleId);
    setMindhubIsReading(true);
    setMindhubQuizAnswers({});
    setMindhubReadingProgress(0);
    setMindhubMomentsOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleBackToMindhubList = useCallback(() => {
    setMindhubIsReading(false);
    setMindhubReadingProgress(0);
    setMindhubQuizAnswers({});
    setMindhubMomentsOpen(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleOpenMindhubSpotify = useCallback(() => {
    if (!selectedMindhubArticle?.spotifyEpisodeUrl) return;
    try {
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'mindhub_spotify_open', {
          article_slug: selectedMindhubArticle.slug || 'unknown'
        });
      }
    } catch {
      // no-op analytics fallback
    }
    window.open(selectedMindhubArticle.spotifyEpisodeUrl, '_blank', 'noopener,noreferrer');
  }, [selectedMindhubArticle]);

  const getMindhubArticleUrl = useCallback((article) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}/#/mindhub/${article.slug}`;
  }, []);

  const handleMindhubShare = useCallback(async (platform) => {
    if (!selectedMindhubArticle) return;
    const url = getMindhubArticleUrl(selectedMindhubArticle);
    const title = selectedMindhubArticle.title;
    const text = selectedMindhubArticle.subtitle;
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    const encodedText = encodeURIComponent(text);

    const pop = (targetUrl) => window.open(targetUrl, '_blank', 'noopener,noreferrer');
    const copyToClipboard = async () => {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return true;
      }
      return false;
    };

    if (platform === 'native' && navigator?.share) {
      try {
        await navigator.share({ title, text, url });
      } catch {
        // no-op
      }
      return;
    }

    if (platform === 'facebook') return pop(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`);
    if (platform === 'linkedin') return pop(`https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodedTitle}`);
    if (platform === 'threads') return pop(`https://www.threads.net/intent/post?text=${encodeURIComponent(`${title} ${url}`)}`);
    if (platform === 'instagram' || platform === 'tiktok') {
      const copied = await copyToClipboard();
      if (!copied) return;
      window.alert(language === 'ro' ? 'Link copiat. Deschide aplicația și publică postarea.' : 'Link copied. Open the app and publish your post.');
      return;
    }

    if (platform === 'copy') {
      const copied = await copyToClipboard();
      if (copied) window.alert(language === 'ro' ? 'Link copiat.' : 'Link copied.');
      return;
    }

    return pop(`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`);
  }, [selectedMindhubArticle, getMindhubArticleUrl, language]);

  const renderMindhubShareIcon = useCallback((platform) => {
    if (platform === 'native') {
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M14 3h7v7h-2V6.41l-8.29 8.3-1.42-1.42 8.3-8.29H14V3z" />
          <path fill="currentColor" d="M5 5h6v2H7v10h10v-4h2v6H5V5z" />
        </svg>
      );
    }
    if (platform === 'facebook') {
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M22 12.07C22 6.5 17.52 2 12 2S2 6.5 2 12.07c0 5.03 3.66 9.2 8.44 9.93v-7.03H7.9v-2.9h2.54V9.85c0-2.52 1.49-3.91 3.78-3.91 1.09 0 2.23.2 2.23.2v2.48h-1.26c-1.24 0-1.62.77-1.62 1.56v1.88h2.77l-.44 2.9h-2.33V22c4.78-.73 8.44-4.9 8.44-9.93z" />
        </svg>
      );
    }
    if (platform === 'linkedin') {
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M6.94 8.5a1.72 1.72 0 1 1 0-3.44 1.72 1.72 0 0 1 0 3.44zM5.4 19.5h3.08v-9.3H5.4v9.3zM10.38 10.2h2.95v1.27h.04c.41-.78 1.42-1.6 2.92-1.6 3.12 0 3.7 2.05 3.7 4.73v4.9h-3.08v-4.35c0-1.04-.02-2.37-1.45-2.37-1.45 0-1.67 1.13-1.67 2.3v4.42h-3.41v-9.3z" />
        </svg>
      );
    }
    if (platform === 'threads') {
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M15.85 11.26c-.17-2.3-1.52-3.58-3.8-3.58-2.38 0-4.12 1.56-4.12 3.7 0 2.28 1.76 3.8 4.39 3.8 1.07 0 1.98-.27 2.74-.8-.2 1.44-1.26 2.27-2.93 2.27-1.2 0-2.18-.45-3.02-1.38l-1.64 1.16c1.08 1.4 2.72 2.13 4.71 2.13 3.38 0 5.59-2 5.59-5.07 0-2.1-.83-3.4-2.79-4.23-.13-.06-.28-.13-.43-.2zm-3.52 2.22c-1.32 0-2.19-.76-2.19-1.9 0-1.1.84-1.84 2.09-1.84 1.2 0 1.9.67 2.04 1.95-.54 1.2-1.16 1.8-1.94 1.8zM12 2.1C6.53 2.1 2.1 6.53 2.1 12S6.53 21.9 12 21.9 21.9 17.47 21.9 12 17.47 2.1 12 2.1z" />
        </svg>
      );
    }
    if (platform === 'instagram') {
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9a5.5 5.5 0 0 1-5.5 5.5h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2zm0 2A3.5 3.5 0 0 0 4 7.5v9A3.5 3.5 0 0 0 7.5 20h9a3.5 3.5 0 0 0 3.5-3.5v-9A3.5 3.5 0 0 0 16.5 4h-9zm9.75 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
        </svg>
      );
    }
    if (platform === 'tiktok') {
      return (
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
          <path fill="currentColor" d="M14.5 3h2.13c.21 1.18.92 2.21 1.94 2.83.59.36 1.25.55 1.93.56V8.6a7.12 7.12 0 0 1-4-1.21v6.28a5.66 5.66 0 1 1-4.88-5.61v2.24a3.42 3.42 0 1 0 2.88 3.37V3z" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path fill="currentColor" d="M16 1H4C2.9 1 2 1.9 2 3v12h2V3h12V1z" />
        <path fill="currentColor" d="M20 5H8C6.9 5 6 5.9 6 7v14c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h12v14z" />
      </svg>
    );
  }, []);

  const handleMindhubPickAnswer = useCallback((questionId, optionIndex) => {
    setMindhubQuizAnswers((prev) => ({
      ...prev,
      [questionId]: optionIndex
    }));
  }, []);

  const handleGamelabPickAnswer = useCallback((questionId, optionIndex) => {
    if (!selectedGamelabArticle?.id) return;
    setGamelabQuizAnswers((prev) => ({
      ...prev,
      [selectedGamelabArticle.id]: {
        ...(prev[selectedGamelabArticle.id] || {}),
        [questionId]: optionIndex
      }
    }));
  }, [selectedGamelabArticle]);

  const shouldShowLevelHeader = !(activeArea === 'mindhub' && isMindhubReadingMode);

  return (
    <div className={`academy-home ${isMindhubReadingMode ? 'mindhub-reading-page' : ''}`}>
      <div className="academy-header">
        <button
          className="btn-back-home"
          onClick={() => window.location.reload()}
        >
          ← {language === 'ro' ? 'Înapoi la pagina principală' : 'Back to Home'}
        </button>
        <h1 className="academy-title">
          {language === 'ro' ? (
            <>
              Academia Chess<span>Mindset</span>
            </>
          ) : (
            <>
              Chess<span>Mindset</span> Academy
            </>
          )}
        </h1>
        <p className="academy-tagline">
          {language === 'ro'
            ? 'Unde mintea devine cea mai puternică piesă!'
            : 'Where the mind becomes the strongest piece!'}
        </p>
      </div>

      <section className="level-section">
        <div className={`academy-layout-shell ${isMindhubReadingMode ? 'mindhub-reading-shell' : ''}`}>
          <aside className="academy-area-panel">
            <h3 className="academy-area-title">{language === 'ro' ? 'Navigare' : 'Navigation'}</h3>
            <div className="academy-area-list">
              {areaMenu.map((item) => (
                <button
                  key={`area-${item.id}`}
                  className={`academy-area-btn ${item.id === 'dailyquest' ? 'dailyquest' : ''} ${activeArea === item.id ? 'active' : ''}`.trim()}
                  onClick={() => setActiveArea(item.id)}
                >
                  <span className="academy-area-icon" aria-hidden="true">{item.icon}</span>
                  {item.id === 'playground' ? (
                    <ChessyBrand />
                  ) : (
                    <span>{item.label}</span>
                  )}
                </button>
              ))}
            </div>
          </aside>

          <div className="academy-main-column">
            {shouldShowLevelHeader && (
            <div className={`level-header ${activeArea === 'academy' ? 'compact' : ''}`}>
              {activeArea !== 'academy' && (
                <>
                  <h2 className="level-title">
                    {activeArea === 'playground'
                      ? <ChessyBrand withPlaygroundSuffix />
                      : areaContent.title}
                  </h2>
                  <p className="level-subtitle">
                    {activeArea === 'dailyquest'
                      ? (
                        language === 'ro'
                          ? <>Mixul zilnic de 10 minute: teorie, puzzle-uri, quiz-uri și final cu <ChessyBrand />.</>
                          : <>Your 10-minute daily mix: theory, puzzles, quizzes, and a final <ChessyBrand /> game.</>
                      )
                      : areaContent.subtitle}
                  </p>
                </>
              )}
              {activeArea !== 'academy' && activeArea !== 'puzzles' && activeArea !== 'mindhub' && (
                <div className="coach-bio">
                  <span className="avatar-icon" aria-hidden="true">♕</span>
                  <p>{areaContent.bio}</p>
                </div>
              )}
              {activeArea === 'puzzles' && (
                <div className="puzzle-top-profile">
                  <div className="puzzle-top-profile-head">
                    <p className="module-index">{language === 'ro' ? 'Profil Puzzle' : 'Puzzle profile'}</p>
                    {user?.id ? (
                      <span className={`puzzle-difficulty-badge ${puzzleProfileUi.badgeClass}`}>
                        <span className="puzzle-difficulty-icon" aria-hidden="true">{puzzleProfileUi.icon}</span>
                        <span>{puzzleProfileUi.label}</span>
                      </span>
                    ) : (
                      <span className="puzzle-difficulty-badge">
                        <span className="puzzle-difficulty-icon" aria-hidden="true">🔒</span>
                        <span>{language === 'ro' ? 'Cont necesar pentru ranking' : 'Account required for ranking'}</span>
                      </span>
                    )}
                  </div>
                  {user?.id ? (
                    <div className="lesson-preview-meta puzzle-meta-grid puzzle-user-stats-grid">
                      <span className="puzzle-meta-chip">
                        <span className="puzzle-meta-icon" aria-hidden="true">🏆</span>
                        <span>
                          {language === 'ro' ? 'Puzzle Points:' : 'Puzzle Points:'}{' '}
                          {isLoadingPuzzleUserStats
                            ? '...'
                            : Number(puzzleUserStats?.puzzlePoints || 0).toLocaleString(language === 'ro' ? 'ro-RO' : 'en-US')}
                        </span>
                      </span>
                      <span className="puzzle-meta-chip">
                        <span className="puzzle-meta-icon" aria-hidden="true">#</span>
                        <span>
                          {language === 'ro' ? 'Rank general:' : 'Global rank:'}{' '}
                          {isLoadingPuzzleUserStats
                            ? '...'
                            : (Number.isFinite(Number(puzzleUserStats?.rank))
                              ? `#${Number(puzzleUserStats.rank)}`
                              : (language === 'ro' ? 'în calcul' : 'calculating'))}
                        </span>
                      </span>
                      <span className="puzzle-meta-chip">
                        <span className="puzzle-meta-icon" aria-hidden="true">📈</span>
                        <span>
                          {language === 'ro' ? 'Puzzle Rating:' : 'Puzzle Rating:'}{' '}
                          {Math.round(Number(puzzleSkillProfile?.puzzleRating || 500))}
                        </span>
                      </span>
                      <span className="puzzle-meta-chip">
                        <span className="puzzle-meta-icon" aria-hidden="true">✅</span>
                        <span>
                          {language === 'ro' ? 'Solved:' : 'Solved:'}{' '}
                          {isLoadingPuzzleUserStats
                            ? '...'
                            : Number(puzzleUserStats?.solved || 0).toLocaleString(language === 'ro' ? 'ro-RO' : 'en-US')}
                        </span>
                      </span>
                      <span className="puzzle-meta-chip">
                        <span className="puzzle-meta-icon" aria-hidden="true">🎯</span>
                        <span>
                          {language === 'ro' ? 'Accuracy:' : 'Accuracy:'}{' '}
                          {isLoadingPuzzleUserStats
                            ? '...'
                            : `${Math.max(0, Math.min(100, Number(puzzleUserStats?.accuracy || 0)))}%`}
                        </span>
                      </span>
                    </div>
                  ) : (
                    <div className="puzzle-guest-info">
                      <p>
                        {language === 'ro'
                          ? 'Puzzle Rating, Puzzle Points și Rank general se calculează doar pe cont activ. Creează cont și setează aliasul pentru clasament.'
                          : 'Puzzle Rating, Puzzle Points, and Global rank are calculated only on an active account.'}
                      </p>
                      <button className="btn-start-lesson mini continue" onClick={() => onRequestLogin && onRequestLogin()}>
                        {language === 'ro' ? 'Creează cont' : 'Create account'}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {isLearningArea ? (
                <>
                  {isLoadingModules && (
                    <p className="supabase-status">
                      {activeArea === 'openings'
                        ? (language === 'ro' ? 'Se încarcă modulele de deschideri...' : 'Loading opening modules...')
                        : (language === 'ro' ? 'Se încarcă modulele...' : 'Loading modules...')}
                    </p>
                  )}
                  {supabaseError && (
                    <p className="supabase-status error">
                      {activeArea === 'openings'
                        ? (language === 'ro'
                          ? 'Conținutul de deschideri este temporar indisponibil. Îți arătăm varianta disponibilă acum.'
                          : 'Opening content is temporarily unavailable. We are showing the currently available version.')
                        : (language === 'ro'
                          ? 'Conținutul este temporar indisponibil. Îți arătăm varianta disponibilă acum.'
                          : 'Content is temporarily unavailable. We are showing the currently available version.')}
                    </p>
                  )}
                </>
              ) : !isMindhubReadingMode ? (
                <p className="supabase-status">
                  {activeArea === 'puzzles'
                    ? (language === 'ro'
                      ? 'Antrenament live: diagrame tactice selectate de antrenor pentru decizii precise, sub presiune.'
                      : 'Live training: coach-selected tactical diagrams for precise decision-making under pressure.')
                    : activeArea === 'openings'
                      ? (language === 'ro'
                        ? 'Openings Lab: structură în pregătire. Primele linii sunt mapate și vor intra gradual.'
                        : 'Openings Lab: structure in progress. First lines are mapped and will roll out gradually.')
                    : activeArea === 'quizzes'
                      ? (language === 'ro'
                        ? 'Antrenament live: sesiuni quiz random din banca generală de întrebări (independentă de lecții).'
                        : 'Live training: random quiz sessions from the global question bank (independent from lessons).')
                    : activeArea === 'mindhub'
                      ? (language === 'ro'
                        ? 'MindHub este live: articole de psihologie aplicată pentru performanță la șah.'
                        : 'MindHub is live: applied psychology articles for chess performance.')
                    : activeArea === 'gamelab'
                      ? (language === 'ro'
                        ? 'GameLab este live: analize de partide celebre cu poziții interactive.'
                        : 'GameLab is live: famous game analysis with interactive positions.')
                    : activeArea === 'playground'
                      ? (language === 'ro'
                        ? 'Chessy AI Playground este live: joacă și revizuiește mutările direct pe tablă.'
                        : 'Chessy AI Playground is live: play and review moves directly on the board.')
                      : (language === 'ro'
                        ? 'Secțiune teaser: conținutul complet urmează.'
                        : 'Teaser section: full content is coming soon.')}
                </p>
              ) : null
              }
            </div>
            )}

            {isLearningArea && (
              <div className="academy-v2-layout">
                {!academyModuleDetailOpen && (
                  <section className="academy-v2-learning-map">
                    <h3>
                      {activeArea === 'openings'
                        ? (language === 'ro' ? 'Openings Lab: Hartă Repertoriu' : 'Openings Lab: Repertoire Map')
                        : (language === 'ro' ? 'Academia ChessMindset: Hartă Învățare' : 'ChessMindset Academy: Learning Map')}
                    </h3>
                    <p className="academy-v2-learning-subtitle">
                      {activeArea === 'openings'
                        ? (language === 'ro'
                          ? 'Fiecare linie bună îți dă un plan clar încă din mutările de început.'
                          : 'Each strong line gives you a clear plan from the very first moves.')
                        : (language === 'ro'
                          ? 'Fiecare lecție te face mai bun decât ieri.'
                          : 'Each lesson makes you better than yesterday.')}
                    </p>
                    <p className="academy-v2-learning-description">
                      {activeArea === 'openings'
                        ? (language === 'ro'
                          ? 'Alege deschiderea, învață ideea principală, apoi fixează planul prin pași interactivi și quiz final.'
                          : 'Choose an opening, learn the core idea, then lock the plan with interactive steps and a final quiz.')
                        : (language === 'ro'
                          ? 'Urmează traseul în 3 pași: continuă lecția curentă, fă antrenamentul de azi, apoi țintește milestone-ul următor.'
                          : 'Follow your path in 3 steps: continue the current lesson, complete today’s training, then target your next milestone.')}
                    </p>
                    <input
                      type="search"
                      className="academy-v2-search"
                      placeholder={
                        activeArea === 'openings'
                          ? (language === 'ro' ? 'Caută deschideri sau lecții...' : 'Search openings or lessons...')
                          : (language === 'ro' ? 'Caută lecții sau module...' : 'Search lessons or modules...')
                      }
                      value={treeSearch}
                      onChange={(e) => setTreeSearch(e.target.value)}
                      aria-label={
                        activeArea === 'openings'
                          ? (language === 'ro' ? 'Caută în deschideri' : 'Search in openings')
                          : (language === 'ro' ? 'Caută în academie' : 'Search in academy')
                      }
                    />
                  </section>
                )}

                {!academyModuleDetailOpen && (
                  <section className="academy-v2-home-priority-strip">
                    <div className="academy-v2-focus-card">
                      <p className="academy-v2-focus-label">{language === 'ro' ? 'Continuă traseul' : 'Continue your path'}</p>
                      <p className="academy-v2-priority-text">
                        {continuePathLesson
                          ? (language === 'ro'
                            ? `Continuă traseul cu: ${continuePathLesson.title}`
                            : `Continue your path with: ${continuePathLesson.title}`)
                          : (language === 'ro' ? 'Nu există un pas recomandat momentan.' : 'No recommended next step right now.')}
                      </p>
                      {continuePathLesson && (
                        <button
                          className="btn-start-lesson mini academy-v2-training-cta"
                          onClick={() => handleStartAcademyLesson(continuePathLesson)}
                        >
                          {continuePathLesson.status === 'in_progress'
                            ? (language === 'ro' ? 'Continuă lecția' : 'Continue lesson')
                            : (language === 'ro' ? 'Pornește lecția' : 'Start lesson')}
                        </button>
                      )}
                    </div>

                    <div className="academy-v2-focus-card">
                      <p className="academy-v2-focus-label">{language === 'ro' ? 'Antrenamentul de azi' : "Today's training"}</p>
                      <p className="academy-v2-priority-text">
                        {todayTrainingLesson
                          ? (language === 'ro'
                            ? `Lecția zilei: ${todayTrainingLesson.title}`
                            : `Lesson of the day: ${todayTrainingLesson.title}`)
                          : (language === 'ro' ? 'Nu există antrenament disponibil azi.' : 'No training available today.')}
                      </p>
                      {todayTrainingLesson && (
                        <button
                          className="btn-start-lesson mini academy-v2-training-cta continue"
                          onClick={() => handleStartAcademyLesson(todayTrainingLesson)}
                        >
                          {language === 'ro' ? 'Începe antrenamentul' : 'Start training'}
                        </button>
                      )}
                    </div>

                    <div className="academy-v2-focus-card">
                      <p className="academy-v2-focus-label">{nextMilestone?.label || (language === 'ro' ? 'Următorul milestone' : 'Next milestone')}</p>
                      <p className="academy-v2-priority-text">
                        {nextMilestone?.text || (language === 'ro' ? 'Continuă traseul pentru următorul obiectiv.' : 'Continue your path for the next objective.')}
                      </p>
                      {nextMilestone?.lesson && (
                        <button
                          className="btn-start-lesson mini"
                          onClick={() => handleStartAcademyLesson(nextMilestone.lesson)}
                        >
                          {nextMilestone?.ctaLabel || (language === 'ro' ? 'Mergi la modul' : 'Go to module')}
                        </button>
                      )}
                    </div>
                  </section>
                )}

                {!academyModuleDetailOpen && (
                  <section className="academy-v2-module-grid">
                    {academyModuleCards.map((module) => {
                      const moduleProgress = Math.min(100, Math.max(0, Number(module.progress || 0)));
                      const lessonsCount = (module.lessons || []).length;
                      const totalMinutes = (module.lessons || []).reduce((sum, l) => sum + parseDurationMinutes(l.duration), 0);
                      const moduleUiStatus = getModuleUiStatus(module);
                      const moduleStatus = moduleUiStatus === 'locked'
                        ? (language === 'ro' ? 'Locked' : 'Locked')
                        : moduleUiStatus === 'completed'
                          ? (language === 'ro' ? 'Finalizat' : 'Completed')
                          : moduleUiStatus === 'in_progress'
                            ? (language === 'ro' ? 'În curs' : 'In progress')
                          : (language === 'ro' ? 'Neînceput' : 'Not started');
                      return (
                        <article
                          key={`academy-v2-module-${module.id}`}
                          className={`academy-v2-module-card status-${moduleUiStatus}`}
                        >
                          <div className="academy-v2-module-card-top">
                            <span className={`academy-v2-module-status status-${moduleUiStatus}`}>{moduleStatus}</span>
                          </div>
                          <h4>{module.title}</h4>
                          <p>{module.summary}</p>
                          <div className="academy-v2-module-meta">
                            <span>{language === 'ro' ? `${lessonsCount} lecții` : `${lessonsCount} lessons`}</span>
                            <span>{totalMinutes > 0 ? `~${totalMinutes} min` : '—'}</span>
                          </div>
                          <div className="academy-v2-progress-track">
                            <span style={{ width: `${moduleProgress}%` }} />
                          </div>
                          <div className="academy-v2-progress-label">
                            {language === 'ro' ? `Progres: ${moduleProgress}%` : `Progress: ${moduleProgress}%`}
                          </div>
                          <button
                            type="button"
                            className="btn-start-lesson mini"
                            onClick={() => {
                              setSelectedModuleId(module.id);
                              setSelectedLessonId('');
                              setAcademyModuleDetailOpen(true);
                              setAcademyLessonFilter('all');
                              setAcademySectionPage({});
                              setAcademyOpenSections({});
                            }}
                          >
                            {language === 'ro' ? 'Deschide modulul' : 'Open module'}
                          </button>
                        </article>
                      );
                    })}
                  </section>
                )}

                {academyModuleDetailOpen && selectedAcademyModule && (
                  <section className="academy-v2-module-detail">
                    <div className="academy-v2-module-detail-head">
                      <div>
                        <h3>{selectedAcademyModule.title}</h3>
                        <p>{selectedAcademyModule.summary}</p>
                      </div>
                      <button
                        type="button"
                        className="academy-v2-view-all-btn"
                        onClick={() => setAcademyModuleDetailOpen(false)}
                      >
                        {language === 'ro' ? 'Vezi toate modulele' : 'View all modules'}
                      </button>
                    </div>

                    <div className="academy-v2-module-content">
                      <div className="academy-v2-lessons-column">
                        <div className="academy-v2-filter-row">
                          {[
                            { id: 'all', label: language === 'ro' ? 'Toate' : 'All' },
                            { id: 'in_progress', label: language === 'ro' ? 'În curs' : 'In progress' },
                            { id: 'completed', label: language === 'ro' ? 'Finalizate' : 'Completed' },
                            { id: 'not_started', label: language === 'ro' ? 'Neîncepute' : 'Not started' },
                            { id: 'locked', label: 'Locked' }
                          ].map((f) => (
                            <button
                              key={`academy-v2-filter-${f.id}`}
                              type="button"
                              className={`academy-v2-filter-btn ${academyLessonFilter === f.id ? 'active' : ''}`}
                              onClick={() => {
                                setAcademyLessonFilter(f.id);
                                setAcademySectionPage({});
                              }}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>

                        <div className="academy-v2-sections">
                          {academyLessonSections.map((section, idx) => {
                            const isOpen = academyOpenSections[section.key] ?? idx === 0;
                            const filteredItems = section.items.filter((lesson) => {
                              const lessonUiStatus = getLessonUiStatus(lesson);
                              if (academyLessonFilter === 'all') return true;
                              if (academyLessonFilter === 'in_progress') return lessonUiStatus === 'in_progress';
                              if (academyLessonFilter === 'locked') return lessonUiStatus === 'locked';
                              if (academyLessonFilter === 'completed') return lessonUiStatus === 'completed';
                              if (academyLessonFilter === 'not_started') return lessonUiStatus === 'pending';
                              return true;
                            });
                            const pageSize = 5;
                            const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
                            const currentPage = Math.min(academySectionPage[section.key] || 1, totalPages);
                            const pageStart = (currentPage - 1) * pageSize;
                            const pageItems = filteredItems.slice(pageStart, pageStart + pageSize);

                            return (
                              <section key={section.key} className="academy-v2-section-card">
                                <button
                                  type="button"
                                  className="academy-v2-section-toggle"
                                  onClick={() =>
                                    setAcademyOpenSections((prev) => ({ ...prev, [section.key]: !isOpen }))
                                  }
                                >
                                  <span>
                                    <strong>{section.title}</strong>
                                    <small>{section.range}</small>
                                  </span>
                                  <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
                                </button>

                                {isOpen && (
                                  <div className="academy-v2-section-lessons">
                                    {pageItems.length === 0 && (
                                      <div className="academy-v2-empty">
                                        {language === 'ro' ? 'Nu există lecții pentru acest filtru.' : 'No lessons for this filter.'}
                                      </div>
                                    )}
                                    {pageItems.map((lesson) => {
                                      const lessonUiStatus = getLessonUiStatus(lesson);
                                      const lessonLockLabel = getLessonLockLabel(lesson);
                                      const lessonStatusLabel =
                                        lessonUiStatus === 'completed'
                                          ? 'Done'
                                          : lessonUiStatus === 'in_progress'
                                            ? (language === 'ro' ? 'În curs' : 'In progress')
                                            : lessonUiStatus === 'locked'
                                              ? lessonLockLabel
                                              : (language === 'ro' ? 'Neînceput' : 'Not started');
                                      return (
                                        <button
                                          key={`academy-v2-lesson-${lesson.id}`}
                                          type="button"
                                          className={`academy-v2-lesson-row status-${lessonUiStatus} ${selectedAcademyLesson?.id === lesson.id ? 'selected' : ''}`}
                                          onClick={() => setSelectedLessonId(lesson.id)}
                                        >
                                          <span className="academy-v2-lesson-main">
                                            <strong>{lesson.title}</strong>
                                            <small>
                                              {lesson.duration || '—'}
                                              {lesson.difficulty ? ` · ${language === 'ro' ? 'Dificultate' : 'Difficulty'}: ${difficultyLabel(lesson.difficulty)}` : ''}
                                            </small>
                                          </span>
                                          <span className={`academy-v2-lesson-status status-${lessonUiStatus}`}>{lessonStatusLabel}</span>
                                        </button>
                                      );
                                    })}

                                    {filteredItems.length > pageSize && (
                                      <div className="academy-v2-pagination">
                                        <button
                                          type="button"
                                          disabled={currentPage <= 1}
                                          onClick={() =>
                                            setAcademySectionPage((prev) => ({
                                              ...prev,
                                              [section.key]: Math.max(1, currentPage - 1)
                                            }))
                                          }
                                        >
                                          {language === 'ro' ? 'Anterior' : 'Prev'}
                                        </button>
                                        <span>
                                          {language === 'ro' ? 'Pagina' : 'Page'} {currentPage}/{totalPages}
                                        </span>
                                        <button
                                          type="button"
                                          disabled={currentPage >= totalPages}
                                          onClick={() =>
                                            setAcademySectionPage((prev) => ({
                                              ...prev,
                                              [section.key]: Math.min(totalPages, currentPage + 1)
                                            }))
                                          }
                                        >
                                          {language === 'ro' ? 'Următor' : 'Next'}
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </section>
                            );
                          })}
                        </div>
                      </div>

                      <aside className="academy-v2-focus-column">
                        <div className="academy-v2-focus-card">
                          <p className="academy-v2-focus-label">{language === 'ro' ? 'Continuă traseul' : 'Continue your path'}</p>
                          <p className="academy-v2-priority-text">
                            {continuePathLesson
                              ? (language === 'ro'
                                ? `Recomandat: ${continuePathLesson.title}`
                                : `Recommended: ${continuePathLesson.title}`)
                              : (language === 'ro' ? 'Nu există o lecție recomandată momentan.' : 'No recommended lesson right now.')}
                          </p>
                          {continuePathLesson && (
                            <button
                              className="btn-start-lesson mini continue"
                              onClick={() => handleStartAcademyLesson(continuePathLesson)}
                            >
                              {continuePathLesson.status === 'in_progress'
                                ? (language === 'ro' ? 'Continuă acum' : 'Continue now')
                                : (language === 'ro' ? 'Pornește lecția' : 'Start lesson')}
                            </button>
                          )}
                        </div>

                        <div className="academy-v2-focus-card">
                          <p className="academy-v2-focus-label">{language === 'ro' ? 'Următorul pas' : 'Next step'}</p>
                          {selectedAcademyLesson && (
                            <button
                              className={`btn-start-lesson mini ${selectedAcademyLesson.status === 'in_progress' ? 'continue' : ''}`}
                              onClick={() => {
                                handleStartAcademyLesson(selectedAcademyLesson);
                              }}
                              disabled={Boolean(selectedAcademyLesson.comingSoon)}
                            >
                              {selectedAcademyLesson.comingSoon
                                ? (language === 'ro' ? 'În curând' : 'Coming soon')
                                : selectedAcademyLesson.planLocked
                                  ? (language === 'ro' ? 'Upgrade necesar' : 'Upgrade required')
                                  : selectedAcademyLesson.guestLocked
                                    ? (language === 'ro' ? 'Creează cont' : 'Create account')
                                    : selectedAcademyLesson.status === 'in_progress'
                                      ? (language === 'ro' ? 'Continuă de unde ai rămas' : 'Continue where you left off')
                                      : (language === 'ro' ? 'Start lecție' : 'Start lesson')}
                            </button>
                          )}

                          <div className="academy-v2-focus-stats">
                            <div>
                              <span>{language === 'ro' ? 'Progres modul' : 'Module progress'}</span>
                              <strong>{selectedAcademyModuleProgress}%</strong>
                            </div>
                            <div className="academy-v2-focus-progress-track" aria-hidden="true">
                              <span style={{ width: `${selectedAcademyModuleProgress}%` }} />
                            </div>
                            <div>
                              <span>{language === 'ro' ? 'Durată estimată' : 'Estimated duration'}</span>
                              <strong>{selectedAcademyModuleMinutes > 0 ? `~${selectedAcademyModuleMinutes} min` : '—'}</strong>
                            </div>
                            <div>
                              <span>{language === 'ro' ? 'Lecții finalizate' : 'Completed lessons'}</span>
                              <strong>
                                {(selectedAcademyModule.lessons || []).filter((l) => l.status === 'completed').length}/
                                {(selectedAcademyModule.lessons || []).length}
                              </strong>
                            </div>
                          </div>
                        </div>

                        {selectedAcademyLesson && (
                          <div className={`lesson-preview-card ${selectedAcademyLesson.comingSoon ? 'coming-soon' : ''} status-${getLessonUiStatus(selectedAcademyLesson)}`}>
                            <div className="lesson-preview-head">
                              <span className="lesson-pill">{selectedAcademyLesson.section}</span>
                              {getLessonUiStatus(selectedAcademyLesson) === 'completed' && (
                                <span className="lesson-pill done">Done</span>
                              )}
                              {getLessonUiStatus(selectedAcademyLesson) === 'in_progress' && (
                                <span className="lesson-pill progress">{language === 'ro' ? 'În progres' : 'In progress'}</span>
                              )}
                            </div>
                            <h4>{selectedAcademyLesson.title}</h4>
                            <p>
                              {selectedAcademyLesson.description ||
                                (language === 'ro'
                                  ? 'Selectează Start pentru a începe lecția.'
                                  : 'Press Start to begin the lesson.')}
                            </p>
                            <div className="lesson-preview-meta">
                              <span>{selectedAcademyLesson.duration}</span>
                              {selectedAcademyLesson.difficulty && (
                                <span>
                                  {language === 'ro' ? 'Dificultate:' : 'Difficulty:'} {difficultyLabel(selectedAcademyLesson.difficulty)}
                                </span>
                              )}
                            </div>
                            <div className="lesson-progress-row">
                              <div className="lesson-progress-track">
                                <div
                                  className="lesson-progress-fill"
                                  style={{ width: `${Math.min(100, Math.max(0, Number(selectedAcademyLesson.progressPct || 0)))}%` }}
                                />
                              </div>
                              <div className="lesson-progress-label">
                                {selectedAcademyLesson.stepsTotal > 0
                                  ? language === 'ro'
                                    ? `Progres: ${selectedAcademyLesson.stepsCompleted}/${selectedAcademyLesson.stepsTotal} pași (${selectedAcademyLesson.progressPct}%)`
                                    : `Progress: ${selectedAcademyLesson.stepsCompleted}/${selectedAcademyLesson.stepsTotal} steps (${selectedAcademyLesson.progressPct}%)`
                                  : language === 'ro'
                                    ? 'Progres: —'
                                    : 'Progress: —'}
                              </div>
                            </div>
                          </div>
                        )}
                      </aside>
                    </div>
                  </section>
                )}
              </div>
            )}

            {!isLearningArea && (
            <div className={`academy-tree-layout ${activeArea === 'puzzles' ? 'puzzle-v2-layout' : ''} ${isMindhubReadingMode ? 'mindhub-reading-mode' : ''} ${activeArea === 'playground' ? 'playground-layout' : ''}`}>
              {activeArea !== 'playground' && (
              <aside className="academy-tree-panel">
                <h3 className="academy-tree-title">
                  {activeArea === 'academy'
                    ? (language === 'ro' ? 'Hartă Învățare' : 'Learning Map')
                    : activeArea === 'puzzles'
                      ? (language === 'ro' ? 'Hartă Tactică' : 'Tactical Map')
                    : activeArea === 'openings'
                      ? (language === 'ro' ? 'Hartă Deschideri' : 'Openings Map')
                    : activeArea === 'quizzes'
                      ? (language === 'ro' ? 'Hartă Quiz-uri' : 'Quiz Map')
                    : activeArea === 'mindhub'
                      ? (language === 'ro' ? 'Articole Mind Hub' : 'Mind Hub Articles')
                    : activeArea === 'gamelab'
                      ? (language === 'ro' ? 'GameLab · Analize' : 'GameLab · Analysis')
                      : activeArea === 'dailyquest'
                        ? (language === 'ro' ? 'Misiunea zilei' : 'Daily Quest')
                      : (language === 'ro' ? 'Community (teaser)' : 'Community (teaser)')}
                </h3>
                <p className="academy-tree-subtitle">
                  {activeArea === 'academy'
                    ? (language === 'ro'
                      ? 'Fiecare lecție te face mai bun decât ieri.'
                      : 'Each lesson makes you better than yesterday.')
                    : activeArea === 'puzzles'
                      ? (language === 'ro'
                        ? 'Alege tema, calculează varianta critică și execută mutarea ca într-o partidă reală.'
                        : 'Pick the theme, calculate the critical line, and execute the move like in a real game.')
                    : activeArea === 'openings'
                      ? (language === 'ro'
                        ? 'Alege o linie și înțelege planul strategic încă din primele mutări.'
                        : 'Choose a line and understand the strategic plan from the first moves.')
                    : activeArea === 'quizzes'
                      ? (language === 'ro'
                        ? 'Alege subiectul, răspunde clar și urcă în rank-ul de quiz.'
                        : 'Pick the topic, answer clearly, and climb the quiz rank.')
                    : activeArea === 'mindhub'
                      ? (language === 'ro'
                        ? 'Alege un articol, vezi timpul estimat de citire și intră în modul focus.'
                        : 'Pick an article, preview estimated reading time, then enter focus mode.')
                      : activeArea === 'gamelab'
                        ? (language === 'ro'
                          ? 'Alege analiza, apoi navighează pozițiile-cheie direct pe tablă.'
                          : 'Pick an analysis, then navigate key positions directly on the board.')
                      : activeArea === 'dailyquest'
                        ? (language === 'ro'
                          ? <>Mixul zilnic de 10 minute: teorie, puzzle-uri, quiz-uri și final cu <ChessyBrand />.</>
                          : <>Your 10-minute daily mix: theory, puzzles, quizzes, and a final <ChessyBrand /> game.</>)
                      : (language === 'ro'
                        ? 'Structură pregătită pentru extindere.'
                        : 'Structure prepared for expansion.')}
                </p>
                {activeArea === 'academy' && (
                  <p className="academy-tree-description">
                    {language === 'ro'
                      ? 'Explorează modulele și alege lecția pe care vrei să o stăpânești astăzi. Navighează rapid prin traseu și urmărește-ți progresul pas cu pas.'
                      : 'Explore modules and choose the lesson you want to master today. Move quickly through your path and track progress step by step.'}
                  </p>
                )}
                {activeArea === 'academy' && (
                  <div className="academy-tree-search-wrap">
                    <input
                      type="search"
                      className="academy-tree-search"
                      placeholder={language === 'ro' ? 'Caută lecții sau module...' : 'Search lessons or modules...'}
                      value={treeSearch}
                      onChange={(e) => setTreeSearch(e.target.value)}
                      aria-label={language === 'ro' ? 'Caută în arborele de lecții' : 'Search in learning tree'}
                    />
                  </div>
                )}
                {activeArea === 'academy' ? (
                  <ul className="academy-tree-list">
                    {filteredModulesData.map((module) => {
                    const moduleComingSoon = module.available === false;
                    const isOpen = openModuleIds.includes(module.id);
                    const moduleProgress = Math.min(100, Math.max(0, Number(module.progress || 0)));
                    const moduleProgressClass =
                      moduleProgress >= 100
                        ? 'progress-done'
                        : moduleProgress >= 60
                          ? 'progress-high'
                          : moduleProgress > 0
                            ? 'progress-low'
                            : 'progress-none';
                    return (
                      <li
                        key={`tree-${module.id}`}
                        className={`academy-tree-module ${isOpen ? 'open' : ''} ${moduleComingSoon ? 'coming-soon' : ''}`}
                      >
                        <button
                          className={`academy-tree-module-btn ${selectedModuleId === module.id ? 'selected' : ''} ${moduleProgressClass}`}
                          onClick={() => {
                            setSelectedModuleId(module.id);
                            setSelectedLessonId('');
                            setOpenModuleIds((prev) =>
                              prev.includes(module.id)
                                ? prev.filter((id) => id !== module.id)
                                : [...prev, module.id]
                            );
                          }}
                        >
                          <span className="academy-tree-module-icon" aria-hidden="true">♜</span>
                          <span className="academy-tree-module-label">
                            {module.title}
                          </span>
                          <span className="academy-tree-module-chevron" aria-hidden="true">{isOpen ? '▾' : '▸'}</span>
                        </button>

                        {isOpen && (
                          <ul className="academy-tree-lesson-list">
                            {(module.lessons || []).map((lesson, lessonIndex) => {
                              const state = lessonMapState(module, lesson);
                              const accessLocked = Boolean(lesson.guestLocked);
                              const planLocked = Boolean(lesson.planLocked);
                              const canStart = state !== 'coming' && !accessLocked && !planLocked && lesson.available !== false;
                              return (
                                <li key={`tree-lesson-${lesson.id}`} className="academy-tree-lesson-item">
                                <button
                                  className={`academy-tree-lesson-btn ${state} ${selectedLessonId === lesson.id ? 'selected' : ''}`}
                                  onClick={() => {
                                    setOpenModuleIds((prev) =>
                                      prev.includes(module.id) ? prev : [...prev, module.id]
                                    );
                                    setSelectedModuleId(module.id);
                                    setSelectedLessonId(lesson.id);
                                    setScrollToLessonActions(true);
                                  }}
                                  disabled={!canStart}
                                  title={
                                    accessLocked
                                      ? (language === 'ro' ? 'Creează cont pentru acces' : 'Create account for access')
                                      : planLocked
                                        ? (language === 'ro' ? 'Upgrade necesar' : 'Upgrade required')
                                      : state === 'coming'
                                        ? (language === 'ro' ? 'În curând' : 'Coming soon')
                                        : !canStart
                                          ? (language === 'ro' ? 'Acces restricționat' : 'Restricted access')
                                        : lesson.title
                                  }
                                >
                                  <span className="academy-tree-lesson-icon" aria-hidden="true" />
                                  <span className="academy-tree-lesson-content">
                                    <span className="academy-tree-lesson-title">
                                      {language === 'ro'
                                        ? `Lecția ${lessonIndex + 1} - ${lesson.title}`
                                        : `Lesson ${lessonIndex + 1} - ${lesson.title}`}
                                    </span>
                                    <span className="academy-tree-lesson-meta">
                                      <span>{lesson.duration || '—'}</span>
                                      {lesson.difficulty && (
                                        <span>
                                          {language === 'ro' ? 'Dificultate:' : 'Difficulty:'} {difficultyLabel(lesson.difficulty)}
                                        </span>
                                      )}
                                    </span>
                                    {lesson.stepsTotal > 0 && (
                                      <span className="academy-tree-lesson-progress">
                                        {language === 'ro'
                                          ? `Progres: ${lesson.stepsCompleted}/${lesson.stepsTotal} pași (${lesson.progressPct}%)`
                                          : `Progress: ${lesson.stepsCompleted}/${lesson.stepsTotal} steps (${lesson.progressPct}%)`}
                                      </span>
                                    )}
                                  </span>
                                  {lesson.progressPct > 0 && (
                                    <span className="academy-tree-lesson-pct">
                                      {`${lesson.progressPct}%`}
                                    </span>
                                  )}
                                  {!canStart && state === 'coming' && (
                                    <span className="academy-tree-lesson-coming">
                                      {language === 'ro' ? 'În curând' : 'Coming soon'}
                                    </span>
                                  )}
                                  {(accessLocked || planLocked || (!canStart && state !== 'coming')) && (
                                    <span className="academy-tree-lesson-coming">
                                      {accessLocked
                                        ? (language === 'ro' ? 'Creează cont' : 'Create account')
                                        : planLocked
                                          ? (language === 'ro' ? 'Upgrade' : 'Upgrade')
                                          : (!user?.id
                                            ? (language === 'ro' ? 'Creează cont' : 'Create account')
                                            : (language === 'ro' ? 'Upgrade' : 'Upgrade'))}
                                    </span>
                                  )}
                                </button>
                              </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                  </ul>
                ) : activeArea === 'puzzles' ? (
                  <p className="academy-tree-empty">
                    {language === 'ro'
                      ? 'Selectează un modul de puzzle din zona principală.'
                      : 'Select a puzzle module from the main area.'}
                  </p>
                ) : activeArea === 'dailyquest' ? (
                  <div className="daily-quest-teaser">
                    <p className="daily-quest-teaser-kicker">
                      {language === 'ro' ? '🔥 Quest-ul zilei' : '🔥 Today’s Quest'}
                    </p>
                    <h4>
                      {language === 'ro'
                        ? 'Intră în misiune și termină mixul zilnic.'
                        : 'Jump in and finish your daily mixed challenge.'}
                    </h4>
                    <button
                      type="button"
                      className="btn-start-lesson mini daily-quest-launch-btn"
                      onClick={dailyQuestStarted ? handleContinueDailyQuest : handleStartDailyQuest}
                    >
                      {dailyQuestStarted
                        ? (language === 'ro' ? 'Continuă Daily Quest' : 'Continue Daily Quest')
                        : (language === 'ro' ? 'Start Daily Quest' : 'Start Daily Quest')}
                    </button>
                    <p className="daily-quest-teaser-timer">
                      {language === 'ro' ? 'Timer quest:' : 'Quest timer:'} <strong>{dailyQuestTimerLabel}</strong>
                    </p>
                    <p className="daily-quest-teaser-step">
                      {language === 'ro' ? 'Pas curent:' : 'Current step:'}{' '}
                      <strong>
                        {dailyQuestStepIndex >= 0 && dailyQuestSteps[dailyQuestStepIndex]
                          ? dailyQuestSteps[dailyQuestStepIndex].title
                          : (language === 'ro' ? 'Neînceput' : 'Not started')}
                      </strong>
                    </p>
                  </div>
                ) : activeArea === 'quizzes' ? (
                  <>
                    <ul className="academy-tree-list">
                      <li className="academy-tree-module open">
                        <button
                          className={`academy-tree-module-btn ${selectedQuizTopic === 'all' ? 'selected' : ''}`}
                          onClick={() => setSelectedQuizTopic('all')}
                        >
                          <span className="academy-tree-module-icon" aria-hidden="true">🗂️</span>
                          <span className="academy-tree-module-label">{language === 'ro' ? 'Toate subiectele' : 'All topics'}</span>
                        </button>
                      </li>
                      {quizTopics.map((topic) => (
                        <li key={`quiz-topic-${topic.id}`} className="academy-tree-module open">
                          <button
                            className={`academy-tree-module-btn ${selectedQuizTopic === topic.id ? 'selected' : ''}`}
                            onClick={() => setSelectedQuizTopic(topic.id)}
                          >
                            <span className="academy-tree-module-icon" aria-hidden="true">{topic.id === 'history' ? '📚' : '🧠'}</span>
                            <span className="academy-tree-module-label">{topic.label}</span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    {isLoadingQuizHub && (
                      <p className="academy-tree-empty">
                        {language === 'ro' ? 'Se încarcă quiz-urile...' : 'Loading quizzes...'}
                      </p>
                    )}
                    {!isLoadingQuizHub && !quizBankQuestions.length && !quizHubError && (
                      <p className="academy-tree-empty">
                        {language === 'ro'
                          ? 'Nu există încă întrebări în banca de quiz.'
                          : 'No questions are available in the quiz bank yet.'}
                      </p>
                    )}
                    {quizHubError && (
                      <p className="academy-tree-empty">{quizHubError}</p>
                    )}
                  </>
                ) : activeArea === 'mindhub' ? (
                  <ul className="academy-tree-list">
                    {mindhubArticles.map((article) => {
                      const isSelected = mindhubSelectedArticleId === article.id;
                      return (
                        <li key={`mindhub-tree-${article.id}`} className="academy-tree-module open">
                          <button
                            className={`academy-tree-module-btn ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              setMindhubSelectedArticleId(article.id);
                              setMindhubReadingProgress(0);
                              setMindhubQuizAnswers({});
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >
                            <span className="academy-tree-module-icon" aria-hidden="true">🧠</span>
                            <span className="academy-tree-module-label">{article.title}</span>
                          </button>
                          <p className="academy-tree-module-title">
                            {article.subtitle}
                          </p>
                          <p className="academy-tree-module-title">
                            {language === 'ro'
                              ? `~${article.readMinutes} min citire`
                              : `~${article.readMinutes} min read`}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                ) : activeArea === 'gamelab' ? (
                  <ul className="academy-tree-list">
                    {gamelabArticles.map((article) => {
                      const isSelected = gamelabSelectedArticleId === article.id;
                      return (
                        <li key={`gamelab-tree-${article.id}`} className="academy-tree-module open">
                          <button
                            className={`academy-tree-module-btn ${isSelected ? 'selected' : ''}`}
                            onClick={() => {
                              setGamelabSelectedArticleId(article.id);
                              setGamelabPositionIndex(0);
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >
                            <span className="academy-tree-module-icon" aria-hidden="true">🎬</span>
                            <span className="academy-tree-module-label">{article.title}</span>
                          </button>
                          <p className="academy-tree-module-title">{article.subtitle}</p>
                          <p className="academy-tree-module-title">
                            {language === 'ro'
                              ? `~${article.readMinutes} min analiză`
                              : `~${article.readMinutes} min analysis`}
                          </p>
                        </li>
                      );
                    })}
                  </ul>
                ) : activeArea === 'playground' ? (
                  <ul className="academy-tree-list">
                    {[
                      { id: 'beginner', label: language === 'ro' ? 'Începător' : 'Beginner' },
                      { id: 'intermediate', label: language === 'ro' ? 'Intermediar' : 'Intermediate' },
                      { id: 'advanced', label: language === 'ro' ? 'Avansat' : 'Advanced' },
                      { id: 'expert', label: language === 'ro' ? 'Expert' : 'Expert' }
                    ].map((lvl) => (
                      <li key={`playground-lvl-${lvl.id}`} className="academy-tree-module open">
                        <button className="academy-tree-module-btn selected" type="button">
                          <span className="academy-tree-module-icon" aria-hidden="true">♛</span>
                          <span className="academy-tree-module-label">{lvl.label}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="academy-tree-list">
                    {teaserModules.map((item, idx) => (
                      <li key={`teaser-tree-${item.id}`} className="academy-tree-module open coming-soon">
                        <button className={`academy-tree-module-btn ${idx === 0 ? 'selected' : ''}`}>
                          <span className="academy-tree-module-icon" aria-hidden="true">
                            {activeArea === 'puzzles' ? '🧩' : '♜'}
                          </span>
                          <span className="academy-tree-module-label">{item.title}</span>
                          <span className="academy-tree-module-chevron" aria-hidden="true">▾</span>
                        </button>
                        <p className="academy-tree-module-title">{item.summary}</p>
                      </li>
                    ))}
                  </ul>
                )}
                {activeArea === 'academy' && filteredModulesData.length === 0 && (
                  <p className="academy-tree-empty">
                    {language === 'ro' ? 'Niciun rezultat pentru căutarea ta.' : 'No results found for your search.'}
                  </p>
                )}
              </aside>
              )}

              <div className="module-list">
              {activeArea === 'dailyquest' ? (
                <>
                  <article className="module-card daily-quest-card">
                    <div className="daily-quest-head">
                      <span className="module-index">{language === 'ro' ? 'Misiune zilnică' : 'Daily mission'}</span>
                      <h3>{language === 'ro' ? 'Daily Quest · 10 minute max' : 'Daily Quest · 10 min max'}</h3>
                      <p>
                        {language === 'ro'
                          ? <>Plan mixt: exerciții, puzzle-uri, quiz-uri și teorie, apoi o partidă finală cu <ChessyBrand />.</>
                          : <>Mixed plan: exercises, puzzles, quizzes, and theory, then a final game with <ChessyBrand />.</>}
                      </p>
                    </div>

                    <div className="daily-quest-stats">
                      <span>{language === 'ro' ? 'Nivel recomandat' : 'Recommended level'}: <strong>{dailyQuestPlan.recommendedDifficulty}</strong></span>
                      <span>{language === 'ro' ? 'Țintă' : 'Target'}: <strong>{dailyQuestPlan.totalQuestions}</strong></span>
                      <span>{language === 'ro' ? 'Timp maxim' : 'Max time'}: <strong>{dailyQuestPlan.maxMinutes} min</strong></span>
                    </div>

                    <div className="daily-quest-grid">
                      <article className="daily-quest-step">
                        <h4>1. {language === 'ro' ? 'Încălzire teorie' : 'Theory warm-up'}</h4>
                        <p>
                          {language === 'ro'
                            ? `${dailyQuestPlan.theoryTarget} întrebări de teorie și reguli (citire + decizie).`
                            : `${dailyQuestPlan.theoryTarget} theory and rules questions (read + decide).`}
                        </p>
                      </article>

                      <article className="daily-quest-step">
                        <h4>2. {language === 'ro' ? 'Puzzle sprint' : 'Puzzle sprint'}</h4>
                        <p>
                          {language === 'ro'
                            ? `${dailyQuestPlan.puzzleTarget} puzzle-uri tactice rapide.`
                            : `${dailyQuestPlan.puzzleTarget} quick tactical puzzles.`}
                        </p>
                      </article>

                      <article className="daily-quest-step">
                        <h4>3. {language === 'ro' ? 'Quiz focus' : 'Quiz focus'}</h4>
                        <p>
                          {language === 'ro'
                            ? `${dailyQuestPlan.quizTarget} întrebări din Quiz Hub, random pe nivel.`
                            : `${dailyQuestPlan.quizTarget} Quiz Hub questions, randomized by level.`}
                        </p>
                      </article>

                      <article className="daily-quest-step">
                        <h4>4. {language === 'ro' ? <>Final boss: <ChessyBrand /></> : <>Final boss: <ChessyBrand /></>}</h4>
                        <p>
                          {language === 'ro'
                            ? 'Joacă o partidă de consolidare imediat după quest.'
                            : 'Play one consolidation game right after the quest.'}
                        </p>
                      </article>
                    </div>

                    <div className="daily-quest-actions">
                      <button
                        type="button"
                        className="btn-start-lesson mini daily-quest-launch-btn"
                        onClick={dailyQuestStarted ? handleContinueDailyQuest : handleStartDailyQuest}
                      >
                        {dailyQuestStarted
                          ? (language === 'ro' ? 'Continuă Daily Quest' : 'Continue Daily Quest')
                          : (language === 'ro' ? 'Start Daily Quest' : 'Start Daily Quest')}
                      </button>
                      <span className="daily-quest-timer-inline">
                        {language === 'ro' ? 'Timer quest:' : 'Quest timer:'} <strong>{dailyQuestTimerLabel}</strong>
                      </span>
                      <span className="daily-quest-timer-inline">
                        {language === 'ro' ? 'Pas curent:' : 'Current step:'}{' '}
                        <strong>
                          {dailyQuestStepIndex >= 0 && dailyQuestSteps[dailyQuestStepIndex]
                            ? dailyQuestSteps[dailyQuestStepIndex].title
                            : (language === 'ro' ? 'Neînceput' : 'Not started')}
                        </strong>
                      </span>
                    </div>

                    {!dailyQuestPlan.hasAnyQuestContent && (
                      <p className="module-footer-note">
                        {language === 'ro'
                          ? 'Încărcăm conținutul Daily Quest. Încearcă din nou în câteva secunde.'
                          : 'Daily Quest content is loading. Try again in a few seconds.'}
                      </p>
                    )}
                  </article>
                </>
              ) : activeArea === 'puzzles' ? (
                <>
                  {!puzzleModuleDetailOpen && (
                    <>
                      <section className="academy-v2-learning-map">
                        <h2>{language === 'ro' ? 'Hartă Tactică Puzzle' : 'Puzzle Tactical Map'}</h2>
                        <p className="academy-v2-learning-map-subtitle">
                          {language === 'ro'
                            ? 'Fiecare categorie îți antrenează un tip diferit de decizie.'
                            : 'Each category trains a different decision pattern.'}
                        </p>
                        <p className="academy-v2-learning-map-desc">
                          {language === 'ro'
                            ? 'Alege modulul de puzzle, intră în secțiune și rezolvă pozițiile pas cu pas, cu progres urmărit la fel ca în Academy.'
                            : 'Choose a puzzle module, enter its section, and solve positions step by step with Academy-style progress tracking.'}
                        </p>
                      </section>

                      <section className="academy-v2-module-grid puzzle-v2-module-grid">
                        {puzzleModuleCards.map((module) => (
                          <article
                            key={`puzzle-module-${module.id}`}
                            className={`academy-v2-module-card status-${module.status}`}
                          >
                            <div className="academy-v2-module-top">
                              <span className={`academy-v2-module-status status-${module.status}`}>
                                {module.status === 'completed'
                                  ? (language === 'ro' ? 'Done' : 'Done')
                                  : module.status === 'in_progress'
                                    ? (language === 'ro' ? 'În curs' : 'In progress')
                                    : module.status === 'locked'
                                      ? 'Locked'
                                      : (language === 'ro' ? 'Disponibil' : 'Available')}
                              </span>
                            </div>
                            <h4>{module.title}</h4>
                            <p>
                              {module.summary ||
                                (language === 'ro'
                                  ? 'Antrenament tactic ghidat pe poziții reale, cu focus pe decizii clare.'
                                  : 'Guided tactical training on real positions, focused on clear decisions.')}
                            </p>
                            <div className="academy-v2-module-meta">
                              <span>{module.lessonCountLabel}</span>
                              <span>{module.durationLabel}</span>
                            </div>
                            <div className="academy-v2-module-progress">
                              <div className="academy-v2-module-progress-track">
                                <div
                                  className="academy-v2-module-progress-fill"
                                  style={{ width: `${Math.min(100, Math.max(0, Number(module.progress || 0)))}%` }}
                                />
                              </div>
                              <div className="academy-v2-module-progress-label">
                                {language === 'ro' ? `Progres: ${module.progress}%` : `Progress: ${module.progress}%`}
                              </div>
                            </div>
                            <button
                              type="button"
                              className="btn-start-lesson mini"
                              onClick={() => {
                                if (module.comingSoon) return;
                                setSelectedPuzzleCategoryId(module.id);
                                if ((module.puzzles || []).length === 0) {
                                  setLoadedPuzzleCategoryMap((prev) => ({ ...prev, [module.id]: false }));
                                }
                                const nextList = module.puzzles || [];
                                const firstRating = Number(nextList?.[0]?.rating || 0);
                                const nextDifficulty =
                                  firstRating < 1000 ? 'easy' :
                                  firstRating < 1300 ? 'medium' :
                                  firstRating < 1600 ? 'advanced' :
                                  firstRating < 2000 ? 'hard' : 'expert';
                                setSelectedPuzzleDifficultyId(nextDifficulty);
                                setPuzzleModuleDetailOpen(true);
                                setPuzzleGroupFilter('all');
                                setPuzzleSectionPage({});
                                setPuzzleOpenSections({});
                              }}
                              disabled={Boolean(module.comingSoon)}
                            >
                              {module.comingSoon
                                ? (language === 'ro' ? 'În curând' : 'Coming soon')
                                : (language === 'ro' ? 'Deschide modulul' : 'Open module')}
                            </button>
                          </article>
                        ))}
                      </section>
                    </>
                  )}

                  {puzzleModuleDetailOpen && selectedPuzzleModuleCard && (
                    <section className="academy-v2-module-detail">
                      <div className="academy-v2-module-detail-head">
                        <div>
                          <h3>{selectedPuzzleModuleCard.title}</h3>
                          <p>
                            {language === 'ro'
                              ? 'Antrenament tactical în grupe de dificultate, cu progres urmărit pas cu pas.'
                              : 'Tactical training in difficulty groups, with step-by-step tracked progress.'}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="academy-v2-view-all-btn"
                          onClick={() => setPuzzleModuleDetailOpen(false)}
                        >
                          {language === 'ro' ? 'Vezi toate modulele' : 'View all modules'}
                        </button>
                      </div>

                      <div className="academy-v2-module-content">
                        <div className="academy-v2-lessons-column">
                          <div className="academy-v2-filter-row">
                            {[
                              { id: 'all', label: language === 'ro' ? 'Toate' : 'All' },
                              { id: 'in_progress', label: language === 'ro' ? 'În curs' : 'In progress' },
                              { id: 'completed', label: language === 'ro' ? 'Finalizate' : 'Completed' },
                              { id: 'not_started', label: language === 'ro' ? 'Neîncepute' : 'Not started' },
                              { id: 'locked', label: 'Locked' }
                            ].map((f) => (
                              <button
                                key={`puzzle-v2-filter-${f.id}`}
                                type="button"
                                className={`academy-v2-filter-btn ${puzzleGroupFilter === f.id ? 'active' : ''}`}
                                onClick={() => {
                                  setPuzzleGroupFilter(f.id);
                                  setPuzzleSectionPage({});
                                }}
                              >
                                {f.label}
                              </button>
                            ))}
                          </div>

                          <div className="academy-v2-sections">
                            {puzzleGroupSections.map((section, idx) => {
                              const isOpen = puzzleOpenSections[section.key] ?? idx === 0;
                              const filteredItems = section.items.filter((group) => {
                                const status = getPuzzleGroupStatus(selectedPuzzleCategory?.id, group);
                                if (puzzleGroupFilter === 'all') return true;
                                if (puzzleGroupFilter === 'in_progress') return status === 'in_progress';
                                if (puzzleGroupFilter === 'completed') return status === 'completed';
                                if (puzzleGroupFilter === 'not_started') return status === 'pending';
                                if (puzzleGroupFilter === 'locked') return status === 'locked';
                                return true;
                              });
                              const pageSize = 5;
                              const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
                              const currentPage = Math.min(puzzleSectionPage[section.key] || 1, totalPages);
                              const pageStart = (currentPage - 1) * pageSize;
                              const pageItems = filteredItems.slice(pageStart, pageStart + pageSize);

                              return (
                                <section key={section.key} className="academy-v2-section-card">
                                  <button
                                    type="button"
                                    className="academy-v2-section-toggle"
                                    onClick={() =>
                                      setPuzzleOpenSections((prev) => ({ ...prev, [section.key]: !isOpen }))
                                    }
                                  >
                                    <span>
                                      <strong>{section.title}</strong>
                                      <small>{section.range}</small>
                                    </span>
                                    <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
                                  </button>

                                  {isOpen && (
                                    <div className="academy-v2-section-lessons">
                                      {pageItems.length === 0 && (
                                        <div className="academy-v2-empty">
                                          {language === 'ro' ? 'Nu există grupe pentru acest filtru.' : 'No groups for this filter.'}
                                        </div>
                                      )}
                                      {pageItems.map((group) => {
                                        const status = getPuzzleGroupStatus(selectedPuzzleCategory?.id, group);
                                        const statusLabel =
                                          status === 'completed'
                                            ? 'Done'
                                            : status === 'in_progress'
                                              ? (language === 'ro' ? 'În curs' : 'In progress')
                                              : status === 'locked'
                                                ? 'Locked'
                                                : (language === 'ro' ? 'Neînceput' : 'Not started');
                                        return (
                                          <button
                                            key={`puzzle-v2-group-${group.id}`}
                                            type="button"
                                            className={`academy-v2-lesson-row puzzle-v2-group-row status-${status} puzzle-group-${group.id} ${selectedPuzzleDifficultyId === group.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedPuzzleDifficultyId(group.id)}
                                            disabled={status === 'locked'}
                                          >
                                            <span className="academy-v2-lesson-main">
                                              <strong>
                                                {language === 'ro'
                                                  ? `${group.title} · ${group.count} puzzle-uri`
                                                  : `${group.title} · ${group.count} puzzles`}
                                              </strong>
                                              <small>
                                                {language === 'ro' ? 'Dificultate:' : 'Difficulty:'} {group.title}
                                              </small>
                                            </span>
                                            <span className={`academy-v2-lesson-status status-${status}`}>
                                              {statusLabel}
                                            </span>
                                          </button>
                                        );
                                      })}

                                      {filteredItems.length > pageSize && (
                                        <div className="academy-v2-pagination">
                                          <button
                                            type="button"
                                            disabled={currentPage <= 1}
                                            onClick={() =>
                                              setPuzzleSectionPage((prev) => ({
                                                ...prev,
                                                [section.key]: Math.max(1, currentPage - 1)
                                              }))
                                            }
                                          >
                                            {language === 'ro' ? 'Anterior' : 'Prev'}
                                          </button>
                                          <span>
                                            {language === 'ro' ? 'Pagina' : 'Page'} {currentPage}/{totalPages}
                                          </span>
                                          <button
                                            type="button"
                                            disabled={currentPage >= totalPages}
                                            onClick={() =>
                                              setPuzzleSectionPage((prev) => ({
                                                ...prev,
                                                [section.key]: Math.min(totalPages, currentPage + 1)
                                              }))
                                            }
                                          >
                                            {language === 'ro' ? 'Următor' : 'Next'}
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </section>
                              );
                            })}
                          </div>
                        </div>

                        <aside className="academy-v2-focus-column">
                          <article className="module-card">
                            {selectedPuzzleGroup ? (
                              <div className={`lesson-preview-card puzzle-level-strip ${puzzleDifficultyUi.badgeClass}`}>
                                <div className="lesson-preview-head">
                                  <span className="lesson-pill">
                                    {language === 'ro' ? 'Obiectivul zilei' : "Today's objective"}
                                  </span>
                                  <span className={`puzzle-difficulty-badge ${puzzleDifficultyUi.badgeClass}`}>
                                    <span className="puzzle-difficulty-icon" aria-hidden="true">{puzzleDifficultyUi.icon}</span>
                                    <span>{selectedPuzzleGroup.title}</span>
                                  </span>
                                </div>
                                <h4>
                                  {language === 'ro'
                                    ? `🎯 Provocarea: ${selectedPuzzleCategory?.title || 'Puzzle tactic'}`
                                    : `🎯 Challenge: ${selectedPuzzleCategory?.title || 'Tactical puzzle'}`}
                                </h4>
                                <p>
                                  {language === 'ro'
                                    ? 'Stăpânește arta finalizării rapide și găsește mutarea decisivă.'
                                    : 'Master fast finishing and find the decisive move.'}
                                </p>
                                <div className="lesson-preview-actions puzzle-hero-actions">
                                  <button
                                    className="btn-start-lesson mini"
                                    onClick={() => {
                                      if (!selectedPuzzleFromGroup) return;
                                      const categoryId = String(selectedPuzzleCategory?.id || '').trim();
                                      const groupId = String(selectedPuzzleGroup?.id || '').trim();
                                      const currentCount = Math.max(0, Number(guestPuzzleStartsByCategory?.[categoryId] || 0));
                                      const isExplorerPlan = String(userPlan || 'explorer') === 'explorer';
                                      const needsLimit = !user?.id || isExplorerPlan;
                                      if (needsLimit && currentCount >= guestPuzzleFreeLimitPerCategory) {
                                        setShowGuestPuzzleLockModal(true);
                                        return;
                                      }
                                      if (needsLimit) {
                                        setGuestPuzzleStartsByCategory((prev) => ({
                                          ...(prev || {}),
                                          [categoryId]: currentCount + 1
                                        }));
                                      }
                                      if (categoryId && groupId) {
                                        const k = `${categoryId}:${groupId}`;
                                        setPuzzleStartsByGroup((prev) => ({
                                          ...(prev || {}),
                                          [k]: Math.max(0, Number(prev?.[k] || 0)) + 1
                                        }));
                                      }
                                      const nextDiagramNo = puzzleSessionDiagramNo;
                                      onStartLesson(selectedPuzzleFromGroup.id, {
                                        sourceArea: 'puzzles',
                                        puzzleDiagramNo: nextDiagramNo,
                                        puzzleTheme: selectedPuzzleCategory?.id || '',
                                        puzzleDifficultyId: selectedPuzzleGroup?.id || '',
                                        minRating: selectedPuzzleGroup?.minRating ?? null,
                                        maxRating: selectedPuzzleGroup?.maxRating ?? null,
                                        puzzleHeaderStats: {
                                          puzzleRating: Math.round(Number(puzzleSkillProfile?.puzzleRating || 500)),
                                          puzzlePoints: Number(puzzleUserStats?.puzzlePoints || 0),
                                          iqPoints: Number(profile?.eloPoints || 0),
                                          profileLabel: String(puzzleProfileUi?.label || ''),
                                          globalRank: Number.isFinite(Number(puzzleUserStats?.rank))
                                            ? Number(puzzleUserStats.rank)
                                            : null
                                        }
                                      });
                                      setPuzzleSessionDiagramNo((n) => n + 1);
                                    }}
                                    disabled={!selectedPuzzleFromGroup}
                                  >
                                    {language === 'ro' ? 'Începe antrenamentul' : 'Start training'}
                                  </button>
                                </div>
                                <p className="puzzle-social-proof" style={{ marginTop: 0 }}>
                                  {language === 'ro'
                                    ? `Diagrama #${puzzleSessionDiagramNo} în sesiunea curentă.`
                                    : `Diagram #${puzzleSessionDiagramNo} in the current session.`}
                                </p>
                                {(!user?.id || String(userPlan || 'explorer') === 'explorer') && (
                                  <p className="puzzle-social-proof" style={{ marginTop: 0 }}>
                                    {!user?.id
                                      ? (language === 'ro'
                                          ? `Acces fără cont: ${selectedPuzzleCategoryGuestRemaining}/${guestPuzzleFreeLimitPerCategory} puzzle-uri rămase în această categorie.`
                                          : `Guest access: ${selectedPuzzleCategoryGuestRemaining}/${guestPuzzleFreeLimitPerCategory} puzzles left in this category.`)
                                      : (language === 'ro'
                                          ? `Plan Explorer: ${selectedPuzzleCategoryGuestRemaining}/${guestPuzzleFreeLimitPerCategory} puzzle-uri rămase în această categorie.`
                                          : `Explorer plan: ${selectedPuzzleCategoryGuestRemaining}/${guestPuzzleFreeLimitPerCategory} puzzles left in this category.`)}
                                  </p>
                                )}
                                <div className="lesson-preview-meta puzzle-meta-grid">
                                  <span className="puzzle-meta-chip">
                                    <span className="puzzle-meta-icon" aria-hidden="true">⏱</span>
                                    <span>{selectedPuzzleFromGroup?.duration || '~2 min'}</span>
                                  </span>
                                  <span className="puzzle-meta-chip">
                                    <span className="puzzle-meta-icon" aria-hidden="true">{puzzleDifficultyUi.icon}</span>
                                    <span>{language === 'ro' ? 'Dificultate:' : 'Difficulty:'} {puzzleDifficultyUi.label}</span>
                                  </span>
                                  <span className="puzzle-meta-chip">
                                    <span className="puzzle-meta-icon" aria-hidden="true">🎯</span>
                                    <span>{language === 'ro' ? 'Rating:' : 'Rating:'} {Number(selectedPuzzleFromGroup?.rating || 0)}</span>
                                  </span>
                                  {selectedPuzzleFromGroup?.nbPlays != null ? (
                                    <span className="puzzle-meta-chip">
                                      <span className="puzzle-meta-icon" aria-hidden="true">👥</span>
                                      <span>{language === 'ro' ? 'Jucat de:' : 'Played by:'} {selectedPuzzleFromGroup.nbPlays}</span>
                                    </span>
                                  ) : null}
                                  {selectedPuzzleFromGroup?.popularity != null ? (
                                    <span className="puzzle-meta-chip">
                                      <span className="puzzle-meta-icon" aria-hidden="true">🔥</span>
                                      <span>{puzzlePopularityText}</span>
                                    </span>
                                  ) : null}
                                </div>
                                {selectedPuzzleFromGroup?.nbPlays != null && (
                                  <p className="puzzle-social-proof">
                                    {language === 'ro'
                                      ? `Peste ${Number(selectedPuzzleFromGroup.nbPlays).toLocaleString('ro-RO')} elevi au exersat această poziție.`
                                      : `Over ${Number(selectedPuzzleFromGroup.nbPlays).toLocaleString('en-US')} learners trained on this position.`}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="module-footer-note">
                                {language === 'ro'
                                  ? 'Selectează o grupă de dificultate din categoria activă.'
                                  : 'Select a difficulty group from the active category.'}
                              </div>
                            )}
                            {isLoadingPuzzles && (
                              <div className="module-footer-note">
                                {language === 'ro' ? 'Se încarcă puzzle-urile...' : 'Loading puzzles...'}
                              </div>
                            )}
                            {puzzleError && (
                              <div className="module-footer-note">
                                {puzzleError}
                              </div>
                            )}
                          </article>
                        </aside>
                      </div>
                    </section>
                  )}
                </>
              ) : activeArea === 'quizzes' ? (
                <>
                  <article className="module-card quiz-module-card">
                    <div className="quiz-session-settings">
                      <div className="quiz-settings-group">
                        <p className="quiz-settings-label">{language === 'ro' ? 'Dificultate' : 'Difficulty'}</p>
                        <div className="quiz-settings-options">
                          {quizDifficultyOptions.map((lvl) => (
                            <button
                              key={`quiz-settings-lvl-${lvl.id}`}
                              type="button"
                              className={`quiz-settings-chip ${selectedQuizDifficulty === lvl.id ? 'active' : ''}`}
                              onClick={() => setSelectedQuizDifficulty(lvl.id)}
                              disabled={quizSessionStarted && !quizSessionCompleted}
                            >
                              {lvl.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="quiz-settings-group">
                        <p className="quiz-settings-label">{language === 'ro' ? 'Mod sesiune' : 'Session mode'}</p>
                        <div className="quiz-settings-options">
                          {quizModeOptions.map((mode) => (
                            <button
                              key={`quiz-settings-mode-${mode.id}`}
                              type="button"
                              className={`quiz-settings-chip ${quizSessionMode === mode.id ? 'active' : ''}`}
                              onClick={() => setQuizSessionMode(mode.id)}
                              disabled={quizSessionStarted && !quizSessionCompleted}
                            >
                              {mode.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="quiz-settings-group">
                        <p className="quiz-settings-label">{language === 'ro' ? 'Număr întrebări' : 'Questions count'}</p>
                        <div className="quiz-settings-options">
                          {quizQuestionCountOptions.map((count) => (
                            <button
                              key={`quiz-settings-count-${count}`}
                              type="button"
                              className={`quiz-settings-chip ${quizSessionSize === count ? 'active' : ''}`}
                              onClick={() => setQuizSessionSize(count)}
                              disabled={quizSessionStarted && !quizSessionCompleted}
                            >
                              {count}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    {quizBankQuestions.length > 0 ? (
                      <div className="lesson-preview-card">
                        <div className="lesson-preview-head">
                          <span className="lesson-pill">
                            {language === 'ro' ? 'Sesiune Quiz' : 'Quiz Session'}
                          </span>
                          <span className="puzzle-difficulty-badge">
                            <span className="puzzle-difficulty-icon" aria-hidden="true">📝</span>
                            <span>{selectedQuizDifficultyLabel}</span>
                          </span>
                          {quizSessionStarted && !quizSessionCompleted && (
                            <span className={`quiz-timer-box ${quizTimerToneClass}`}>
                              <span className="quiz-timer-label">{language === 'ro' ? 'Timp rămas' : 'Time left'}</span>
                              <strong>{quizTimerLabel}</strong>
                            </span>
                          )}
                        </div>
                        <p className="lesson-card-desc">
                          {language === 'ro' ? 'Subiect:' : 'Topic:'} {selectedQuizTopicLabel}
                        </p>

                        {!quizSessionStarted && (
                          <>
                            <h4>{language === 'ro' ? 'Pregătit de antrenament?' : 'Ready to train?'}</h4>
                            <p>
                              {language === 'ro'
                                ? `Vei primi ${plannedQuizQuestionCount} întrebări random. Modul selectat este ${quizSessionMode === 'challenge' ? 'Provocare' : 'Antrenament'}.`
                                : `You will get ${plannedQuizQuestionCount} random questions. Current mode is ${quizSessionMode === 'challenge' ? 'Challenge' : 'Practice'}.`}
                            </p>
                            <div className="lesson-preview-meta puzzle-meta-grid">
                              <span className="puzzle-meta-chip">
                                <span className="puzzle-meta-icon" aria-hidden="true">🎯</span>
                                <span>
                                  {language === 'ro' ? 'Întrebări disponibile:' : 'Available questions:'}{' '}
                                  {selectedQuizAvailableCount}
                                </span>
                              </span>
                              <span className="puzzle-meta-chip">
                                <span className="puzzle-meta-icon" aria-hidden="true">❤️</span>
                                <span>{language === 'ro' ? 'Vieți:' : 'Lives:'} {quizSessionLivesPreset}</span>
                              </span>
                              <span className="puzzle-meta-chip">
                                <span className="puzzle-meta-icon" aria-hidden="true">🏆</span>
                                <span>{language === 'ro' ? 'Punctaj max sesiune:' : 'Max session score:'} {quizSessionMaxScore}</span>
                              </span>
                              <span className="puzzle-meta-chip">
                                <span className="puzzle-meta-icon" aria-hidden="true">⏱️</span>
                                <span>{language === 'ro' ? 'Timer sesiune:' : 'Session timer:'} {Math.floor(quizSessionTotalTimeSec / 60)} {language === 'ro' ? 'min' : 'min'}</span>
                              </span>
                            </div>
                            <div className="lesson-preview-actions">
                              <button className="btn-start-lesson mini" onClick={startQuizSession} disabled={plannedQuizQuestionCount < 1}>
                                {language === 'ro' ? 'Începe quiz-ul' : 'Start quiz'}
                              </button>
                            </div>
                          </>
                        )}

                        {quizSessionStarted && currentQuizQuestion && (
                          <>
                            <h4>
                              {language === 'ro'
                                ? `Întrebarea ${quizSessionIndex + 1}/${quizSessionQuestions.length}`
                                : `Question ${quizSessionIndex + 1}/${quizSessionQuestions.length}`}
                            </h4>
                            <p>{currentQuizQuestion.question}</p>

                            <div className="quiz-options-grid">
                              {(currentQuizQuestion.options || []).map((opt) => {
                                const oid = String(opt.id || '');
                                const selected = quizSelectedOptionId === oid;
                                const isCorrect = oid === String(currentQuizQuestion.correctOptionId || '');
                                const wrongAnswerChecked = quizChecked && quizSelectedOptionId !== String(currentQuizQuestion.correctOptionId || '');
                                const wrongSelected = quizChecked && selected && !isCorrect;
                                const correctSelected = quizChecked && isCorrect;
                                const revealCorrect = wrongAnswerChecked && isCorrect;
                                return (
                                  <button
                                    key={`quiz-opt-${currentQuizQuestion.id}-${oid}`}
                                    className={`quiz-option-btn ${wrongSelected ? 'wrong' : ''} ${correctSelected ? 'correct' : ''} ${revealCorrect ? 'reveal-correct' : ''} ${selected ? 'selected' : ''}`}
                                    onClick={() => handleQuizPick(oid)}
                                    disabled={quizChecked}
                                  >
                                    <span className="quiz-option-key">{oid.toUpperCase()}</span>
                                    <span>{opt.text}</span>
                                  </button>
                                );
                              })}
                            </div>

                            {quizChecked && (
                              <>
                                <div
                                  className="lesson-mission"
                                  style={{
                                    marginTop: 12,
                                    borderColor: quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '') ? '#16a34a' : '#dc2626',
                                    background: quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '') ? '#ecfdf5' : '#fef2f2'
                                  }}
                                >
                                  <span className="lesson-mission-label">
                                    {quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '')
                                      ? (language === 'ro' ? 'Corect' : 'Correct')
                                      : (language === 'ro' ? 'Răspuns greșit' : 'Wrong answer')}
                                  </span>
                                  {quizSelectedOptionId === String(currentQuizQuestion.correctOptionId || '') && (
                                    <strong>{language === 'ro' ? 'Răspuns corect!' : 'Correct answer!'}</strong>
                                  )}
                                  {quizSelectedOptionId !== String(currentQuizQuestion.correctOptionId || '') && currentQuizCorrectOption && (
                                    <strong>
                                      {language === 'ro' ? 'Răspuns corect:' : 'Correct answer:'}{' '}
                                      {String(currentQuizCorrectOption.id || '').toUpperCase()} — {currentQuizCorrectOption.text}
                                    </strong>
                                  )}
                                  {currentQuizQuestion.explanation && (
                                    <strong>{currentQuizQuestion.explanation}</strong>
                                  )}
                                </div>
                                <div className="lesson-preview-actions">
                                  <button className="btn-start-lesson mini" onClick={goNextQuizQuestion}>
                                    {(quizSessionIndex >= quizSessionQuestions.length - 1 || quizSessionLives <= 0)
                                      ? (language === 'ro' ? 'Finalizează sesiunea' : 'Finish session')
                                      : (language === 'ro' ? 'Întrebarea următoare' : 'Next question')}
                                  </button>
                                </div>
                              </>
                            )}
                          </>
                        )}

                        {(quizSessionStarted && quizSessionCompleted) && (
                          <div className="lesson-mission" style={{ marginTop: 14, borderColor: '#1e40af', background: '#eef2ff' }}>
                            <span className="lesson-mission-label">{language === 'ro' ? 'Sesiune finalizată' : 'Session completed'}</span>
                            <strong>
                              {language === 'ro'
                                ? `Scor ${quizSessionScore} · Acuratețe ${quizAccuracyPct}% · Corecte ${quizSessionCorrectCount}/${quizSessionAnsweredCount} · Timp ${quizTimerLabel}`
                                : `Score ${quizSessionScore} · Accuracy ${quizAccuracyPct}% · Correct ${quizSessionCorrectCount}/${quizSessionAnsweredCount} · Time ${quizTimerLabel}`}
                            </strong>
                            <div className="lesson-preview-actions" style={{ marginTop: 10 }}>
                              <button className="btn-start-lesson mini" onClick={startQuizSession}>
                                {language === 'ro' ? 'Reia sesiunea' : 'Restart session'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="module-footer-note">
                        {language === 'ro'
                          ? 'Nu există încă întrebări active pentru quiz.'
                          : 'No active quiz questions are available yet.'}
                      </div>
                    )}
                  </article>

                  <div className="teaser-modules-grid">
                    <article className="teaser-module-card">
                      <p className="module-index">{language === 'ro' ? 'Quiz Stats' : 'Quiz Stats'}</p>
                      <h4>{language === 'ro' ? 'Scor sesiune' : 'Session score'}</h4>
                      <p>{quizSessionScore}</p>
                    </article>
                    <article className="teaser-module-card">
                      <p className="module-index">{language === 'ro' ? 'Quiz Stats' : 'Quiz Stats'}</p>
                      <h4>{language === 'ro' ? 'Vieți' : 'Lives'}</h4>
                      <p>{'❤️'.repeat(Math.max(0, quizSessionLives)) || '—'}</p>
                    </article>
                    <article className="teaser-module-card">
                      <p className="module-index">{language === 'ro' ? 'Quiz Stats' : 'Quiz Stats'}</p>
                      <h4>{language === 'ro' ? 'Progres' : 'Progress'}</h4>
                      <p>{quizSessionAnsweredCount}/{Math.max(quizSessionQuestions.length, plannedQuizQuestionCount || 1)} ({quizProgressPct}%)</p>
                    </article>
                    <article className="teaser-module-card">
                      <p className="module-index">{language === 'ro' ? 'Quiz Stats' : 'Quiz Stats'}</p>
                      <h4>{language === 'ro' ? 'Rank general' : 'Global rank'}</h4>
                      <p>{quizRankLabel}</p>
                    </article>
                  </div>
                </>
              ) : activeArea === 'gamelab' ? (
                <>
                  {!selectedGamelabArticle ? (
                    <article className="mindhub-article-card mindhub-preview-card">
                      <div className="mindhub-preview-empty">
                        <span aria-hidden="true">🎬</span>
                        <h3>
                          {language === 'ro'
                            ? 'Alege o analiză din stânga ca să vezi pozițiile-cheie.'
                            : 'Pick an analysis from the left to see key positions.'}
                        </h3>
                      </div>
                    </article>
                  ) : (
                    <article className="module-card gamelab-article-card">
                      <div className="gamelab-head">
                        <span className="module-index">{language === 'ro' ? 'GameLab · Analiză' : 'GameLab · Analysis'}</span>
                        <h3>{selectedGamelabArticle.title}</h3>
                        <p>{selectedGamelabArticle.subtitle}</p>
                      </div>

                      <div className="gamelab-meta-row">
                        <span className="lesson-meta-chip">
                          🧩 {language === 'ro' ? 'Poziții' : 'Positions'}: {gamelabArticleProgress.visited}/{gamelabArticleProgress.totalPositions}
                        </span>
                        <span className="lesson-meta-chip">
                          🧠 {language === 'ro' ? 'Quiz final' : 'Final quiz'}: {gamelabArticleProgress.answered}/{gamelabArticleProgress.totalQuiz}
                        </span>
                        <span className="lesson-meta-chip">
                          📈 {language === 'ro' ? 'Progres articol' : 'Article progress'}: {gamelabArticleProgress.progressPct}%
                        </span>
                      </div>
                      <div className="mindhub-reading-progress micro" aria-hidden="true">
                        <div className="mindhub-reading-track">
                          <div className="mindhub-reading-fill" style={{ width: `${gamelabArticleProgress.progressPct}%` }} />
                        </div>
                      </div>
                      {selectedGamelabArticle.sourceMeta && (
                        <p className="gamelab-source-note">{selectedGamelabArticle.sourceMeta}</p>
                      )}

                      <div className="gamelab-hero">
                        <img
                          src={selectedGamelabArticle.heroImage}
                          alt={selectedGamelabArticle.title}
                          loading="lazy"
                        />
                      </div>

                      <div className="gamelab-position-nav">
                        <button
                          type="button"
                          className="btn-start-lesson mini"
                          disabled={gamelabPositionIndex <= 0}
                          onClick={() => setGamelabPositionIndex((prev) => Math.max(0, prev - 1))}
                        >
                          {language === 'ro' ? '← Poziția anterioară' : '← Previous position'}
                        </button>
                        <span>
                          {language === 'ro' ? 'Poziția' : 'Position'} {gamelabPositionIndex + 1}/
                          {selectedGamelabArticle.positions.length}
                        </span>
                        <button
                          type="button"
                          className="btn-start-lesson mini continue"
                          disabled={gamelabPositionIndex >= selectedGamelabArticle.positions.length - 1}
                          onClick={() => setGamelabPositionIndex((prev) => Math.min(selectedGamelabArticle.positions.length - 1, prev + 1))}
                        >
                          {language === 'ro' ? 'Poziția următoare →' : 'Next position →'}
                        </button>
                      </div>

                      {selectedGamelabPosition && (
                        <div className="gamelab-position-grid">
                          <div className="gamelab-board-wrap">
                            <Chessboard
                              id={`gamelab-board-${selectedGamelabArticle.id}`}
                              position={selectedGamelabPosition.fen}
                              boardWidth={340}
                              arePiecesDraggable={false}
                              areArrowsAllowed={false}
                              customBoardStyle={{
                                borderRadius: 12,
                                boxShadow: '0 10px 26px rgba(15, 23, 42, 0.18)'
                              }}
                            />
                          </div>
                          <div className="gamelab-insight-card">
                            <span className="module-index">{selectedGamelabPosition.label}</span>
                            <p>{selectedGamelabPosition.insight}</p>
                          </div>
                        </div>
                      )}

                      <div className="gamelab-summary-card">
                        <h4>{language === 'ro' ? 'Ce reții din analiză' : 'What to retain from this analysis'}</h4>
                        <ul>
                          {(selectedGamelabArticle.summaryBullets || []).map((item, idx) => (
                            <li key={`gamelab-summary-${idx}`}>{item}</li>
                          ))}
                        </ul>
                      </div>

                      <section className="mindhub-article-section mindhub-quiz-section gamelab-quiz-section">
                        <h3>{language === 'ro' ? 'Quiz final · verifică înțelegerea' : 'Final quiz · validate your understanding'}</h3>
                        <div className="mindhub-quiz-summary">
                          <div className="mindhub-quiz-stat">
                            <span>{language === 'ro' ? 'Răspunse' : 'Answered'}</span>
                            <strong>{gamelabQuizSummary.answered}/{gamelabQuizSummary.total}</strong>
                          </div>
                          <div className="mindhub-quiz-stat">
                            <span>{language === 'ro' ? 'Corecte' : 'Correct'}</span>
                            <strong>{gamelabQuizSummary.correct}</strong>
                          </div>
                          <div className="mindhub-quiz-stat">
                            <span>{language === 'ro' ? 'Acuratețe' : 'Accuracy'}</span>
                            <strong>{gamelabQuizSummary.pct}%</strong>
                          </div>
                        </div>
                        <div className="mindhub-quiz-grid">
                          {(selectedGamelabArticle.quizQuestions || []).map((question, qIdx) => {
                            const picked = selectedGamelabQuizAnswers[question.id];
                            const isAnswered = typeof picked === 'number';
                            return (
                              <article key={question.id} className="mindhub-quiz-card">
                                <div className="mindhub-quiz-headline">
                                  <span className="module-index">
                                    {language === 'ro' ? `Întrebarea ${qIdx + 1}` : `Question ${qIdx + 1}`}
                                  </span>
                                  <span className="mindhub-quiz-icon" aria-hidden="true">🎬</span>
                                </div>
                                <h4>{question.question}</h4>
                                <div className="mindhub-quiz-options">
                                  {question.options.map((option, optIdx) => {
                                    const isPicked = picked === optIdx;
                                    const isCorrect = question.correctIndex === optIdx;
                                    const isWrongPicked = isAnswered && isPicked && !isCorrect;
                                    return (
                                      <button
                                        key={`${question.id}-${optIdx}`}
                                        type="button"
                                        className={`mindhub-quiz-option ${isPicked ? 'picked' : ''} ${isAnswered && isCorrect ? 'correct' : ''} ${isWrongPicked ? 'wrong' : ''}`}
                                        onClick={() => handleGamelabPickAnswer(question.id, optIdx)}
                                      >
                                        <span className="mindhub-option-key">{String.fromCharCode(65 + optIdx)}</span>
                                        <span className="mindhub-option-text">{option}</span>
                                        {isAnswered && isCorrect ? <span className="mindhub-option-state">✓</span> : null}
                                      </button>
                                    );
                                  })}
                                </div>
                                {isAnswered ? (
                                  <p className={`mindhub-quiz-feedback ${picked === question.correctIndex ? 'correct' : 'wrong'}`}>
                                    {question.explanation}
                                  </p>
                                ) : null}
                              </article>
                            );
                          })}
                        </div>
                      </section>
                    </article>
                  )}
                </>
              ) : activeArea === 'mindhub' ? (
                <>
                  {!selectedMindhubArticle ? (
                    <article className="mindhub-article-card mindhub-preview-card">
                      <div className="mindhub-preview-empty">
                        <span aria-hidden="true">🧠</span>
                        <h3>
                          {language === 'ro'
                            ? 'Alege un articol din stânga ca să vezi detaliile.'
                            : 'Pick an article from the left to view details.'}
                        </h3>
                      </div>
                    </article>
                  ) : (
                    <>
                      {!isMindhubReadingMode && (
                        <article className="mindhub-article-card">
                          <div
                            className="mindhub-card-hero"
                            style={{ backgroundImage: `url(${selectedMindhubArticle.heroImage})` }}
                            role="img"
                            aria-label={selectedMindhubArticle.heroImageAlt}
                          />
                          <div className="mindhub-card-body">
                            <p className="module-index">{language === 'ro' ? 'Articol recomandat' : 'Featured article'}</p>
                            <h3>{selectedMindhubArticle.title}</h3>
                            <p>{selectedMindhubArticle.subtitle}</p>
                            <div className="mindhub-card-meta">
                              <span>{language === 'ro' ? `~${selectedMindhubArticle.readMinutes} min citire` : `~${selectedMindhubArticle.readMinutes} min read`}</span>
                            </div>
                            <button
                              className="btn-start-lesson continue"
                              onClick={() => handleOpenMindhubArticle(selectedMindhubArticle.id)}
                            >
                              {language === 'ro' ? 'Citește articolul' : 'Read article'}
                            </button>
                          </div>
                        </article>
                      )}

                      {isMindhubReadingMode && (
                        <article ref={mindhubArticleViewRef} className="mindhub-article-view immersive">
                      <div
                        className="mindhub-reading-progress micro"
                        role="status"
                        aria-live="polite"
                        aria-label={language === 'ro' ? `Progres citire ${mindhubReadingProgress}%` : `Reading progress ${mindhubReadingProgress}%`}
                      >
                        <div className="mindhub-reading-track">
                          <div className="mindhub-reading-fill" style={{ width: `${mindhubReadingProgress}%` }} />
                        </div>
                        <span className="mindhub-reading-value">{mindhubReadingProgress}%</span>
                      </div>

                      <div className="mindhub-reading-controls">
                        <button className="btn-back-home mindhub-back-btn" onClick={handleBackToMindhubList}>
                          ← {language === 'ro' ? 'Înapoi la articole' : 'Back to articles'}
                        </button>
                      </div>

                      <header className="mindhub-article-header compact-text">
                        <h2 className="mindhub-article-title">{selectedMindhubArticle.title}</h2>
                        <div className="mindhub-article-summary-row">
                          <p className="mindhub-article-subtitle">{selectedMindhubArticle.subtitle}</p>
                          <span className="mindhub-article-meta">
                            {language === 'ro'
                              ? `~${selectedMindhubArticle.readMinutes} min citire`
                              : `~${selectedMindhubArticle.readMinutes} min read`}
                          </span>
                        </div>
                      </header>

                      <section className="mindhub-spotify-card">
                        <div className="mindhub-spotify-head">
                          <div>
                            <h3>{language === 'ro' ? 'Ascultă varianta audio' : 'Listen to the audio version'}</h3>
                            <p>
                              {language === 'ro'
                                ? 'Preferi audio? Poți asculta episodul complet direct aici sau în aplicația Spotify.'
                                : 'Prefer audio? Listen to the full episode here or open it in Spotify.'}
                            </p>
                          </div>
                          <span className="mindhub-spotify-duration">{selectedMindhubArticle.spotifyDurationLabel}</span>
                        </div>
                        <iframe
                          className="mindhub-spotify-embed"
                          src={selectedMindhubArticle.spotifyEmbedUrl}
                          title="Spotify podcast episode"
                          loading="lazy"
                          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                        />
                        <div className="mindhub-spotify-footer">
                          <button className="mindhub-spotify-open" onClick={handleOpenMindhubSpotify}>
                            {language === 'ro' ? 'Open in Spotify' : 'Open in Spotify'}
                          </button>
                          {!!selectedMindhubArticle.podcastMoments?.length && (
                            <button
                              type="button"
                              className="mindhub-spotify-moments-chip"
                              onClick={() => setMindhubMomentsOpen((prev) => !prev)}
                              aria-expanded={mindhubMomentsOpen}
                              aria-controls="mindhub-spotify-moments-panel"
                            >
                              {language === 'ro' ? 'Momente recomandate' : 'Recommended moments'}
                            </button>
                          )}
                        </div>
                        {!!selectedMindhubArticle.podcastMoments?.length && mindhubMomentsOpen && (
                          <div
                            id="mindhub-spotify-moments-panel"
                            className="mindhub-spotify-moments"
                            role="note"
                            aria-label={language === 'ro' ? 'Momente recomandate' : 'Recommended moments'}
                          >
                            <p className="mindhub-spotify-moments-title">
                              {language === 'ro' ? 'Momente recomandate:' : 'Recommended moments:'}
                            </p>
                            <ul>
                              {selectedMindhubArticle.podcastMoments.map((moment) => (
                                <li key={moment.time}>
                                  <span>{moment.time}</span>
                                  <span>{moment.label}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </section>

                      {selectedMindhubArticle.sections.map((section, idx) => (
                        <section className="mindhub-article-section" key={`${section.title}-${idx}`}>
                          <h3>{section.title}</h3>
                          {section.visual && (
                            <div
                              className="mindhub-section-visual"
                              style={{ backgroundImage: `url(${section.visual})` }}
                              role="img"
                              aria-label={section.visualAlt || section.title}
                            />
                          )}
                          {section.paragraphs.map((paragraph, pIdx) => (
                            <p key={`${section.title}-${pIdx}`}>{paragraph}</p>
                          ))}
                        </section>
                      ))}

                      <section className="mindhub-article-section mindhub-quiz-section">
                        <h3>{selectedMindhubArticle.quizTitle}</h3>
                        <div className="mindhub-quiz-summary">
                          <div className="mindhub-quiz-stat">
                            <span>{language === 'ro' ? 'Răspunse' : 'Answered'}</span>
                            <strong>{mindhubQuizSummary.answered}/{mindhubQuizSummary.total}</strong>
                          </div>
                          <div className="mindhub-quiz-stat">
                            <span>{language === 'ro' ? 'Corecte' : 'Correct'}</span>
                            <strong>{mindhubQuizSummary.correct}</strong>
                          </div>
                          <div className="mindhub-quiz-stat">
                            <span>{language === 'ro' ? 'Acuratețe' : 'Accuracy'}</span>
                            <strong>{mindhubQuizSummary.pct}%</strong>
                          </div>
                        </div>
                        <div className="mindhub-quiz-grid">
                          {selectedMindhubArticle.quizQuestions.map((question, qIdx) => {
                            const picked = mindhubQuizAnswers[question.id];
                            const isAnswered = typeof picked === 'number';
                            return (
                              <article key={question.id} className="mindhub-quiz-card">
                                <div className="mindhub-quiz-headline">
                                  <p className="module-index">
                                    {language === 'ro' ? `Întrebarea ${qIdx + 1}/4` : `Question ${qIdx + 1}/4`}
                                  </p>
                                  <span className="mindhub-quiz-icon" aria-hidden="true">
                                    {qIdx % 2 === 0 ? '♟' : '🧠'}
                                  </span>
                                </div>
                                <h4>{question.question}</h4>
                                <div className="mindhub-quiz-options">
                                  {question.options.map((opt, optIdx) => {
                                    const isCorrect = optIdx === question.correctIndex;
                                    const isPicked = picked === optIdx;
                                    const isWrongPicked = isAnswered && isPicked && !isCorrect;
                                    return (
                                      <button
                                        key={`${question.id}-opt-${optIdx}`}
                                        className={`mindhub-quiz-option ${isPicked ? 'picked' : ''} ${isAnswered && isCorrect ? 'correct' : ''} ${isWrongPicked ? 'wrong' : ''}`}
                                        onClick={() => handleMindhubPickAnswer(question.id, optIdx)}
                                      >
                                        <span className="mindhub-option-key">{String.fromCharCode(65 + optIdx)}</span>
                                        <span className="mindhub-option-text">{opt}</span>
                                        {isAnswered && isCorrect && <span className="mindhub-option-state">✓</span>}
                                        {isWrongPicked && <span className="mindhub-option-state wrong">✕</span>}
                                      </button>
                                    );
                                  })}
                                </div>
                                {isAnswered && (
                                  <p className={`mindhub-quiz-feedback ${picked === question.correctIndex ? 'correct' : 'wrong'}`}>
                                    {question.explanation}
                                  </p>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      </section>

                      <section className="mindhub-share-row">
                        <div className="mindhub-share-actions">
                          <button className="native" aria-label={language === 'ro' ? 'Partajează' : 'Share'} title={language === 'ro' ? 'Partajează' : 'Share'} data-tooltip={language === 'ro' ? 'Partajează' : 'Share'} onClick={() => handleMindhubShare('native')}>
                            {renderMindhubShareIcon('native')}
                          </button>
                          <button className="facebook" aria-label="Facebook" title="Facebook" data-tooltip="Facebook" onClick={() => handleMindhubShare('facebook')}>
                            {renderMindhubShareIcon('facebook')}
                          </button>
                          <button className="linkedin" aria-label="LinkedIn" title="LinkedIn" data-tooltip="LinkedIn" onClick={() => handleMindhubShare('linkedin')}>
                            {renderMindhubShareIcon('linkedin')}
                          </button>
                          <button className="threads" aria-label="Threads" title="Threads" data-tooltip="Threads" onClick={() => handleMindhubShare('threads')}>
                            {renderMindhubShareIcon('threads')}
                          </button>
                          <button className="instagram" aria-label="Instagram" title="Instagram" data-tooltip="Instagram" onClick={() => handleMindhubShare('instagram')}>
                            {renderMindhubShareIcon('instagram')}
                          </button>
                          <button className="tiktok" aria-label="TikTok" title="TikTok" data-tooltip="TikTok" onClick={() => handleMindhubShare('tiktok')}>
                            {renderMindhubShareIcon('tiktok')}
                          </button>
                          <button className="copy" aria-label={language === 'ro' ? 'Copiază link' : 'Copy link'} title={language === 'ro' ? 'Copiază link' : 'Copy link'} data-tooltip={language === 'ro' ? 'Copiază link' : 'Copy link'} onClick={() => handleMindhubShare('copy')}>
                            {renderMindhubShareIcon('copy')}
                          </button>
                        </div>
                      </section>
                        </article>
                      )}
                    </>
                  )}
                </>
              ) : activeArea === 'playground' ? (
                <>
                  <ChessyPlayground language={language} />
                  <div className="teaser-modules-grid">
                    <article className="teaser-module-card">
                      <p className="module-index"><ChessyBrand /></p>
                      <h4>{language === 'ro' ? 'Flow recomandat' : 'Recommended flow'}</h4>
                      <p>{language === 'ro' ? 'Puzzle → Playground → Review' : 'Puzzle → Playground → Review'}</p>
                    </article>
                    <article className="teaser-module-card">
                      <p className="module-index"><ChessyBrand /></p>
                      <h4>{language === 'ro' ? 'Motor' : 'Engine'}</h4>
                      <p>{language === 'ro' ? 'Bot de antrenament orientat pe progres' : 'Training bot focused on progression'}</p>
                    </article>
                  </div>
                </>
              ) : !isLearningArea ? (
                <>
                  <article className="module-card coming-soon">
                    <div className="module-header">
                      <div>
                        <p className="module-index">{language === 'ro' ? 'Secțiune selectată' : 'Selected section'}</p>
                        <h3 className="module-title">
                          {activeArea === 'puzzles'
                            ? (language === 'ro' ? 'Puzzle-uri Tactice' : 'Tactical Puzzles')
                            : activeArea === 'openings'
                              ? 'Openings Lab'
                            : activeArea === 'quizzes'
                              ? (language === 'ro' ? 'Quiz-uri de Șah' : 'Chess Quizzes')
                            : activeArea === 'mindhub'
                                ? 'Mind Hub'
                              : activeArea === 'playground'
                                ? 'Chessy AI Playground'
                                : 'Community'}
                        </h3>
                        <p className="module-summary">
                          {activeArea === 'puzzles'
                            ? (language === 'ro'
                              ? 'Puzzle-uri pe categorii, cu poziții FEN și motor unic de validare a mutărilor pentru toate piesele.'
                              : 'Category-based puzzles with FEN positions and a unified move-validation engine for all pieces.')
                            : activeArea === 'openings'
                            ? (language === 'ro'
                              ? 'Primele module de deschideri vor include idei-cheie, ordine de mutări, capcane frecvente și tranziții clare spre mijlocul jocului.'
                              : 'The first opening modules will include key ideas, move orders, common traps, and clear transitions to the middlegame.')
                            : activeArea === 'quizzes'
                            ? (language === 'ro'
                              ? 'Quiz-uri pe roluri și niveluri, cu întrebări extrase aleator din banca generală de subiecte (fără alocare pe lecții).'
                              : 'Role-based and level-based quizzes, with questions randomly drawn from the global topic bank (not lesson-assigned).')
                            : activeArea === 'mindhub'
                            ? (language === 'ro'
                              ? 'Aici vom avea resursele de mindset: reziliență, psihologia jocului și focus.'
                              : 'This will host mindset resources: resilience, game psychology, and focus.')
                            : activeArea === 'playground'
                            ? (language === 'ro'
                              ? 'Aici rulează duelurile practice contra lui Chessy AI pe niveluri.'
                              : 'This hosts practical sparring games versus Chessy AI across levels.')
                            : (language === 'ro'
                              ? 'Aici vom avea interacțiunea comunității: discuții, Q&A și competiții.'
                              : 'This will host community interaction: discussions, Q&A, and competitions.')}
                        </p>
                      </div>
                    </div>
                  </article>
                  <div className="teaser-modules-grid">
                    {teaserModules.map((teaser) => (
                      <article key={teaser.id} className="teaser-module-card coming-soon">
                        <p className="module-index">{language === 'ro' ? 'Modul' : 'Module'}</p>
                        <h4>{teaser.title}</h4>
                        <p>{teaser.summary}</p>
                        <span className="lesson-pill">{language === 'ro' ? 'În curând' : 'Coming soon'}</span>
                      </article>
                    ))}
                  </div>
                  <div className="module-footer-note">
                    {activeArea === 'puzzles'
                      ? (language === 'ro'
                        ? 'Puzzle-urile sunt pregătite pe categorii. Urmează conectarea loturilor FEN.'
                        : 'Puzzle categories are ready. Next: connecting FEN batches.')
                      : activeArea === 'openings'
                      ? (language === 'ro'
                        ? 'Deschiderile sunt în pregătire. Începem cu linii clasice pentru repertoriu practic.'
                        : 'Openings are in preparation. We begin with classic lines for practical repertoire.')
                      : activeArea === 'quizzes'
                      ? (language === 'ro'
                        ? 'Banca de întrebări este pregătită pe subiecte. Urmează sesiunile random și scoring-ul de quiz.'
                        : 'Question bank is prepared by topic. Next: random sessions and quiz scoring.')
                      : activeArea === 'playground'
                      ? (language === 'ro'
                        ? 'Playground-ul Chessy AI este activ. Următorul pas: conectare rating + analytics per sesiune.'
                        : 'Chessy AI playground is live. Next step: rating sync + per-session analytics.')
                      : (language === 'ro'
                        ? 'Secțiune în pregătire. Structura UI este gata.'
                        : 'Section in preparation. UI structure is ready.')}
                  </div>
                </>
              ) : selectedModule && (
                <article className={`module-card ${selectedModule.available === false ? 'coming-soon' : ''}`} key={selectedModule.id}>
                  <div className="module-header">
                    <div>
                      <p className="module-index">
                        {language === 'ro' ? 'Modul selectat' : 'Selected module'}
                      </p>
                      <h3 className="module-title">{selectedModule.title}</h3>
                      <p className="module-summary">{selectedModule.summary}</p>
                    </div>
                  </div>

                  {selectedLesson ? (
                    <div
                      id={`lesson-${selectedLesson.id}`}
                      className={`lesson-preview-card ${selectedLesson.comingSoon ? 'coming-soon' : ''} ${flashLessonSlug === selectedLesson.id ? 'flash' : ''}`}
                    >
                      <div className="lesson-preview-head">
                        <span className="lesson-pill">
                          {selectedLesson.section}
                        </span>
                        {selectedLesson.status === 'completed' && (
                          <span className="lesson-pill done">{language === 'ro' ? 'Done' : 'Done'}</span>
                        )}
                        {selectedLesson.status === 'in_progress' && (
                          <span className="lesson-pill progress">{language === 'ro' ? 'În progres' : 'In progress'}</span>
                        )}
                      </div>

                      <h4>{selectedLesson.title}</h4>
                      <p>{selectedLesson.description || (language === 'ro' ? 'Selectează Start pentru a începe lecția.' : 'Press Start to begin the lesson.')}</p>

                      <div className="lesson-preview-meta">
                        <span>{selectedLesson.duration}</span>
                        {selectedLesson.difficulty && (
                          <span>{language === 'ro' ? 'Dificultate:' : 'Difficulty:'} {difficultyLabel(selectedLesson.difficulty)}</span>
                        )}
                      </div>

                      <div className="lesson-progress-row" aria-label="Lesson progress">
                        <div className="lesson-progress-track">
                          <div
                            className="lesson-progress-fill"
                            style={{ width: `${Math.min(100, Math.max(0, Number(selectedLesson.progressPct || 0)))}%` }}
                          />
                        </div>
                        <div className="lesson-progress-label">
                          {selectedLesson.stepsTotal > 0
                            ? language === 'ro'
                              ? `Progres: ${selectedLesson.stepsCompleted}/${selectedLesson.stepsTotal} pași (${selectedLesson.progressPct}%)`
                              : `Progress: ${selectedLesson.stepsCompleted}/${selectedLesson.stepsTotal} steps (${selectedLesson.progressPct}%)`
                            : language === 'ro'
                              ? 'Progres: —'
                              : 'Progress: —'}
                        </div>
                      </div>

                      <div className="lesson-preview-actions" id={`lesson-actions-${selectedLesson.id}`}>
                        {selectedLesson.comingSoon ? (
                          <span className="lesson-pill">
                            {language === 'ro' ? 'În curând' : 'Coming soon'}
                          </span>
                        ) : selectedLesson.planLocked ? (
                          <button
                            className="btn-start-lesson mini continue"
                            onClick={goToBilling}
                          >
                            {language === 'ro' ? 'Upgrade necesar' : 'Upgrade required'}
                          </button>
                        ) : selectedLesson.available === false ? (
                          <button
                            className="btn-start-lesson mini continue"
                            onClick={() => {
                              if (!user?.id && onRequestLogin) onRequestLogin();
                            }}
                            disabled={Boolean(user?.id)}
                          >
                            {!user?.id
                              ? (language === 'ro' ? 'Creează cont' : 'Create account')
                              : (language === 'ro' ? 'Acces restricționat' : 'Restricted access')}
                          </button>
                        ) : selectedLesson.guestLocked ? (
                          <button
                            className="btn-start-lesson mini continue"
                            onClick={() => {
                              if (!user?.id && onRequestLogin) onRequestLogin();
                            }}
                            disabled={Boolean(user?.id)}
                          >
                            {!user?.id
                              ? (language === 'ro' ? 'Creează cont' : 'Create account')
                              : (language === 'ro' ? 'Disponibil cu autentificare' : 'Available with sign in')}
                          </button>
                        ) : (
                          <button
                            className={`btn-start-lesson mini ${selectedLesson.status === 'in_progress' ? 'continue' : ''}`}
                            onClick={() => onStartLesson(selectedLesson.id, { sourceArea: activeArea })}
                          >
                            {selectedLesson.status === 'in_progress'
                              ? language === 'ro'
                                ? 'Continuă pregătirea'
                                : 'Continue training'
                            : language === 'ro'
                                ? 'Start lecție'
                                : 'Start lesson'}
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="module-footer-note">
                      {language === 'ro'
                        ? 'Selectează o lecție din arborele din stânga.'
                        : 'Select a lesson from the tree on the left.'}
                    </div>
                  )}
                </article>
              )}
              </div>
            </div>
            )}
          </div>
        </div>
      </section>
      {showGuestPuzzleLockModal && (
        <div className="cm-modal-overlay" role="dialog" aria-modal="true">
          <div className="cm-modal-card">
            <h3>{language === 'ro' ? 'Acces puzzle-uri extins' : 'Extended puzzle access'}</h3>
            <p>
              {!user?.id
                ? (language === 'ro'
                  ? 'Ai folosit cele 5 puzzle-uri gratuite din această categorie. Creează cont pentru acces complet, progres salvat, rank general, punctaj puzzle și alias în clasament.'
                  : 'You used the 5 free puzzles in this category. Create an account for full access, saved progress, global rank, and puzzle scoring.')
                : (language === 'ro'
                  ? 'Ai atins limita planului Explorer pentru această categorie. Upgrade la Starter sau Premium Mindset pentru acces extins.'
                  : 'You reached the Explorer plan limit for this category. Upgrade to Starter or Premium Mindset for extended access.')}
            </p>
            <div className="cm-modal-actions">
              <button className="btn-card" onClick={() => setShowGuestPuzzleLockModal(false)}>
                {language === 'ro' ? 'Mai târziu' : 'Later'}
              </button>
              <button
                className="btn-start-lesson mini continue"
                onClick={() => {
                  setShowGuestPuzzleLockModal(false);
                  if (!user?.id) {
                    if (onRequestLogin) onRequestLogin();
                    return;
                  }
                  goToBilling();
                }}
              >
                {!user?.id
                  ? (language === 'ro' ? 'Creează cont' : 'Create account')
                  : (language === 'ro' ? 'Vezi abonamente' : 'View plans')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AcademyHome;
